import { Trans, useTranslation } from "react-i18next";
import { translateAdminLabel } from "../../i18n/adminLabels";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiArrowDown,
  FiArrowUp,
  FiPlay,
  FiPause,
  FiPlus,
  FiMinus,
  FiMaximize2,
  FiMinimize2,
  FiEye,
  FiEyeOff,
  FiAlignCenter,
  FiAlignLeft,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import ParanoidiAdminFooter from "../components/ParanoidiAdminFooter";

import HelpCenter from "../components/HelpCenter";
import ParanoidiButton from "../components/ui/ParanoidiButton";
import SongLibraryAdmin from "../components/SongLibraryAdmin";
import GigNotesAdmin from "../components/GigNotesAdmin";
import PerformerEventsAdmin from "../components/PerformerEventsAdmin";
import "./SongRequestsPage.css";

import {
  fetchParanoidiSettings,
  getParanoidiSettings,
  saveParanoidiSettings,
  subscribeParanoidiSettings,
} from "../data/paranoidiSettings";
import { getCurrentAdminPublicPath } from "../config/currentPerformer";
import {
  bulkCreateSongs,
  deleteSongRequest,
  getSongRequests,
  getSongs,
  updateSongRequestStatus,
} from "../data/songRequestsApi";

function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatTime(value, locale = "fi-FI") {
  if (!value) {
    return "";
  }

  try {
    const normalizedValue = String(value).includes("T")
      ? String(value)
      : String(value).replace(" ", "T") + "Z";

    return new Intl.DateTimeFormat(locale, {
      timeZone: "Europe/Helsinki",
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    }).format(new Date(normalizedValue));
  } catch {
    return "";
  }
}

// Näytetään keikkasession ajat aina Suomen ajassa.
function formatGigDateTime(value, locale = "fi-FI") {
  if (!value) {
    return "";
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/Helsinki",
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function getGigDurationMinutes(startedAt, endedAt = new Date()) {
  if (!startedAt) {
    return null;
  }

  const startDate = new Date(startedAt);
  const endDate =
    endedAt instanceof Date ? endedAt : new Date(endedAt);

  if (
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime())
  ) {
    return null;
  }

  return Math.max(
    0,
    Math.round(
      (endDate.getTime() - startDate.getTime()) / 60000
    )
  );
}

function formatGigDuration(minutes) {
  if (
    minutes === null ||
    minutes === undefined ||
    !Number.isFinite(Number(minutes))
  ) {
    return "";
  }

  const totalMinutes = Math.max(0, Number(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} h ${remainingMinutes} min`;
  }

  if (hours > 0) {
    return `${hours} h`;
  }

  return `${remainingMinutes} min`;
}

function getStatusGroupClass(status) {
  if (status === "played") {
    return "text-emerald-700 theme-dark:text-emerald-300";
  }

  if (status === "seen") {
    return "text-amber-700 theme-dark:text-amber-300";
  }

  return "text-red-700 theme-dark:text-red-300";
}

  function getAdminApiBaseUrl() {
    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    return String(apiBaseUrl).replace(/\/$/, "");
  }

function getGigNotesApiUrl(performerSlug, noteId = "") {
  const cleanSlug = encodeURIComponent(
    performerSlug || "default"
  );

  // Kun noteId annetaan, osoite kohdistuu yhteen olemassa olevaan
  // Keikkamuistiin. Tätä käytetään Keikkatyökalusta tehtävään PUT-päivitykseen.
  const cleanNoteId = noteId
    ? `/${encodeURIComponent(noteId)}`
    : "";

  return `${getAdminApiBaseUrl()}/admin/performers/${cleanSlug}/gig-notes${cleanNoteId}`;
}

  function getGigReportsApiUrl(performerSlug) {
    return `${getAdminApiBaseUrl()}/admin/performers/${encodeURIComponent(
      performerSlug || "default"
    )}/gig-reports`;
  }

function getGigNoteTypeLabel(type) {
  if (type === "speech") {
    return "Välispiikki";
  }

  if (type === "reminder") {
    return "Muistutus";
  }

  return "Muistiinpano";
}

function formatSetlistSongForText(
  song,
  index,
  availableGigNotes = [],
  options = {}
) {
  const artist = song.artist ? ` – ${song.artist}` : "";

  const {
    performancePrefix = "Keikkamerkintä",
    getPerformanceLabel = getSetlistSongPerformanceLabel,
    getGigNoteTypeLabelFn = getGigNoteTypeLabel,
  } = options;

  // Tekstimuotoinen settilista käyttää biisin omaa tekstiä sekä
  // ID:llä linkitettyjen Keikkamuistien tämänhetkistä sisältöä.
  const note = buildSetlistSongNote(
    song,
    availableGigNotes,
    getGigNoteTypeLabelFn
  );

  const performanceLabel = getPerformanceLabel(song);
  const baseLine = `${index + 1}. ${song.title}${artist}`;

  const extraLines = [];

  if (performanceLabel) {
    extraLines.push(`   ↳ ${performancePrefix}: ${performanceLabel}`);
  }

  if (note) {
    extraLines.push(
      ...note
        .split(/\r?\n/)
        .map((line) => `   ↳ ${line}`)
    );
  }

  if (!extraLines.length) {
    return baseLine;
  }

  return `${baseLine}\n${extraLines.join("\n")}`;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getSongNotes(song) {
  return String(song?.notes || "").trim();
}

function hasChordProNotes(song) {
  // Hyväksytään myös tavallisena tekstinä lisätyt sanat.
  // getReadableChordProNotes poistaa mahdolliset ChordPro-metatiedot
  // ja palauttaa vain lukutilassa näytettävän sisällön.
  return Boolean(getReadableChordProNotes(song));
}

const CHORDPRO_SECTION_DIRECTIVES = {
  soc: {
    kind: "chorus",
    labelKey: "chorus",
    defaultLabel: "CHORUS",
  },
  start_of_chorus: {
    kind: "chorus",
    labelKey: "chorus",
    defaultLabel: "CHORUS",
  },
  chorus: {
    kind: "chorus",
    labelKey: "chorus",
    defaultLabel: "CHORUS",
  },

  sov: {
    kind: "section",
    labelKey: "verse",
    defaultLabel: "VERSE",
  },
  start_of_verse: {
    kind: "section",
    labelKey: "verse",
    defaultLabel: "VERSE",
  },
  verse: {
    kind: "section",
    labelKey: "verse",
    defaultLabel: "VERSE",
  },

  sob: {
    kind: "section",
    labelKey: "bridge",
    defaultLabel: "BRIDGE",
  },
  start_of_bridge: {
    kind: "section",
    labelKey: "bridge",
    defaultLabel: "BRIDGE",
  },
  bridge: {
    kind: "section",
    labelKey: "bridge",
    defaultLabel: "BRIDGE",
  },

  sot: {
    kind: "section",
    labelKey: "tab",
    defaultLabel: "TAB",
  },
  start_of_tab: {
    kind: "section",
    labelKey: "tab",
    defaultLabel: "TAB",
  },
  tab: {
    kind: "section",
    labelKey: "tab",
    defaultLabel: "TAB",
  },

  sog: {
    kind: "section",
    labelKey: "grid",
    defaultLabel: "GRID",
  },
  start_of_grid: {
    kind: "section",
    labelKey: "grid",
    defaultLabel: "GRID",
  },

  start_of_section: {
    kind: "section",
    labelKey: "section",
    defaultLabel: "SECTION",
  },
  section: {
    kind: "section",
    labelKey: "section",
    defaultLabel: "SECTION",
  },
};

const CHORDPRO_COMMENT_DIRECTIVES = new Set([
  "c",
  "comment",
  "ci",
  "comment_italic",
  "cb",
  "comment_box",
  "highlight",
]);

const CHORDPRO_HIGHLIGHT_START_DIRECTIVES = new Set([
  "soh",
  "start_of_highlight",
]);

const CHORDPRO_HIGHLIGHT_END_DIRECTIVES = new Set([
  "eoh",
  "end_of_highlight",
]);

/*
 * Näitä upotettuja formaatteja ei voida näyttää järkevästi tavallisena
 * keikkatekstinä. Ohitetaan sekä direktiivit että niiden lähdekoodisisältö.
 */
const CHORDPRO_SKIPPED_ENVIRONMENTS = new Map([
  ["start_of_abc", "end_of_abc"],
  ["start_of_ly", "end_of_ly"],
  ["start_of_svg", "end_of_svg"],
]);

/*
 * Textblock säilyttää sisältönsä, mutta alku- ja lopputunnisteita
 * ei näytetä lukutilassa.
 */
const CHORDPRO_TRANSPARENT_ENVIRONMENTS = new Set([
  "start_of_textblock",
  "end_of_textblock",
]);

const CHORDPRO_SEMANTIC_DIRECTIVE_NAMES = Array.from(
  new Set([
    ...Object.keys(CHORDPRO_SECTION_DIRECTIVES),
    ...CHORDPRO_COMMENT_DIRECTIVES,
    ...CHORDPRO_HIGHLIGHT_START_DIRECTIVES,
    ...CHORDPRO_HIGHLIGHT_END_DIRECTIVES,
    ...CHORDPRO_SKIPPED_ENVIRONMENTS.keys(),
    ...CHORDPRO_SKIPPED_ENVIRONMENTS.values(),
    ...CHORDPRO_TRANSPARENT_ENVIRONMENTS,

    "eoc",
    "end_of_chorus",
    "eov",
    "end_of_verse",
    "eob",
    "end_of_bridge",
    "eot",
    "end_of_tab",
    "eog",
    "end_of_grid",
    "end_of_section",
  ])
).sort((firstName, secondName) => secondName.length - firstName.length);

const CHORDPRO_CHORUS_HEADINGS = [
  "KERTOSÄE",
  "CHORUS",
  "REFRAIN",
  "REFRÄNGI",
  "REFR",
  "REF",
];

const CHORDPRO_SECTION_HEADINGS = [
  "SÄKEISTÖ",
  "SÄE",
  "VERSE",
  "VERS",

  "PRE-CHORUS",
  "PRE CHORUS",
  "PRECHORUS",
  "ESISÄKEISTÖ",
  "PRE",

  "BRIDGE",
  "MIDDLE 8",
  "VÄLIOSA",
  "C-OSA",
  "C OSA",

  "INTRO",
  "ALKU",

  "OUTRO",
  "LOPPU",
  "ENDING",
  "CODA",

  "INSTRUMENTAALI",
  "INSTRUMENTAL",
  "VÄLISOITTO",
  "INTERLUDE",
  "SOOLO",
  "SOLO",

  "TURNAROUND",
  "BREAK",
  "TAUKO",
  "VAMP",
  "HOOK",
  "TAG",

  "A-OSA",
  "A OSA",
  "B-OSA",
  "B OSA",
  "D-OSA",
  "D OSA",

  "TAB",
  "GRID",
];

function normalizeChordProDirectiveName(value) {
  const rawName = String(value || "").trim().toLowerCase();

  if (!rawName) {
    return "";
  }

  const knownName = CHORDPRO_SEMANTIC_DIRECTIVE_NAMES.find(
    (directiveName) =>
      rawName === directiveName ||
      rawName.startsWith(`${directiveName}-`)
  );

  if (knownName) {
    return knownName;
  }

  /*
   * ChordPro sallii esimerkiksi:
   * {start_of_verse-guitar}
   * {comment-soprano: ...}
   *
   * Ympäristön nimi käyttää alaviivoja, joten viimeinen yhdysmerkillä
   * alkava osa voidaan tulkita valitsimeksi.
   */
  if (/^(start_of|end_of)_[a-z0-9_]+-[a-z0-9_!]+$/i.test(rawName)) {
    return rawName.replace(/-[a-z0-9_!]+$/i, "");
  }

  return rawName;
}

function extractChordProDirectiveValue(value) {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "";
  }

  const labelMatch = rawValue.match(
    /\blabel\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s]+))/i
  );

  if (labelMatch) {
    return String(
      labelMatch[1] || labelMatch[2] || labelMatch[3] || ""
    )
      .replace(/\\n/g, "\n")
      .trim();
  }

  return rawValue.replace(/\\n/g, "\n").trim();
}

function parseChordProDirective(line) {
  const trimmedLine = String(line || "").trim();

  const match = trimmedLine.match(
    /^\{\s*([a-z][a-z0-9_]*(?:-[a-z0-9_!]+)*)\s*(?:(?::|\s)\s*([\s\S]*?))?\s*\}$/i
  );

  if (!match) {
    return null;
  }

  return {
    rawName: match[1],
    name: normalizeChordProDirectiveName(match[1]),
    value: extractChordProDirectiveValue(match[2]),
  };
}

function getGenericChordProSectionLabel(directiveName) {
  return String(directiveName || "")
    .replace(/^start_of_/, "")
    .replace(/_/g, " ")
    .trim()
    .toLocaleUpperCase("fi-FI");
}

function getChordProSectionInfo(directive, translate = null) {
  if (!directive) {
    return null;
  }

  const configuredSection =
    CHORDPRO_SECTION_DIRECTIVES[directive.name];

  if (configuredSection) {
    const localizedDefaultLabel =
      typeof translate === "function"
        ? translate(
            `setlistReader.chordProSections.${configuredSection.labelKey}`,
            {
              defaultValue: configuredSection.defaultLabel,
            }
          )
        : configuredSection.defaultLabel;

    return {
      kind: configuredSection.kind,
      label:
        directive.value ||
        localizedDefaultLabel,
    };
  }

  /*
   * ChordPro sallii myös omia ympäristönimiä:
   * {start_of_intro}
   * {start_of_pre_chorus}
   * {start_of_solo}
   *
   * Tunnistetaan ne automaattisesti ilman erillistä nimilistaa.
   */
  if (
    directive.name.startsWith("start_of_") &&
    !CHORDPRO_SKIPPED_ENVIRONMENTS.has(directive.name) &&
    !CHORDPRO_TRANSPARENT_ENVIRONMENTS.has(directive.name) &&
    !CHORDPRO_HIGHLIGHT_START_DIRECTIVES.has(directive.name)
  ) {
    const generatedLabel =
      directive.value ||
      getGenericChordProSectionLabel(directive.name);

    const headingInfo =
      getPlainChordProSectionHeadingInfo(generatedLabel);

    return {
      kind: headingInfo?.kind || "section",
      label: generatedLabel,
    };
  }

  return null;
}

function chordProHeadingStartsWith(value, headings) {
  const comparableValue = String(value || "")
    .trim()
    .replace(/^\d+\s*[.)-]\s*/, "")
    .toLocaleUpperCase("fi-FI");

  return headings.some((heading) => {
    if (comparableValue === heading) {
      return true;
    }

    return [
      `${heading} `,
      `${heading}:`,
      `${heading};`,
      `${heading}=`,
      `${heading} =`,
      `${heading}-`,
      `${heading} -`,
      `${heading}–`,
      `${heading} –`,
      `${heading}—`,
      `${heading} —`,
      `${heading}(`,
      `${heading}[`,
    ].some((prefix) => comparableValue.startsWith(prefix));
  });
}

function getPlainChordProSectionHeadingInfo(value) {
  const cleanValue = String(value || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleanValue || cleanValue.length > 140) {
    return null;
  }

  if (
    chordProHeadingStartsWith(
      cleanValue,
      CHORDPRO_CHORUS_HEADINGS
    )
  ) {
    return {
      kind: "chorus",
      label: cleanValue,
    };
  }

  if (
    chordProHeadingStartsWith(
      cleanValue,
      CHORDPRO_SECTION_HEADINGS
    )
  ) {
    return {
      kind: "section",
      label: cleanValue,
    };
  }

  return null;
}

function getInlineChordProDirectives(line) {
  return Array.from(
    String(line || "").matchAll(/\{[^{}]+\}/g)
  )
    .map((match) => parseChordProDirective(match[0]))
    .filter(Boolean);
}

function stripInlineChordProDirectives(line, translate = null) {
  return String(line || "").replace(
    /\{[^{}]+\}/g,
    (directiveText) => {
      const directive =
        parseChordProDirective(directiveText);

      if (!directive) {
        return "";
      }

      const sectionInfo =
        getChordProSectionInfo(directive, translate);

      if (sectionInfo) {
        return sectionInfo.label;
      }

      if (
        CHORDPRO_COMMENT_DIRECTIVES.has(directive.name)
      ) {
        return directive.value;
      }

      /*
       * Metadata-, fontti-, sivutus-, transpose-, define-,
       * custom x_- ja muut tekniset direktiivit eivät kuulu
       * keikalla näkyvään tekstiin.
       */
      return "";
    }
  );
}

function renderChordProSectionHeading({
  keyValue,
  label,
  kind,
  theme,
  fontSize,
  textAlign,
}) {
  const isChorus = kind === "chorus";

  if (isChorus) {
    return (
      <div
        key={keyValue}
        className="mt-6 mb-3 rounded-[1.1rem] border px-4 py-3 font-black uppercase tracking-[0.14em]"
        style={{
          color: theme.accent,
          background: theme.highlightPanel,
          borderColor: theme.panelBorder,
          boxShadow: `inset 4px 0 0 ${theme.accent}`,
          fontSize: Math.max(16, fontSize * 1.1) + "px",
          lineHeight: 1.35,
          textAlign,
        }}
      >
        {label}
      </div>
    );
  }

  return (
    <div
      key={keyValue}
      className="mt-5 mb-2"
      style={{
        textAlign,
      }}
    >
      <span
        className="inline-flex max-w-full rounded-full border px-3 py-1.5 font-black tracking-[0.12em]"
        style={{
          color: theme.accent,
          background: theme.emptyPanel,
          borderColor: theme.panelBorder,
          fontSize: Math.max(11, fontSize * 0.86) + "px",
          lineHeight: 1.35,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function renderChordProInstruction({
  keyValue,
  text,
  theme,
  fontSize,
  textAlign,
}) {
  return (
    <div
      key={keyValue}
      className="my-3 rounded-xl border-l-4 px-3 py-2 font-semibold"
      style={{
        color: theme.body,
        background: theme.highlightPanel,
        borderColor: theme.accent,
        fontSize: Math.max(11, fontSize * 0.9) + "px",
        lineHeight: 1.55,
        textAlign,
      }}
    >
      {text}
    </div>
  );
}

function formatChordProForDisplay(content, translate = null) {
  const output = [];
  const lines = String(content || "")
    .replace(/\r\n/g, "\n")
    .split("\n");

  let skippedEnvironmentEnd = "";

  lines.forEach((originalLine) => {
    const line = String(originalLine || "");
    const trimmedLine = line.trim();
    const directive = parseChordProDirective(trimmedLine);

    if (skippedEnvironmentEnd) {
      if (directive?.name === skippedEnvironmentEnd) {
        skippedEnvironmentEnd = "";
      }

      return;
    }

    if (!trimmedLine) {
      output.push("");
      return;
    }

    if (trimmedLine.startsWith("#")) {
      return;
    }

    if (directive) {
      const skippedEnvironment =
        CHORDPRO_SKIPPED_ENVIRONMENTS.get(
          directive.name
        );

      if (skippedEnvironment) {
        skippedEnvironmentEnd = skippedEnvironment;
        return;
      }

      const sectionInfo =
        getChordProSectionInfo(directive, translate);

      if (sectionInfo) {
        output.push(sectionInfo.label);
        return;
      }

      if (
        CHORDPRO_COMMENT_DIRECTIVES.has(directive.name) &&
        directive.value
      ) {
        output.push(directive.value);
      }

      /*
       * Kaikki muut kokonaisella rivillä olevat direktiivit
       * on tunnistettu tekniseksi merkinnäksi ja ohitetaan.
       */
      return;
    }

    const cleanLine =
      stripInlineChordProDirectives(line, translate).trimEnd();

    if (cleanLine.trim()) {
      output.push(cleanLine);
    }
  });

  return output
    .filter(
      (line, index, allLines) =>
        !(line === "" && allLines[index - 1] === "")
    )
    .join("\n")
    .trim();
}

function getReadableChordProNotes(song, translate = null) {
  const notes = getSongNotes(song);

  if (!notes) {
    return "";
  }

  const marker = "----";
  const markerIndex = notes.indexOf(marker);

  const readableNotes =
    markerIndex >= 0 ? notes.slice(markerIndex + marker.length).trim() : notes;

  return formatChordProForDisplay(readableNotes, translate);
}

function buildSetlistReaderNotes(song, nextContent) {
  const cleanContent = String(nextContent || "").trim();

  if (!cleanContent) {
    return "";
  }

  const currentNotes = getSongNotes(song);
  const marker = "----";
  const markerIndex = currentNotes.indexOf(marker);

  if (
    currentNotes.includes("SANAT JA SOINNUT / CHORDPRO") &&
    markerIndex >= 0
  ) {
    const header = currentNotes.slice(0, markerIndex + marker.length).trim();
    return `${header}\n\n${cleanContent}`;
  }

  return [
    "SANAT JA SOINNUT / CHORDPRO",
    "",
    song?.title ? `Biisi: ${song.title}` : "",
    song?.artist ? `Artisti: ${song.artist}` : "",
    "",
    "----",
    "",
    cleanContent,
  ]
    .filter((line) => line !== "")
    .join("\n");
}

function getRawChordProBody(song) {
  const notes = getSongNotes(song);

  if (!notes) {
    return "";
  }

  const marker = "----";
  const markerIndex = notes.indexOf(marker);

  return markerIndex >= 0 ? notes.slice(markerIndex + marker.length).trim() : notes;
}

function splitChordProLineToSegments(line) {
  const segments = [];
  const chordPattern = /\[([A-GH][#b]?(?:m|maj|min|sus|dim|aug|add)?[0-9/]*?)\]/gi;
  let cursor = 0;
  let pendingChord = null;
  let match = null;

  while ((match = chordPattern.exec(line)) !== null) {
    const textBeforeChord = line.slice(cursor, match.index);

    if (textBeforeChord) {
      if (pendingChord) {
        segments.push({
          chord: pendingChord,
          text: textBeforeChord,
        });
        pendingChord = null;
      } else {
        segments.push({
          chord: "",
          text: textBeforeChord,
        });
      }
    }

    pendingChord = match[1];
    cursor = match.index + match[0].length;
  }

  const tail = line.slice(cursor);

  if (pendingChord) {
    segments.push({
      chord: pendingChord,
      text: tail || "\u00a0",
    });
  } else if (tail) {
    segments.push({
      chord: "",
      text: tail,
    });
  }

  return segments.length
    ? segments
    : [
        {
          chord: "",
          text: line,
        },
      ];
}

function renderChordProTextLine(line, options) {
  const {
    keyValue,
    showChords,
    theme,
    fontSize,
    isHighlighted,
    highlightColor,
    textAlign = "left",
  } = options;

  const segments = splitChordProLineToSegments(line);
  const hasVisibleLyrics = segments.some((segment) =>
    String(segment.text || "").trim()
  );

  if (!showChords && !hasVisibleLyrics) {
    return (
      <div
        key={keyValue}
        style={{
          minHeight: fontSize * 0.9,
        }}
      />
    );
  }

  const lineStyle = {
    color: theme.body,
    fontSize: fontSize + "px",
    lineHeight: 1.75,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    textAlign,
  };

  const content = (
    <div style={lineStyle}>
      {segments.map((segment, segmentIndex) => {
        const textValue = segment.text || "\u00a0";
        const hasChord = showChords && segment.chord;

        if (!segment.chord) {
          return (
            <span key={segmentIndex}>
              {textValue}
            </span>
          );
        }

        return (
          <span
            key={segmentIndex}
            className="inline-flex flex-col align-bottom"
            style={{
              marginRight: "0.04em",
              minWidth: textValue.trim() ? undefined : "2.4em",
            }}
          >
            {hasChord && (
              <span
                style={{
                  color: theme.accent,
                  fontSize: "0.72em",
                  fontWeight: 900,
                  lineHeight: 1.05,
                  minHeight: "1.05em",
                }}
              >
                {segment.chord}
              </span>
            )}

            <span>
              {textValue}
            </span>
          </span>
        );
      })}
    </div>
  );

  if (!isHighlighted) {
    return (
      <div key={keyValue} className="py-0.5">
        {content}
      </div>
    );
  }

    return (
    <div
      key={keyValue}
      className="border-l-4 px-3 py-0.5"
      style={{
        background: theme.highlightPanel || theme.emptyPanel,
        borderColor: highlightColor || theme.accent,
      }}
    >
      {content}
    </div>
  );
}

function renderChordProReader(song, options) {
  const {
    showChords = true,
    theme = {},
    fontSize = 14,
    textAlign = "left",
    translate = null,
  } = options || {};

  const readerTheme = {
    body: theme.body || "#f4f4f5",
    accent: theme.accent || "#fca5a5",
    emptyPanel:
      theme.emptyPanel ||
      "rgba(255,255,255,0.025)",
    highlightPanel:
      theme.highlightPanel ||
      theme.emptyPanel ||
      "rgba(255,255,255,0.025)",
    panelBorder:
      theme.panelBorder ||
      theme.accent ||
      "rgba(255,255,255,0.10)",
  };

  const rawBody = getRawChordProBody(song);

  if (!rawBody) {
    return null;
  }

  const output = [];
  const lines = rawBody
    .replace(/\r\n/g, "\n")
    .split("\n");

  let highlightActive = false;
  let activeHighlightColor = "";
  let skippedEnvironmentEnd = "";

  lines.forEach((originalLine, lineIndex) => {
    let line = String(originalLine || "");
    let trimmedLine = line.trim();

    const fullLineDirective =
      parseChordProDirective(trimmedLine);

    /*
     * ABC-, LilyPond- ja SVG-lohkojen sisältö ei ole
     * tavallista keikalla näytettävää tekstiä.
     */
    if (skippedEnvironmentEnd) {
      if (
        fullLineDirective?.name ===
        skippedEnvironmentEnd
      ) {
        skippedEnvironmentEnd = "";
      }

      return;
    }

    if (!trimmedLine) {
      output.push(
        <div
          key={"empty-" + lineIndex}
          style={{
            minHeight: fontSize * 0.9,
          }}
        />
      );

      return;
    }

    if (trimmedLine.startsWith("#")) {
      return;
    }

    if (fullLineDirective) {
      const skippedEnvironment =
        CHORDPRO_SKIPPED_ENVIRONMENTS.get(
          fullLineDirective.name
        );

      if (skippedEnvironment) {
        skippedEnvironmentEnd = skippedEnvironment;
        return;
      }

      if (
        CHORDPRO_HIGHLIGHT_START_DIRECTIVES.has(
          fullLineDirective.name
        )
      ) {
        highlightActive = true;

        const colorMatch =
          fullLineDirective.value.match(
            /#[0-9a-f]{3,8}/i
          );

        activeHighlightColor =
          colorMatch?.[0] || "";

        return;
      }

      if (
        CHORDPRO_HIGHLIGHT_END_DIRECTIVES.has(
          fullLineDirective.name
        )
      ) {
        highlightActive = false;
        activeHighlightColor = "";
        return;
      }

      const sectionInfo =
        getChordProSectionInfo(
          fullLineDirective,
          translate
        );

      if (sectionInfo) {
        output.push(
          renderChordProSectionHeading({
            keyValue: "section-" + lineIndex,
            label: sectionInfo.label,
            kind: sectionInfo.kind,
            theme: readerTheme,
            fontSize,
            textAlign,
          })
        );

        return;
      }

      if (
        fullLineDirective.name.startsWith("end_of_") ||
        fullLineDirective.name === "eoc" ||
        fullLineDirective.name === "eov" ||
        fullLineDirective.name === "eob" ||
        fullLineDirective.name === "eot" ||
        fullLineDirective.name === "eog" ||
        CHORDPRO_TRANSPARENT_ENVIRONMENTS.has(
          fullLineDirective.name
        )
      ) {
        return;
      }

      if (
        CHORDPRO_COMMENT_DIRECTIVES.has(
          fullLineDirective.name
        ) &&
        fullLineDirective.value
      ) {
        const headingInfo =
          getPlainChordProSectionHeadingInfo(
            fullLineDirective.value
          );

        if (headingInfo) {
          output.push(
            renderChordProSectionHeading({
              keyValue: "comment-section-" + lineIndex,
              label: headingInfo.label,
              kind: headingInfo.kind,
              theme: readerTheme,
              fontSize,
              textAlign,
            })
          );
        } else {
          output.push(
            renderChordProInstruction({
              keyValue: "comment-" + lineIndex,
              text: fullLineDirective.value,
              theme: readerTheme,
              fontSize,
              textAlign,
            })
          );
        }

        return;
      }

      /*
       * Kaikki muut kokonaisella rivillä olevat ChordPro-
       * direktiivit ovat teknisiä. Niitä ei tulosteta.
       */
      return;
    }

    const inlineDirectives =
      getInlineChordProDirectives(line);

    const highlightStartDirective =
      inlineDirectives.find((directive) =>
        CHORDPRO_HIGHLIGHT_START_DIRECTIVES.has(
          directive.name
        )
      );

    if (highlightStartDirective) {
      highlightActive = true;

      const colorMatch =
        highlightStartDirective.value.match(
          /#[0-9a-f]{3,8}/i
        );

      activeHighlightColor =
        colorMatch?.[0] || "";
    }

    const highlightEndsOnThisLine =
      inlineDirectives.some((directive) =>
        CHORDPRO_HIGHLIGHT_END_DIRECTIVES.has(
          directive.name
        )
      );

    line = stripInlineChordProDirectives(line, translate);
    trimmedLine = line.trim();

    if (!trimmedLine) {
      if (highlightEndsOnThisLine) {
        highlightActive = false;
        activeHighlightColor = "";
      }

      return;
    }

    /*
     * Tunnistetaan myös SongBookin tapa tehdä osio-otsikoita:
     *
     * {soh}SÄE{eoh}
     * {soh}PRE{eoh}
     * {soh}OUTRO = Arttu hidastaa!{eoh}
     */
    const headingInfo =
      getPlainChordProSectionHeadingInfo(
        trimmedLine
      );

    if (headingInfo) {
      output.push(
        renderChordProSectionHeading({
          keyValue: "plain-section-" + lineIndex,
          label: headingInfo.label,
          kind: headingInfo.kind,
          theme: readerTheme,
          fontSize,
          textAlign,
        })
      );

      if (highlightEndsOnThisLine) {
        highlightActive = false;
        activeHighlightColor = "";
      }

      return;
    }

    output.push(
      renderChordProTextLine(line, {
        keyValue: "line-" + lineIndex,
        showChords,
        theme: readerTheme,
        fontSize,
        isHighlighted: highlightActive,
        highlightColor:
          activeHighlightColor,
        textAlign,
      })
    );

    if (highlightEndsOnThisLine) {
      highlightActive = false;
      activeHighlightColor = "";
    }
  });

  return output;
}

function getSetlistSongNote(song) {
  return String(
    song?.setlistNote ||
      song?.setlist_note ||
      ""
  ).trim();
}

// Palauttaa vain biisille käsin kirjoitetun oman muistiinpanon.
//
// Jos setissä on vanhaa dataa ilman erillistä manual-kenttää eikä
// Keikkamuisti-linkkejä ole, vanha setlistNote tulkitaan käsin
// kirjoitetuksi tekstiksi. Näin vanhat setit eivät menetä sisältöään.
function getSetlistSongManualNote(song) {
  const hasExplicitManualNote =
    Object.prototype.hasOwnProperty.call(
      song || {},
      "setlistManualNote"
    ) ||
    Object.prototype.hasOwnProperty.call(
      song || {},
      "setlist_manual_note"
    );

  if (hasExplicitManualNote) {
    return String(
      song?.setlistManualNote ??
        song?.setlist_manual_note ??
        ""
    ).trim();
  }

  const linkedGigNoteIds =
    getSetlistSongGigNoteIds(song);

  // Jos biisiin on jo linkitetty Keikkamuisti ID:llä,
  // vanha setlistNote voi olla aiemman toteutuksen tekstikopio.
  // Sitä ei tulkita enää omaksi manuaaliseksi muistiinpanoksi.
  if (linkedGigNoteIds.length > 0) {
    return "";
  }

  return getSetlistSongNote(song);
}

// Settilistabiisi säilyttää vain linkit Keikkamuistin oikeisiin
// tietueisiin. Sama Keikkamuisti voidaan näyttää useassa paikassa,
// mutta sillä on aina vain yksi alkuperäinen sisältö.
function getSetlistSongGigNoteIds(song) {
  const rawIds =
    song?.setlistGigNoteIds ??
    song?.setlist_gig_note_ids ??
    [];

  const ids = Array.isArray(rawIds)
    ? rawIds
    : rawIds
      ? [rawIds]
      : [];

  return Array.from(
    new Set(
      ids
        .map((id) => String(id).trim())
        .filter(Boolean)
    )
  );
}

// Muodostaa yhden Keikkamuistin lukutilassa ja yhteenvedoissa
// näytettävään muotoon.
function formatGigNoteForSetlist(
  gigNote,
  getTypeLabel = getGigNoteTypeLabel
) {
  if (!gigNote) {
    return "";
  }

  const title = String(
    gigNote.title || ""
  ).trim();

  const content = String(
    gigNote.content || ""
  ).trim();

  const typeLabel = getTypeLabel(
    gigNote.type || gigNote.note_type
  );

  return [
    title
      ? `${typeLabel}: ${title}`
      : typeLabel,
    content,
  ]
    .filter(Boolean)
    .join("\n");
}

// Rakentaa biisin näkyvän muistiinpanon aina kahdesta lähteestä:
//
// 1. mahdollinen biisikohtainen oma teksti
// 2. ID:llä linkitettyjen Keikkamuistien tämänhetkinen sisältö
//
// Keikkamuistin tekstiä ei siis tallenneta erillisenä kopiona.
function buildSetlistSongNote(
  song,
  availableGigNotes = [],
  getTypeLabel = getGigNoteTypeLabel
) {
  const manualNote =
    getSetlistSongManualNote(song);

  const linkedGigNotes =
    getSetlistSongGigNoteIds(song)
      .map((gigNoteId) =>
        availableGigNotes.find(
          (item) =>
            String(item.id) ===
            String(gigNoteId)
        )
      )
      .filter(Boolean);

  return [
    manualNote,
    ...linkedGigNotes.map((gigNote) =>
      formatGigNoteForSetlist(
        gigNote,
        getTypeLabel
      )
    ),
  ]
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

// Vanhan bugisen toteutuksen jäljiltä setlistNote voi sisältää
// saman Keikkamuistin tekstikopion jo kerran tai useammin.
//
// Kun vanha setti siirretään ID-linkkimalliin, poistetaan saman
// Keikkamuistin otsikolla alkavat vanhat tekstikopiot.
// Muuta käsin kirjoitettua sisältöä ei poisteta.
function removeLegacyGigNoteCopies(
  value,
  gigNote
) {
  const noteText = String(
    value || ""
  ).trim();

  const title = String(
    gigNote?.title || ""
  ).trim();

  if (!noteText || !title) {
    return noteText;
  }

  const typeLabel = getGigNoteTypeLabel(
    gigNote?.type ||
      gigNote?.note_type
  );

  const linkedHeader =
    `${typeLabel}: ${title}`;

  return noteText
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .filter((block) => {
      const firstLine = String(
        block.split(/\r?\n/)[0] || ""
      ).trim();

      return firstLine !== linkedHeader;
    })
    .join("\n\n")
    .trim();
}

function getSetlistSongPerformanceStatus(song) {
  return String(
    song?.performanceStatus ||
      song?.performance_status ||
      song?.setlistPerformanceStatus ||
      ""
  ).trim();
}

function getSetlistSongPerformanceLabel(song) {
  const status = getSetlistSongPerformanceStatus(song);

  if (status === "played") {
    return "Soitettu";
  }

  if (status === "skipped") {
    return "Ei soitettu";
  }

  return "";
}

const SAVED_SETLISTS_KEY = "paranoidi:saved-setlists";

const SETLIST_SAVE_SUCCESS_UPDATED =
  "__setlist_save_success_updated__";

const SETLIST_SAVE_SUCCESS_CREATED =
  "__setlist_save_success_created__";

const SETLIST_READER_THEME_OPTIONS = [
  { value: "dark", labelKey: "text264" },
  { value: "light", labelKey: "text265" },
  { value: "contrast", labelKey: "text266" },
];

const SETLIST_READER_THEMES = {
  dark: {
    fullscreenBackground: "#050605",
    shell: "#050605",
    surface: "#050605",
    toolbar: "#050605",
    navigation: "#050605",

    panel: "rgba(0,0,0,0.35)",
    panelBorder: "rgba(255,255,255,0.10)",
    divider: "rgba(255,255,255,0.10)",

    body: "#f4f4f5",
    muted: "#a1a1aa",
    subtle: "#71717a",

    accent: "#fca5a5",
    accentStrong: "#ef4444",
    emptyPanel: "rgba(255,255,255,0.025)",
    highlightPanel: "rgba(239,68,68,0.10)",

    buttonBackground: "rgba(255,255,255,0.04)",
    buttonBorder: "rgba(255,255,255,0.10)",
    buttonText: "#ffffff",

    activeBackground: "rgba(239,68,68,0.18)",
    activeText: "#fecaca",

    shadow: "0 28px 80px rgba(0,0,0,0.60)",
  },

  light: {
    fullscreenBackground: "#ead6c2",
    shell: "#fff8ef",
    surface: "#fff8ef",
    toolbar: "#f3e5d9",
    navigation: "#fff8ef",

    panel: "#fffdf9",
    panelBorder: "rgba(90,56,36,0.20)",
    divider: "rgba(90,56,36,0.16)",

    body: "#241a16",
    muted: "#6b4229",
    subtle: "#8a5a35",

    accent: "#b42318",
    accentStrong: "#ef4444",
    emptyPanel: "#fff8ef",
    highlightPanel: "#f2d8d5",

    buttonBackground: "#fffdf9",
    buttonBorder: "rgba(90,56,36,0.18)",
    buttonText: "#541717",

    activeBackground: "#f2d8d5",
    activeText: "#b42318",

    shadow: "0 28px 70px rgba(58,43,24,0.18)",
  },

  contrast: {
    fullscreenBackground: "#000000",
    shell: "#000000",
    surface: "#000000",
    toolbar: "#000000",
    navigation: "#000000",

    panel: "#000000",
    panelBorder: "rgba(250,204,21,0.78)",
    divider: "rgba(250,204,21,0.48)",

    body: "#ffffff",
    muted: "#fef3c7",
    subtle: "#facc15",

    accent: "#facc15",
    accentStrong: "#facc15",
    emptyPanel: "#000000",
    highlightPanel: "#151515",

    buttonBackground: "#000000",
    buttonBorder: "rgba(250,204,21,0.75)",
    buttonText: "#ffffff",

    activeBackground: "#facc15",
    activeText: "#000000",

    shadow:
      "0 0 0 1px rgba(250,204,21,0.55), 0 0 42px rgba(250,204,21,0.10)",
  },
};

const SETLIST_OCCASIONS = [
  "After ski",
  "Baarikeikka",
  "Brunssi",
  "Festivaali",
  "Häät",
  "Iltamat",
  "Jatkot",
  "Joulukeikka",
  "Klubi",
  "Laiva",
  "Lastenjuhlat",
  "Lounge",
  "Opiskelijabileet",
  "Palvelutalo",
  "Päivätanssit",
  "Pikkujoulut",
  "Polttarit",
  "Pubi",
  "Ravintola",
  "Risteily",
  "Syntymäpäivät",
  "Tapahtuma",
  "Terassikeikka",
  "Tanssit",
  "Urheilutapahtuma",
  "Valmistujaiset",
  "Vuosijuhla",
  "Yksityisjuhlat",
  "Yritysjuhlat",
  "Yökerho",
];

const SONG_LANGUAGE_LABELS = {
  fi: "Finnish",
  en: "English",
  sv: "Swedish",
  de: "German",
  es: "Spanish",
  fr: "French",
  it: "Italian",
  no: "Norwegian",
  da: "Danish",
  et: "Estonian",
  other: "Other",
  unknown: "Unknown",
};

function getSongLanguage(song) {
  return String(
    song?.language ||
      song?.songLanguage ||
      song?.song_language ||
      "unknown"
  ).trim() || "unknown";
}

function formatSongLanguageLabel(language) {
  const value = String(language || "unknown").trim();

  return SONG_LANGUAGE_LABELS[value] || value.toUpperCase();
}

function parseCommaSeparatedFilters(value) {
  return String(value || "")
    .split(",")
    .map((item) => normalize(item.trim()))
    .filter(Boolean);
}

function textMatchesAnyFilter(value, filters) {
  const normalizedValue = normalize(value);

  return filters.some((filter) => filter && normalizedValue.includes(filter));
}

function getSongStyleSearchText(song) {
  return [
    song?.genre,
    song?.mood,
    song?.decade,
    getSongOccasionTags(song),
  ]
    .filter(Boolean)
    .join(" ");
}

function getSongOccasionTags(song) {
  return song?.occasionTags || song?.occasion_tags || "";
}

function parseTagList(value) {
  return String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function shuffleItems(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

function isSongRequestable(song) {
  const rawValue = song?.isRequestable ?? song?.is_requestable;

  if (rawValue === undefined || rawValue === null) {
    return true;
  }

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue === 1;
  }

  return String(rawValue).toLowerCase() !== "false" && String(rawValue) !== "0";
}

function parseSongImportLine(line) {
  const cleanLine = String(line || "").trim();

  if (!cleanLine) {
    return null;
  }

  const parts = cleanLine
    .split(/\s+[-–—]\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    title: parts[0] || "",
    artist: parts.slice(1).join(" – ") || "",
    raw: cleanLine,
  };
}

function detectSongLanguageFromText(value) {
  const text = normalize(value);

  const scores = {
    fi: 0,
    en: 0,
    sv: 0,
    es: 0,
    fr: 0,
    de: 0,
    it: 0,
  };

  const addScore = (language, words, points = 1) => {
    words.forEach((word) => {
      const pattern = new RegExp(`(^|\\s)${word}(\\s|$)`, "i");

      if (pattern.test(text)) {
        scores[language] += points;
      }
    });
  };

  if (/[äöå]/i.test(String(value || ""))) {
    scores.fi += 2;
  }

  addScore("fi", [
    "ja",
    "on",
    "olen",
    "olet",
    "sinä",
    "mina",
    "mun",
    "sun",
    "meidan",
    "teidan",
    "rakkaus",
    "sydan",
    "yo",
    "ilta",
    "aamu",
    "tanaan",
    "huomenna",
    "kaipaan",
    "laulu",
    "tanssi",
  ]);

  addScore("en", [
    "the",
    "and",
    "you",
    "your",
    "love",
    "baby",
    "night",
    "heart",
    "dance",
    "dream",
    "forever",
    "tonight",
    "never",
    "always",
    "come",
    "go",
    "my",
    "me",
  ]);

  addScore("sv", [
    "och",
    "jag",
    "du",
    "det",
    "min",
    "din",
    "karlek",
    "hjarta",
    "natt",
    "dans",
  ]);

  addScore("es", [
    "el",
    "la",
    "mi",
    "amor",
    "corazon",
    "noche",
    "baila",
    "vida",
  ]);

  addScore("fr", [
    "le",
    "la",
    "mon",
    "ma",
    "amour",
    "coeur",
    "nuit",
    "danse",
  ]);

  addScore("de", [
    "der",
    "die",
    "das",
    "ich",
    "du",
    "liebe",
    "herz",
    "nacht",
  ]);

  addScore("it", [
    "il",
    "la",
    "amore",
    "cuore",
    "notte",
    "vita",
    "bella",
  ]);

  const winner = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];

  if (!winner || winner[1] <= 0) {
    return "unknown";
  }

  return winner[0];
}

function buildBulkSongItems(lines, selectedLanguage) {
  return lines
    .map(parseSongImportLine)
    .filter(Boolean)
    .map((item) => ({
      title: item.title,
      artist: item.artist,
      language:
        selectedLanguage === "auto"
          ? detectSongLanguageFromText(`${item.title} ${item.artist}`)
          : selectedLanguage,
    }));
}

function getLoggedInPerformerName(fallbackName) {
  try {
    const rawPerformer = window.sessionStorage.getItem("paranoidi_auth_performer");
    const performer = rawPerformer ? JSON.parse(rawPerformer) : null;

    return performer?.name || fallbackName || "Paranoidi";
  } catch {
    return fallbackName || "Paranoidi";
  }
}

function isFreeRequest(request) {
  return !request.song_id && !request.songId;
}

function isRaffleOptedIn(request) {
  const rawValue = request?.raffleOptIn ?? request?.raffle_opt_in ?? false;

  return (
    rawValue === true ||
    rawValue === 1 ||
    String(rawValue).trim().toLowerCase() === "true" ||
    String(rawValue).trim() === "1"
  );
}

function getRequestCount(request) {
  const count =
    request.requestCount ??
    request.request_count ??
    request.count ??
    1;

  const numberValue = Number(count);

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 1;
}

// Yksi maksettu näkyvyysnosto maksaa tällä hetkellä 3 €.
// Tallennamme summan sentteinä, jotta rahalaskuissa ei tule liukulukujen
// pyöristysvirheitä.
const REQUEST_BOOST_PRICE_CENTS = 300;

function formatBoostAmount(cents, locale = "fi-FI") {
  const amount = Number(cents || 0) / 100;

  return amount.toLocaleString(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getRequestSongLabel(
  request,
  unknownSongLabel = "Tuntematon biisi"
) {
  const title =
    String(request?.songTitle || request?.song_title || "").trim() ||
    unknownSongLabel;

  const artist = String(
    request?.artist || request?.songArtist || request?.song_artist || ""
  ).trim();

  return artist ? `${title} – ${artist}` : title;
}

function sortSummaryItems(items, locale = "fi") {
  return [...items].sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }

    return a.label.localeCompare(b.label, locale);
  });
}

function formatSummaryList(items, emptyText) {
  if (!items.length) {
    return `- ${emptyText}`;
  }

  return items
    .map((item, index) => `${index + 1}. ${item.label} — ${item.count}`)
    .join("\n");
}

function formatSetlistForSummaryText(
  sets,
  availableGigNotes = [],
  options = {}
) {
  const {
    emptySetlistText = "Ei settilistaa mukana raportissa.",
    defaultSetTitle = "Setti",
    emptySetText = "Ei biisejä.",
    songTextOptions = {},
  } = options;

  if (!Array.isArray(sets) || !sets.length) {
    return `- ${emptySetlistText}`;
  }

  return sets
    .map((set) => {
      const songs = Array.isArray(set?.songs)
        ? set.songs
        : [];

      const title =
        String(set?.title || defaultSetTitle).trim() ||
        defaultSetTitle;

      if (!songs.length) {
        return `${title}\n- ${emptySetText}`;
      }

      return `${title}\n${songs
        .map((song, index) =>
          formatSetlistSongForText(
            song,
            index,
            availableGigNotes,
            songTextOptions
          )
        )
        .join("\n")}`;
    })
    .join("\n\n");
}

function getSetlistSets(value) {
  return Array.isArray(value) ? value : [];
}

function getSetlistSetCount(value) {
  return getSetlistSets(value).length;
}

function getSetlistSongCount(value) {
  return getSetlistSets(value).reduce((total, set) => {
    const songs = Array.isArray(set?.songs) ? set.songs : [];
    return total + songs.length;
  }, 0);
}

function getSetlistMaxSongsPerSet(value) {
  const counts = getSetlistSets(value).map((set) =>
    Array.isArray(set?.songs) ? set.songs.length : 0
  );

  return counts.length ? Math.max(...counts) : 0;
}

function formatSavedSetlistMeta(savedSetlist, t) {
  const sets = getSetlistSets(savedSetlist?.sets);
  const setCount =
    getSetlistSetCount(sets) ||
    Number(savedSetlist?.setCount || savedSetlist?.set_count || 0);

  const songCount = getSetlistSongCount(sets);

  // Vain näytettävä metatieto käännetään. Alkuperäinen tilaisuustägi säilyy.
  const occasionLabel = savedSetlist?.occasion
    ? translateAdminLabel(savedSetlist.occasion)
    : t("savedSetlist.defaultName");

  return [
    occasionLabel,
    t("savedSetlist.setCount", { count: setCount }),
    t("savedSetlist.songCount", { count: songCount }),
  ].join(" · ");
}

// Backend voi muodostaa tyhjälle nimelle automaattisen nimen,
// esimerkiksi "Häät · 1 setti · 5 biisiä".
// Automaattista nimeä ei käsitellä käyttäjän itse kirjoittamana nimenä,
// koska sen biisi- ja settimäärien pitää elää sisällön mukana.
function isAutoGeneratedSetlistTitle(value) {
  const title = String(value || "").trim();

  return /^.+\s+[·•]\s+\d+\s+(?:setti(?:ä)?|sets?)\s+[·•]\s+\d+\s+(?:biisi(?:ä)?|songs?)$/i.test(
    title
  );
}

function getEditableSetlistTitle(value) {
  const title = String(value || "").trim();

  return isAutoGeneratedSetlistTitle(title) ? "" : title;
}

function getSavedSetlistDisplayTitle(savedSetlist, t) {
  return (
    getEditableSetlistTitle(savedSetlist?.title) ||
    formatSavedSetlistMeta(savedSetlist, t)
  );
}

/*
 * Tallennettu settilista voi sisältää biisin tunnisteen eri nimillä.
 * Ensin etsitään biisi tunnisteella. Ellei tunnisteella löydy osumaa,
 * käytetään täsmällistä biisin nimi + esittäjä -yhdistelmää.
 */
function getSetlistSongLibraryId(song) {
  return song?.id ?? song?.songId ?? song?.song_id ?? null;
}

function findMatchingLibrarySong(librarySongs, setlistSong) {
  const setlistSongId = getSetlistSongLibraryId(setlistSong);

  if (
    setlistSongId !== null &&
    setlistSongId !== undefined &&
    String(setlistSongId).trim()
  ) {
    const matchingSongById = librarySongs.find(
      (librarySong) =>
        String(getSetlistSongLibraryId(librarySong)) ===
        String(setlistSongId)
    );

    if (matchingSongById) {
      return matchingSongById;
    }
  }

  const setlistTitle = normalize(
    setlistSong?.title ??
      setlistSong?.songTitle ??
      setlistSong?.song_title
  );

  const setlistArtist = normalize(
    setlistSong?.artist ??
      setlistSong?.songArtist ??
      setlistSong?.song_artist
  );

  if (!setlistTitle) {
    return null;
  }

  return (
    librarySongs.find(
      (librarySong) =>
        normalize(librarySong?.title) === setlistTitle &&
        normalize(librarySong?.artist) === setlistArtist
    ) || null
  );
}

export default function SongRequestsPage({ config }) {
  // Käännetään vain käyttöliittymän tekstit. Biisien ja esiintyjän sisältö säilyy.
  const { t, i18n } = useTranslation("admin");

  const adminLocale =
  i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en-GB"
    : "fi-FI";

  function getLocalizedGigNoteTypeLabel(type) {
    if (type === "speech") {
      return t("gigNoteTypes.speech", {
        defaultValue: "Välispiikki",
      });
    }

    if (type === "reminder") {
      return t("gigNoteTypes.reminder", {
        defaultValue: "Muistutus",
      });
    }

    return t("gigNoteTypes.note", {
      defaultValue: "Muistiinpano",
    });
  }

  function getLocalizedSetlistSongPerformanceLabel(song) {
    const status = getSetlistSongPerformanceStatus(song);

    if (status === "played") {
      return t("setlistPerformance.played", {
        defaultValue: "Soitettu",
      });
    }

    if (status === "skipped") {
      return t("setlistPerformance.skipped", {
        defaultValue: "Ei soitettu",
      });
    }

    return "";
  }

  function getLocalizedAdminError(error, key, defaultValue) {
    const rawMessage = String(error?.message || "").trim();

    if (rawMessage) {
      const translatedMessage = translateAdminLabel(rawMessage);

      // Tunnetut vanhat backend-/UI-viestit voidaan edelleen kääntää
      // adminLabels-kartan kautta.
      if (translatedMessage && translatedMessage !== rawMessage) {
        return translatedMessage;
      }

      // Suomenkielisessä hallinnassa palvelimen tarkempi virhe voidaan näyttää.
      // Englanninkielisessä hallinnassa tuntematonta suomenkielistä backend-
      // viestiä ei päästetä käyttöliittymään, vaan käytetään vakaata käännösavainta.
      if (!adminLocale.toLowerCase().startsWith("en")) {
        return rawMessage;
      }
    }

    return t(key, { defaultValue });
  }

  function getLocalizedSongLanguageLabel(language) {
    const value = String(language || "unknown").trim();

    const languageKeys = {
      fi: "text059",
      en: "text060",
      sv: "text061",
      de: "text062",
      es: "text063",
      fr: "text267",
      it: "text064",
      et: "text065",
      no: "text066",
      da: "text067",
      other: "text068",
      unknown: "text269",
    };

    const key = languageKeys[value];

    return key
      ? t(key, {
          defaultValue: formatSongLanguageLabel(value),
        })
      : value.toUpperCase();
  }

  function getLocalizedRequestStatusLabel(status) {
    if (status === "played") {
      return t("text024", { defaultValue: "Soitettu" });
    }

    if (status === "seen") {
      return t("text260", { defaultValue: "Nähty" });
    }

    return t("text261", { defaultValue: "Uusi" });
  }

  function getLocalizedRequestStatusGroupLabel(status) {
    if (status === "played") {
      return t("text100", { defaultValue: "Soitetut" });
    }

    if (status === "seen") {
      return t("text262", { defaultValue: "Nähdyt toiveet" });
    }

    return t("text263", { defaultValue: "Uudet toiveet" });
  }

  function getLocalizedSetTitle(value, index) {
    const title = String(value || "").trim();

    if (!title || /^(?:setti|set)\s+\d+$/i.test(title)) {
      return t("setlistDynamic.setTitle", {
        number: index + 1,
        defaultValue: `Setti ${index + 1}`,
      });
    }

    return title;
  }

  const [requests, setRequests] = useState([]);
  const [librarySongs, setLibrarySongs] = useState([]);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [raffleWinner, setRaffleWinner] = useState(null);
  const [raffleError, setRaffleError] = useState("");
  const [liveSummaryMessage, setLiveSummaryMessage] = useState("");
  const [savedGigReports, setSavedGigReports] = useState([]);
  const [isSavingLiveSummary, setIsSavingLiveSummary] = useState(false);

  // Tallennetaan poistettavan raportin id, jotta selaimen window.confirmia
  // ei tarvitse käyttää.
  const [gigReportToDelete, setGigReportToDelete] = useState(null);
  const [isDeletingGigReport, setIsDeletingGigReport] = useState(false);

  const [isStartingNewGig, setIsStartingNewGig] = useState(false);
  const [isStartNewGigModalOpen, setIsStartNewGigModalOpen] = useState(false);
  const [isPlayedRequestsOpen, setIsPlayedRequestsOpen] = useState(false);
  const [bulkGenre, setBulkGenre] = useState("iskelmä");
  const [bulkLanguage, setBulkLanguage] = useState("auto");
  const [bulkDecade, setBulkDecade] = useState("1970-luku");
  const [bulkYear, setBulkYear] = useState("1970");
  const [bulkMood, setBulkMood] = useState("klassikko, tunnelmallinen");
  const [bulkSongsText, setBulkSongsText] = useState("");
  const [bulkImportMessage, setBulkImportMessage] = useState("");
  const [adminTab, setAdminTab] = useState(() => {
    try {
      const savedTab =
        window.sessionStorage.getItem("paranoidi:admin-tab");

      const allowedTabs = [
        "requests",
        "events",
        "songs",
        "setlists",
        "gigNotes",
        "summary",
      ];

      if (allowedTabs.includes(savedTab)) {
        return savedTab;
      }
    } catch {
      // Jos sessionStorage ei ole käytettävissä,
      // aloitetaan normaalisti Toivehallinnasta.
    }

    return "requests";
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        "paranoidi:admin-tab",
        adminTab
      );
    } catch {
      // Ei estä käyttöä, vaikka sessionStorage ei olisi käytettävissä.
    }
  }, [adminTab]);
  const [setlistOccasion, setSetlistOccasion] = useState("Häät");
  const [setlistSetCount, setSetlistSetCount] = useState(2);
  const [setlistSongsPerSet, setSetlistSongsPerSet] = useState(12);
  const [setlistLanguageFilter, setSetlistLanguageFilter] = useState("all");
  const [setlistExcludedArtists, setSetlistExcludedArtists] = useState("");
  const [setlistExcludedGenres, setSetlistExcludedGenres] = useState("");
  const [setlistTitle, setSetlistTitle] = useState("");
  const [generatedSetlist, setGeneratedSetlist] = useState([]);
  const [activeSetlistSong, setActiveSetlistSong] = useState(null);
  const lyricsScrollRef = useRef(null);
  const prompterRemainderRef = useRef(0);
  const wakeLockRef = useRef(null);
  const setlistSwipeStartRef = useRef(null);
  const [isPrompterRunning, setIsPrompterRunning] = useState(false);
  const [prompterSpeed, setPrompterSpeed] = useState(() => {
    try {
      const savedSpeed = Number(
        window.localStorage.getItem("paranoidi:setlist-reader-prompter-speed")
      );

      if (Number.isFinite(savedSpeed)) {
        return Math.min(12, Math.max(1, savedSpeed));
      }
    } catch {
      // Käytetään oletusnopeutta.
    }

    return 3;
  });
  const [isSetlistSongEditOpen, setIsSetlistSongEditOpen] = useState(false);
  const [isSetlistNotePanelOpen, setIsSetlistNotePanelOpen] = useState(false);
  const [isSetlistReaderControlsHidden, setIsSetlistReaderControlsHidden] = useState(false);
  const [setlistSongEditNotes, setSetlistSongEditNotes] = useState("");
  const [isSetlistSongEditSaving, setIsSetlistSongEditSaving] = useState(false);
  const [setlistSongEditMessage, setSetlistSongEditMessage] = useState("");
    const [setlistReaderFontSize, setSetlistReaderFontSize] = useState(() => {
    try {
      const savedFontSize = Number(
        window.localStorage.getItem("paranoidi:setlist-reader-font-size")
      );

      if (Number.isFinite(savedFontSize)) {
        return Math.min(22, Math.max(10, savedFontSize));
      }
    } catch {
      // Jos localStorage ei ole käytettävissä, käytetään oletuskokoa.
    }

    return 14;
  });

  const [setlistReaderTheme, setSetlistReaderTheme] = useState("light");
  const [isSetlistReaderChordsVisible, setIsSetlistReaderChordsVisible] = useState(() => {
    try {
      const savedValue = window.localStorage.getItem("paranoidi:setlist-reader-chords-visible");

      if (savedValue === "false") {
        return false;
      }

      if (savedValue === "true") {
        return true;
      }
    } catch {
      // Jos localStorage ei ole käytettävissä, näytetään soinnut oletuksena.
    }

    return true;
  });
  const [setlistReaderTextAlign, setSetlistReaderTextAlign] = useState(() => {
    try {
      const savedValue = window.localStorage.getItem("paranoidi:setlist-reader-text-align");

      if (savedValue === "center" || savedValue === "left") {
        return savedValue;
      }
    } catch {
      // Jos localStorage ei ole käytettävissä, käytetään vasenta reunaa.
    }

    return "left";
  });
  const [setlistMessage, setSetlistMessage] = useState("");
  const [randomOccasion, setRandomOccasion] = useState("kaikki");
  const [randomSong, setRandomSong] = useState(null);
  const [randomMessage, setRandomMessage] = useState("");
  const [activeInfoModal, setActiveInfoModal] = useState(null);
  const [savedSetlists, setSavedSetlists] = useState([]);
  const [activeSavedSetlistId, setActiveSavedSetlistId] = useState(null);

  // Muistetaan viimeksi palvelimelle tallennetun settilistan sisältö.
  // Tätä verrataan nykyiseen settiin, jotta käyttöliittymä osaa
  // kertoa, jos muutoksia ei ole vielä tallennettu.
  const [
    savedSetlistEditSnapshot,
    setSavedSetlistEditSnapshot,
  ] = useState("");

  function buildSetlistEditSnapshot({
  title = "",
  occasion = "",
  sets = [],
} = {}) {
  return JSON.stringify({
    title: String(title || "").trim(),
    occasion: String(occasion || "").trim(),

    // Verrataan vain Keikkatyökalussa oikeasti muokattavia
    // settilistan tietoja. Esimerkiksi Keikkamuistin sisältöä
    // ei kopioida settiin, joten sen muuttaminen ei tee setistä
    // tallentamatonta.
    sets: (Array.isArray(sets) ? sets : []).map((set) => ({
      title: String(set?.title || ""),
      songs: (Array.isArray(set?.songs) ? set.songs : []).map(
        (song) => ({
          id: String(
            song?.id ??
              song?.songId ??
              song?.song_id ??
              ""
          ),
          title: String(song?.title || ""),
          artist: String(song?.artist || ""),
          manualNote: getSetlistSongManualNote(song),
          gigNoteIds: getSetlistSongGigNoteIds(song),
        })
      ),
    })),
  });
}

  const currentSetlistEditSnapshot = buildSetlistEditSnapshot({
    title: setlistTitle,
    occasion: setlistOccasion,
    sets: generatedSetlist,
  });

  const hasUnsavedSetlistChanges =
    generatedSetlist.length > 0 &&
    (
      !activeSavedSetlistId ||
      !savedSetlistEditSnapshot ||
      currentSetlistEditSnapshot !== savedSetlistEditSnapshot
    );

  const isSetlistSaveSuccess =
    !hasUnsavedSetlistChanges &&
    (
      setlistMessage === SETLIST_SAVE_SUCCESS_UPDATED ||
      setlistMessage === SETLIST_SAVE_SUCCESS_CREATED
    );

  // Muistetaan, mikä backendistä palautettu aktiivinen settilista
  // on jo palautettu tämän sivulatauksen aikana.
  // Näin effect ei lataa samaa settiä uudelleen jokaisella renderillä.
  const restoredCurrentGigSetlistIdRef = useRef(null);

  const [setlistSongSearchBySet, setSetlistSongSearchBySet] = useState({});
  const [activeSetlistAddSongSetIndex, setActiveSetlistAddSongSetIndex] =
    useState(null);
  const [gigNotes, setGigNotes] = useState([]);

  // Keikkatyökalussa muokattavat Keikkamuistit pidetään luonnoksina
  // niiden oikean Keikkamuisti-ID:n mukaan.
  // Näin sama muistiinpano ei saa biisikohtaista tekstikopiota.
  const [gigNoteDrafts, setGigNoteDrafts] = useState({});

  const [gigNoteSaveNotice, setGigNoteSaveNotice] = useState({
    key: "",
    type: "",
    text: "",
  });

  const [paranoidiSettings, setParanoidiSettings] = useState(() =>
    getParanoidiSettings()
  );

  // Aktiivista keikkasettiä ei palauteta sessionStorage-cachen perusteella.
  // Odotetaan ensin tuoreet asetukset backendiltä.
  const [
    areParanoidiSettingsLoaded,
    setAreParanoidiSettingsLoaded,
  ] = useState(false);

  const [freshPerformer, setFreshPerformer] = useState(null);

const requestRaffleEnabledRaw =
  paranoidiSettings?.requestRaffleEnabled ??
  paranoidiSettings?.request_raffle_enabled ??
  config?.paranoidiSettings?.requestRaffleEnabled ??
  config?.paranoidiSettings?.request_raffle_enabled ??
  config?.performer?.settings?.requestRaffleEnabled ??
  config?.performer?.settings?.request_raffle_enabled ??
  config?.performer?.requestRaffleEnabled ??
  config?.performer?.request_raffle_enabled ??
  config?.requestRaffleEnabled ??
  config?.request_raffle_enabled ??
  false;

const isRequestRaffleEnabled =
  requestRaffleEnabledRaw === true ||
  requestRaffleEnabledRaw === 1 ||
  String(requestRaffleEnabledRaw).trim().toLowerCase() === "true" ||
  String(requestRaffleEnabledRaw).trim() === "1";

  const effectiveFeatures = freshPerformer?.features || config?.features || {};
const effectivePerformer = freshPerformer || config?.performer || {};

// Settilistat kuuluvat Plus-pakettiin.
const canUseSetlists = effectiveFeatures.setlistsEnabled === true;

// Keikkamuisti pidetään omana ominaisuutenaan,
// jotta paketit voidaan rajata feature-kohtaisesti.
const canUseGigNotes = effectiveFeatures.gigNotesEnabled === true;

// Tapahtumat kuuluvat Plus-pakettiin.
const canUseEvents = effectiveFeatures.eventsEnabled === true;

// Yleisölle näkyvän kielen rajaus näytetään vain Basic/Plus-paketeissa.
// Trialissa biisit näkyvät yleisölle kaikki kielet -tilassa.
const canUsePublicLanguageFilter =
  effectiveFeatures.publicBrandingEnabled === true;

// Live-yhteenveto on ensimmäisessä versiossa read-only-näkymä nykyisistä
// biisitoiveista. Se näytetään Plus-käyttäjälle, jolla on lavatyökalut käytössä.

const canUseLiveSummary = canUseSetlists || canUseGigNotes;

// Päävalikossa näytetään vain ne osiot, joita käyttäjä voi oikeasti käyttää.
const adminTabCount =
  2 +
  (canUseEvents ? 1 : 0) +
  (canUseSetlists ? 1 : 0) +
  (canUseGigNotes ? 1 : 0) +
  (canUseLiveSummary ? 1 : 0);

const adminTabGridClass =
  adminTabCount >= 6
    ? "grid-cols-1 sm:grid-cols-3 xl:grid-cols-6"
    : adminTabCount >= 5
      ? "grid-cols-1 sm:grid-cols-3 xl:grid-cols-5"
    : adminTabCount >= 4
      ? "grid-cols-2 sm:grid-cols-4"
      : adminTabCount === 3
        ? "grid-cols-1 sm:grid-cols-3"
        : "grid-cols-2";
  // Hallinnan yläosa kertoo aina selvästi, missä osiossa käyttäjä on.
  // Pakettirajaukset hoidetaan jo välilehtien näkyvyydessä.
  const adminHeroContentByTab = {
    requests: {
      title: t("text075"),
      intro:
        t("text253"),
    },
    events: {
      title: t("text076"),
      intro:
        t("text254"),
    },
    songs: {
      title: t("text052"),
      intro:
        canUseSetlists || canUseGigNotes
          ? t("text255")
          : t("text256"),
    },
    setlists: {
      title: t("text077"),
      intro:
        t("text257"),
    },
    gigNotes: {
      title: t("text078"),
      intro:
        t("text258"),
    },
    summary: {
      title: t("text079"),
      intro:
        t("text259"),
    },
  };

  const adminHeroContent =
    adminHeroContentByTab[adminTab] ||
    adminHeroContentByTab.requests;

  const adminHeroTitle = adminHeroContent.title;
  const adminHeroIntro = adminHeroContent.intro;

  function getAdminTabButtonClass(isActive) {
    return [
      "group relative min-h-[4.25rem] overflow-hidden rounded-[1.35rem] border px-4 py-3 text-left transition duration-200 sm:min-h-[3.75rem] sm:rounded-full sm:text-center",
      isActive
        ? "border-[#ef4444]/70 bg-gradient-to-r from-[#7f1d1d] via-[#991b1b] to-[#450a0a] text-white shadow-[0_0_0_1px_rgba(239,68,68,0.22),0_18px_38px_rgba(127,29,29,0.34)]"
        : "border-[#5a3824]/15 bg-white/55 text-[#5a3824] hover:border-[#ef4444]/35 hover:bg-white/85 hover:text-[#541717] theme-dark:border-white/10 theme-dark:bg-white/[0.025] theme-dark:text-zinc-300 theme-dark:hover:border-[#ef4444]/25 theme-dark:hover:bg-white/[0.06] theme-dark:hover:text-white",
    ].join(" ");
  }

  function getAdminTabDotClass(isActive) {
    return [
      "h-2 w-2 shrink-0 rounded-full transition",
      isActive
        ? "bg-white shadow-[0_0_16px_rgba(255,255,255,0.85)]"
        : "bg-[#8a5a35]/60 group-hover:bg-[#ef4444] theme-dark:bg-zinc-600",
    ].join(" ");
  }

  const effectivePerformerSlug =
  config?.performer?.slug ||
  (() => {
    try {
      return JSON.parse(
        window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
      )?.slug || "";
    } catch {
      return "";
    }
  })();

  useEffect(() => {
  if (!effectivePerformerSlug) {
    return undefined;
  }

  let isMounted = true;

  async function refreshPerformerSubscription() {
    try {
      const response = await fetchWithCredentials(
        `${getAdminApiBaseUrl()}/public/performers/${encodeURIComponent(
          effectivePerformerSlug
        )}?t=${Date.now()}`
      );

      const data = await response.json().catch(() => ({}));

      if (!isMounted || !response.ok || !data?.ok || !data?.performer) {
        return;
      }

      const nextPerformer = data.performer;

      setFreshPerformer(nextPerformer);

      try {
        const currentPerformer = JSON.parse(
          window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
        );

        window.sessionStorage.setItem(
          "paranoidi_auth_performer",
          JSON.stringify({
            ...currentPerformer,
            ...nextPerformer,
            features: nextPerformer.features || currentPerformer.features || {},
            plan: nextPerformer.plan || currentPerformer.plan,
            subscriptionStatus:
              nextPerformer.subscriptionStatus ||
              nextPerformer.subscription_status ||
              currentPerformer.subscriptionStatus,
            subscription_status:
              nextPerformer.subscription_status ||
              nextPerformer.subscriptionStatus ||
              currentPerformer.subscription_status,
          })
        );
      } catch {
        // SessionStoragen päivitys ei saa rikkoa hallintaa.
      }
    } catch (error) {
      console.error("Esiintyjän tilauksen päivitys epäonnistui:", error);
    }
  }

  refreshPerformerSubscription();

  return () => {
    isMounted = false;
  };
}, [effectivePerformerSlug]);

const setlistLanguageCounts = useMemo(() => {
  return librarySongs.reduce(
    (counts, song) => {
      const language = getSongLanguage(song);

      counts.all += 1;
      counts[language] = (counts[language] || 0) + 1;

      return counts;
    },
    { all: 0 }
  );
}, [librarySongs]);

const setlistUnknownLanguageCount = setlistLanguageCounts.unknown || 0;

const availableSetlistLanguageOptions = useMemo(() => {
  const languages = Array.from(
    new Set(
      librarySongs
        .map((song) => getSongLanguage(song))
        .filter(Boolean)
    )
  ).sort((a, b) => {
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;

    return getLocalizedSongLanguageLabel(a).localeCompare(
      getLocalizedSongLanguageLabel(b),
      adminLocale
    );
  });

  return [
    {
      value: "all",
      label: `${t("text058")} (${setlistLanguageCounts.all || 0})`,
    },
    ...languages.map((language) => {
      const label =
        language === "unknown"
          ? t("text270")
          : getLocalizedSongLanguageLabel(language);

      return {
        value: language,
        label: `${label} (${setlistLanguageCounts[language] || 0})`,
      };
    }),
  ];
}, [
  librarySongs,
  setlistLanguageCounts,
  t,
  i18n.resolvedLanguage,
  adminLocale,
]);

useEffect(() => {
  // Jos käyttäjän paketti ei kata osiota, ei näytetä lukittua tabia,
  // vaan palautetaan käyttäjä takaisin ensimmäiseen sallittuun näkymään.
  if (!canUseEvents && adminTab === "events") {
    setAdminTab("requests");
    return;
  }

  if (
    !canUseSetlists &&
    (adminTab === "setlists" || adminTab === "setlistsLocked")
  ) {
    setAdminTab("requests");
    return;
  }

  if (
    !canUseGigNotes &&
    (adminTab === "gigNotes" || adminTab === "gigNotesLocked")
  ) {
    setAdminTab("requests");
    return;
  }

  if (!canUseLiveSummary && adminTab === "summary") {
    setAdminTab("requests");
    return;
  }

}, [canUseEvents, canUseSetlists, canUseGigNotes, canUseLiveSummary, adminTab]);

  useEffect(() => {
  // Hallintasivu alkaa aina ylhäältä, ettei fixed-navbar jää sisällön päälle
  // selaimen palauttaman vanhan scroll-positionin takia.
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "auto",
  });
}, []);

useEffect(() => {
  if (!canUseGigNotes || !effectivePerformerSlug) {
    setGigNotes([]);
    return;
  }

  let isMounted = true;

  async function fetchGigNotes() {
    try {
      const response = await fetchWithCredentials(getGigNotesApiUrl(effectivePerformerSlug));
      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Keikkamuistin haku epäonnistui.");
      }

      if (isMounted) {
        setGigNotes(Array.isArray(data.items) ? data.items : []);
      }
    } catch (error) {
      console.error("Keikkamuistin lataus settilistaan epäonnistui:", error);

      if (isMounted) {
        setGigNotes([]);
      }
    }
  }

  fetchGigNotes();

  return () => {
    isMounted = false;
  };
}, [canUseGigNotes, effectivePerformerSlug, adminTab]);

useEffect(() => {
  if (!canUseLiveSummary || !effectivePerformerSlug) {
    setSavedGigReports([]);
    return;
  }

  refreshGigReports();
}, [canUseLiveSummary, effectivePerformerSlug]);

useEffect(() => {
  if (adminTab !== "setlists") {
    return;
  }

  refreshLibrarySongs();
}, [adminTab, effectivePerformerSlug]);

useEffect(() => {
  if (!canUseSetlists || !effectivePerformerSlug) {
    return;
  }

  let isMounted = true;

  async function fetchSavedSetlists() {
    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    try {
      const response = await fetchWithCredentials(
        `${apiBaseUrl}/admin/performers/${encodeURIComponent(
          effectivePerformerSlug
        )}/setlists`
      );

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Tallennettujen settilistojen haku epäonnistui.");
      }

      if (isMounted) {
        setSavedSetlists(Array.isArray(data.items) ? data.items : []);
      }
    } catch (error) {
      console.error("Tallennettujen settilistojen lataus epäonnistui:", error);
    }
  }

  fetchSavedSetlists();

  return () => {
    isMounted = false;
  };
}, [canUseSetlists, effectivePerformerSlug]);

useLayoutEffect(() => {
  // Estetään selainta palauttamasta hallintasivua vanhaan scroll-kohtaan.
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  window.scrollTo(0, 0);

  const frame = window.requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });

  const timer = window.setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);

  return () => {
    window.cancelAnimationFrame(frame);
    window.clearTimeout(timer);
  };
}, []);

  function handleLogout() {
    window.sessionStorage.clear();
    window.location.href = "/";
  }

  async function updateParanoidiSettings(updates) {
    const previousSettings = paranoidiSettings || {};

    const optimisticSettings = {
      ...previousSettings,
      ...updates,
    };

    setParanoidiSettings(optimisticSettings);

    try {
      const nextSettings = await saveParanoidiSettings(updates);

      setParanoidiSettings(nextSettings);

      return nextSettings;
    } catch (error) {
      // Jos backend-tallennus epäonnistuu, palautetaan myös
      // käyttöliittymän asetukset siihen tilaan missä ne olivat ennen yritystä.
      setParanoidiSettings(previousSettings);

      throw error;
    }
  }

  function drawRaffleWinner() {
    if (!isRequestRaffleEnabled) {
      setRaffleError(
        t("raffleMessages.disabled", {
          defaultValue:
            "Toivearvonta ei ole käytössä. Ota se tarvittaessa käyttöön Asetuksista.",
        })
      );
      return;
    }

    if (raffleWinner) {
      setRaffleError(
        t("raffleMessages.alreadyDrawn", {
          defaultValue:
            "Arvonta on jo suoritettu tälle toivelistalle. Siivoa soitetut toiveet aloittaaksesi uuden setin.",
        })
      );
      return;
    }

    const participantsByName = requests.reduce((participants, request) => {
      if (!isRaffleOptedIn(request)) {
        return participants;
      }

      const requesterName = String(request.requesterName || "").trim();

      if (!requesterName) {
        return participants;
      }

      const key = normalize(requesterName);

      if (!participants.has(key)) {
        participants.set(key, {
          name: requesterName,
          requestCount: 0,
          songs: [],
        });
      }

      const participant = participants.get(key);

      participant.requestCount += 1;
      participant.songs.push({
        title: request.songTitle,
        artist: request.artist,
      });

      return participants;
    }, new Map());

    const participants = Array.from(participantsByName.values());

    if (!participants.length) {
      setRaffleError(
        t("raffleMessages.noParticipants", {
          defaultValue:
            "Palkinnon valintaan tarvitaan vähintään yksi biisitoive, jossa osallistuja on valinnut olevansa mukana arvonnassa.",
        })
      );
      return;
    }

    const winner = participants[Math.floor(Math.random() * participants.length)];

    setRaffleError("");
    setRaffleWinner({
      ...winner,
      drawnAt: new Date().toISOString(),
    });
  }

  async function refreshLibrarySongs() {
  try {
    const data = await getSongs(effectivePerformerSlug);
    setLibrarySongs(data.items || []);
  } catch (error) {
    console.error("Biisikirjaston haku hallintaan epäonnistui:", error);
    setLibrarySongs([]);
  }
}

async function refreshGigReports() {
  const slug = String(effectivePerformerSlug || "").trim();

  if (!slug || !canUseLiveSummary) {
    setSavedGigReports([]);
    return;
  }

  try {
    const response = await fetchWithCredentials(getGigReportsApiUrl(slug));
    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(data.error || "Keikkaraporttien haku epäonnistui.");
    }

    setSavedGigReports(Array.isArray(data.items) ? data.items : []);
  } catch (error) {
    console.error("Keikkaraporttien haku epäonnistui:", error);
    setSavedGigReports([]);
  }
}

  async function refreshRequests() {
    try {
      const data = await getSongRequests(effectivePerformerSlug);

      const normalizedItems = (data.items || [])
        .map((item) => ({
          ...item,
          songTitle: item.songTitle || item.song_title,
          artist: item.artist || item.songArtist || item.song_artist || "",
          requesterName: item.requesterName || item.requester_name,
          requesterLocation: item.requesterLocation || item.requester_location,
          createdAt: item.createdAt || item.created_at,
          updatedAt: item.updatedAt || item.updated_at,
          requestType: item.requestType || item.request_type || "library",
          request_type: item.request_type || item.requestType || "library",
          requestCount: Number(item.requestCount || item.request_count || 1),
          request_count: Number(item.request_count || item.requestCount || 1),
          eventId: item.eventId || item.event_id || null,
          event_id: item.event_id || item.eventId || null,
          eventName: item.eventName || item.event_name || "",
          event_name: item.event_name || item.eventName || "",
          raffleOptIn: isRaffleOptedIn(item),
          raffle_opt_in: isRaffleOptedIn(item),
          isBoosted: Boolean(item.isBoosted ?? item.is_boosted),
          is_boosted: Boolean(item.isBoosted ?? item.is_boosted),
          boostPaidAt: item.boostPaidAt || item.boost_paid_at || null,
          boost_paid_at: item.boost_paid_at || item.boostPaidAt || null,
        }));

      const statusOrder = {
        new: 0,
        seen: 1,
        played: 2,
      };

      const sortedItems = normalizedItems.sort((a, b) => {
        const statusA = statusOrder[a.status] ?? 99;
        const statusB = statusOrder[b.status] ?? 99;

        if (statusA !== statusB) {
          return statusA - statusB;
        }

        // Maksetut näkyvyysnostot näytetään tavallisten toiveiden edellä.
        const boostedA = Boolean(a.isBoosted ?? a.is_boosted);
        const boostedB = Boolean(b.isBoosted ?? b.is_boosted);

        if (boostedA !== boostedB) {
          return boostedA ? -1 : 1;
        }

        // Useista nostetuista toiveista ensimmäisenä maksanut säilyttää paikkansa.
        // Uudempi maksettu nosto tulee olemassa olevan noston jälkeen.
        if (boostedA && boostedB) {
          const boostDateA = Date.parse(
            a.boostPaidAt || a.boost_paid_at || ""
          );

          const boostDateB = Date.parse(
            b.boostPaidAt || b.boost_paid_at || ""
          );

          if (
            Number.isFinite(boostDateA) &&
            Number.isFinite(boostDateB) &&
            boostDateA !== boostDateB
          ) {
            return boostDateA - boostDateB;
          }
        }

        const dateA = new Date(
          String(a.createdAt || "").includes("T")
            ? String(a.createdAt || "")
            : String(a.createdAt || "").replace(" ", "T") + "Z"
        ).getTime();

        const dateB = new Date(
          String(b.createdAt || "").includes("T")
            ? String(b.createdAt || "")
            : String(b.createdAt || "").replace(" ", "T") + "Z"
        ).getTime();

        return dateA - dateB;
      });

      setRequests(sortedItems);
    } catch (error) {
      console.error("Biisitoiveiden haku epäonnistui:", error);
      setRequests([]);
    }
  }

  useEffect(() => {
    refreshRequests();
    refreshLibrarySongs();

    function handleUpdate() {
      refreshRequests();
      refreshLibrarySongs();
    }

    window.addEventListener("storage", handleUpdate);
    window.addEventListener("paranoidi:song-requests-updated", handleUpdate);

    const interval = window.setInterval(refreshRequests, 2500);

    return () => {
      window.removeEventListener("storage", handleUpdate);
      window.removeEventListener("paranoidi:song-requests-updated", handleUpdate);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

fetchParanoidiSettings()
  .then((settings) => {
    if (isMounted) {
      setParanoidiSettings(settings);
      setAreParanoidiSettingsLoaded(true);
    }
  })
  .catch((error) => {
    console.error(
      "Paranoidi-asetusten ensimmäinen haku epäonnistui:",
      error
    );

    if (isMounted) {
      setAreParanoidiSettingsLoaded(true);
    }
  });

    const unsubscribe = subscribeParanoidiSettings(setParanoidiSettings);

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

async function generateSetlist() {
  const occasion = String(setlistOccasion || "").trim();

  if (!occasion) {
    setSetlistMessage(
      t("setlistEdit.selectOccasionFirst", {
        defaultValue: "Valitse ensin tilaisuus.",
      })
    );
    return;
  }

  const selectedLanguage = String(setlistLanguageFilter || "all").trim();
  const excludedArtists = parseCommaSeparatedFilters(setlistExcludedArtists);
  const excludedGenres = parseCommaSeparatedFilters(setlistExcludedGenres);
  const setCount = Math.max(1, Number(setlistSetCount) || 1);
  const songsPerSet = Math.max(1, Number(setlistSongsPerSet) || 1);

  const matchingSongs = librarySongs.filter((song) => {
    if (!isSongRequestable(song)) {
      return false;
    }

    const tags = parseTagList(getSongOccasionTags(song)).map(normalize);

    if (!tags.includes(normalize(occasion))) {
      return false;
    }

    if (selectedLanguage !== "all" && getSongLanguage(song) !== selectedLanguage) {
      return false;
    }

    if (
      excludedArtists.length &&
      textMatchesAnyFilter(song.artist || "", excludedArtists)
    ) {
      return false;
    }

    if (
      excludedGenres.length &&
      textMatchesAnyFilter(getSongStyleSearchText(song), excludedGenres)
    ) {
      return false;
    }

    return true;
  });

  const neededSongs = setCount * songsPerSet;

  const activeFilters = [
    selectedLanguage !== "all"
      ? t("setlistGenerator.filterLanguage", {
          language: getLocalizedSongLanguageLabel(selectedLanguage),
          defaultValue: "kieli: {{language}}",
        })
      : "",
    excludedArtists.length
      ? t("setlistGenerator.filterExcludedArtists", {
          artists: setlistExcludedArtists,
          defaultValue: "vältettävät artistit: {{artists}}",
        })
      : "",
    excludedGenres.length
      ? t("setlistGenerator.filterExcludedGenres", {
          genres: setlistExcludedGenres,
          defaultValue: "vältettävät genret/tyylit: {{genres}}",
        })
      : "",
  ].filter(Boolean);

  const filterText = activeFilters.length
    ? t("setlistGenerator.filtersSummary", {
        filters: activeFilters.join(", "),
        defaultValue: " Rajaukset: {{filters}}.",
      })
    : "";

  if (matchingSongs.length < neededSongs) {
    setSetlistMessage(
      t("setlistGenerator.notEnoughSongs", {
        found: matchingSongs.length,
        needed: neededSongs,
        filters: filterText,
        defaultValue:
          "Rajauksilla löytyi vain {{found}} biisiä. Tarvitaan {{needed}} biisiä.{{filters}} Poista jokin rajaus, lisää tageja biiseihin tai pienennä määrää.",
      })
    );
    return;
  }

  const shuffledSongs = shuffleItems(matchingSongs).slice(0, neededSongs);
  const sets = [];

  for (let setIndex = 0; setIndex < setCount; setIndex += 1) {
    const start = setIndex * songsPerSet;
    const end = start + songsPerSet;

    sets.push({
      title: t("setlistDynamic.setTitle", {
        number: setIndex + 1,
        defaultValue: `Setti ${setIndex + 1}`,
      }),
      songs: shuffledSongs.slice(start, end),
    });
  }

  try {
    // Uuden settilistan luominen irrottaa aiemmin ladatun
    // tallennetun settilistan nykyisestä keikkasessiosta.
    await updateParanoidiSettings({
      currentGigSetlistId: null,
    });

    restoredCurrentGigSetlistIdRef.current = null;
    setActiveSavedSetlistId(null);

    setGeneratedSetlist(sets);

    setSetlistMessage(
      t("setlistGenerator.created", {
        count: sets.length,
        occasion: translateAdminLabel(occasion),
        filters: filterText,
        defaultValue:
          "Luotiin {{count}} settiä tilaisuuteen: {{occasion}}.{{filters}}",
      })
    );
  } catch (error) {
    console.error(
      "Uuden settilistan aloittaminen epäonnistui:",
      error
    );

    setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistGenerator.startFailed",
        "Uutta settilistaa ei voitu aloittaa."
      )
    );
  }
}

async function createEmptySetlist() {
  const setCount = Math.max(1, Number(setlistSetCount) || 1);

  const sets = Array.from({ length: setCount }, (_item, index) => ({
    title: t("setlistDynamic.setTitle", {
      number: index + 1,
      defaultValue: `Setti ${index + 1}`,
    }),
    songs: [],
  }));

  try {
    // Tyhjän uuden settilistan aloittaminen irrottaa
    // aiemmin aktiivisena olleen tallennetun setin keikkasessiosta.
    await updateParanoidiSettings({
      currentGigSetlistId: null,
    });

    restoredCurrentGigSetlistIdRef.current = null;
    setActiveSavedSetlistId(null);

    setGeneratedSetlist(sets);
    setSetlistSongSearchBySet({});
    setActiveSetlistAddSongSetIndex(0);
    setActiveSetlistSong(null);
    setIsPrompterRunning(false);

    setSetlistMessage(
      t("setlistGenerator.emptyCreated", {
        defaultValue:
          "Tyhjä settilista luotu. Hae biisi setin alta omasta biisikirjastosta ja lisää se tähän settiin.",
      })
    );
  } catch (error) {
    console.error(
      "Tyhjän settilistan aloittaminen epäonnistui:",
      error
    );

    setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistGenerator.emptyStartFailed",
        "Tyhjää settilistaa ei voitu aloittaa."
      )
    );
  }
}

function drawAdminRandomSong() {
  const occasion = String(randomOccasion || "kaikki").trim();

  const candidates = librarySongs.filter((song) => {
    if (!isSongRequestable(song)) {
      return false;
    }

    if (occasion === "kaikki") {
      return true;
    }

    const tags = parseTagList(getSongOccasionTags(song)).map(normalize);

    return tags.includes(normalize(occasion));
  });

  if (!candidates.length) {
    setRandomSong(null);
    setRandomMessage(
      occasion === "kaikki"
        ? t("randomSong.noSongs", {
            defaultValue: "Toivottavia biisejä ei löytynyt.",
          })
        : t("randomSong.noSongsForOccasion", {
            occasion: translateAdminLabel(occasion),
            defaultValue:
              'Tilaisuustägillä "{{occasion}}" ei löytynyt toivottavia biisejä.',
          })
    );
    return;
  }

  const nextSong = candidates[Math.floor(Math.random() * candidates.length)];

  setRandomSong(nextSong);
  setRandomMessage(
    occasion === "kaikki"
      ? t("randomSong.candidateCount", {
          count: candidates.length,
          defaultValue:
            "Mukana arvonnassa {{count}} toivottavaa biisiä.",
        })
      : t("randomSong.candidateCountForOccasion", {
          count: candidates.length,
          occasion: translateAdminLabel(occasion),
          defaultValue:
            "Mukana arvonnassa {{count}} biisiä tilaisuustägillä: {{occasion}}.",
        })
  );
}

function moveSetlistSong(setIndex, songIndex, direction) {
  setGeneratedSetlist((currentSets) => {
    const nextSets = currentSets.map((set) => ({
      ...set,
      songs: [...set.songs],
    }));

    const set = nextSets[setIndex];

    if (!set) {
      return currentSets;
    }

    const targetIndex = songIndex + direction;

    if (targetIndex < 0 || targetIndex >= set.songs.length) {
      return currentSets;
    }

    const movingSong = set.songs[songIndex];

    set.songs.splice(songIndex, 1);
    set.songs.splice(targetIndex, 0, movingSong);

    return nextSets;
  });
}

function updateSetlistSongNote(setIndex, songIndex, nextNote) {
  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      return {
        ...set,
        songs: set.songs.map(
          (song, currentSongIndex) => {
            if (currentSongIndex !== songIndex) {
              return song;
            }

            // Tämä kenttä on vain tämän biisin oma käsin kirjoitettu
            // muistiinpano. Linkitettyjä Keikkamuisti-tietueita
            // muokataan erikseen niiden omalla ID:llä.
            return {
              ...song,
              setlistManualNote: nextNote,
              setlist_manual_note: nextNote,

              // Säilytetään nämä vielä yhteensopivuuden vuoksi,
              // kunnes kaikki näyttöpaikat käyttävät
              // buildSetlistSongNote()-funktiota.
              setlistNote: nextNote,
              setlist_note: nextNote,
            };
          }
        ),
      };
    })
  );
}

async function saveLinkedGigNote(gigNoteId, saveNoticeKey = "") {
  const normalizedGigNoteId = String(gigNoteId || "").trim();
  const noticeKey =
    String(saveNoticeKey || "").trim() || normalizedGigNoteId;

  const currentGigNote = gigNotes.find(
    (item) => String(item.id) === normalizedGigNoteId
  );

  if (!currentGigNote) {
    setGigNoteSaveNotice({
      key: noticeKey,
      type: "error",
      text: t("gigNoteLink.notFound", {
        defaultValue:
          "Tallennettavaa Keikkamuistia ei löytynyt.",
      }),
    });
    return;
  }

  // Luonnosta käytetään vain Keikkatyökalun editorissa.
  // Jos sisältöä ei ole vielä muutettu, käytetään tietueen nykyistä sisältöä.
  const nextContent = String(
    Object.prototype.hasOwnProperty.call(
      gigNoteDrafts,
      normalizedGigNoteId
    )
      ? gigNoteDrafts[normalizedGigNoteId]
      : currentGigNote.content || ""
  ).trim();

  if (!nextContent) {
    setGigNoteSaveNotice({
      key: noticeKey,
      type: "error",
      text: t("gigNoteLink.emptyContent", {
        defaultValue:
          "Keikkamuistin sisältö ei voi olla tyhjä.",
      }),
    });
    return;
  }

  setGigNoteSaveNotice({
    key: noticeKey,
    type: "",
    text: "",
  });

  try {
    const response = await fetchWithCredentials(
      getGigNotesApiUrl(
        effectivePerformerSlug,
        normalizedGigNoteId
      ),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          type: currentGigNote.type || "note",
          title: currentGigNote.title || "",
          occasion: currentGigNote.occasion || "yleinen",
          content: nextContent,
          isPinned: Boolean(currentGigNote.isPinned),
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(
        data.error ||
          t("gigNoteLink.saveFailed", {
            defaultValue:
              "Keikkamuistin tallennus epäonnistui.",
          })
      );
    }

    // Päivitetään sama alkuperäinen Keikkamuisti paikalliseen stateen.
    // Kaikki samaan ID:hen linkitetyt biisit käyttävät tämän jälkeen
    // automaattisesti tätä samaa päivitettyä tietuetta.
    setGigNotes((currentItems) =>
      currentItems.map((item) =>
        String(item.id) === normalizedGigNoteId
          ? data.item
          : item
      )
    );

    // Luonnosta ei enää tarvita onnistuneen tallennuksen jälkeen.
    setGigNoteDrafts((currentDrafts) => {
      const nextDrafts = {
        ...currentDrafts,
      };

      delete nextDrafts[normalizedGigNoteId];

      return nextDrafts;
    });

    setGigNoteSaveNotice({
      key: noticeKey,
      type: "success",
      text: t("gigNoteLink.saved", {
        defaultValue:
          "✓ Keikkamuisti tallennettu.",
      }),
    });
  } catch (error) {
    console.error(
      "Keikkamuistin tallennus Keikkatyökalusta epäonnistui:",
      error
    );

    setGigNoteSaveNotice({
      key: noticeKey,
      type: "error",
      text: getLocalizedAdminError(
        error,
        "gigNoteLink.saveFailed",
        "Keikkamuistin tallennus epäonnistui."
      ),
    });
  }
}

function applyGigNoteToSetlistSong(setIndex, songIndex, gigNoteId) {
  const selectedGigNote = gigNotes.find(
    (item) => String(item.id) === String(gigNoteId)
  );

  if (!selectedGigNote) {
    return;
  }

  const normalizedGigNoteId = String(selectedGigNote.id);

  const currentSong =
    generatedSetlist?.[setIndex]?.songs?.[songIndex];

  if (!currentSong) {
    return;
  }

  // Sama Keikkamuisti saa olla samalla biisillä vain kerran.
  // Linkitys perustuu Keikkamuistin pysyvään ID:hen.
  if (
    getSetlistSongGigNoteIds(currentSong).includes(
      normalizedGigNoteId
    )
  ) {
    setSetlistMessage(
      t("gigNoteLink.alreadyLinked", {
        defaultValue:
          "Tämä Keikkamuisti on jo liitetty tähän biisiin.",
      })
    );
    return;
  }

  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      return {
        ...set,
        songs: set.songs.map((song, currentSongIndex) => {
          if (currentSongIndex !== songIndex) {
            return song;
          }

          const currentGigNoteIds =
            getSetlistSongGigNoteIds(song);

          // Säilytetään mahdollinen tämän biisin oma käsin
          // kirjoitettu muistiinpano erillään Keikkamuistista.
          //
          // Samalla siivotaan vanhan snapshot-toteutuksen mahdollinen
          // kopio juuri tästä Keikkamuistista.
          const manualNote =
            removeLegacyGigNoteCopies(
              getSetlistSongManualNote(song),
              selectedGigNote
            );

          const nextGigNoteIds = Array.from(
            new Set([
              ...currentGigNoteIds,
              normalizedGigNoteId,
            ])
          );

          return {
            ...song,

            // Biisin oma teksti.
            setlistManualNote: manualNote,
            setlist_manual_note: manualNote,

            // Vanhojen näyttöpaikkojen yhteensopivuus.
            // Keikkamuistin sisältöä EI kopioida tähän.
            setlistNote: manualNote,
            setlist_note: manualNote,

            // Varsinainen Keikkamuisti-linkitys.
            setlistGigNoteIds: nextGigNoteIds,
            setlist_gig_note_ids: nextGigNoteIds,
          };
        }),
      };
    })
  );

  setSetlistMessage(
    t("gigNoteLink.linked", {
      defaultValue:
        "Keikkamuisti liitetty biisiin.",
    })
  );
}

function unlinkGigNoteFromSetlistSong(
  setIndex,
  songIndex,
  gigNoteId
) {
  const normalizedGigNoteId = String(
    gigNoteId || ""
  ).trim();

  if (!normalizedGigNoteId) {
    return;
  }

  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      return {
        ...set,
        songs: set.songs.map(
          (song, currentSongIndex) => {
            if (currentSongIndex !== songIndex) {
              return song;
            }

            // Irrotetaan vain tämän biisin linkki.
            // Varsinaista Keikkamuisti-tietuetta ei poisteta
            // eikä sen sisältöä muuteta.
            const nextGigNoteIds =
              getSetlistSongGigNoteIds(song).filter(
                (currentGigNoteId) =>
                  String(currentGigNoteId) !==
                  normalizedGigNoteId
              );

            // Biisin oma käsin kirjoitettu teksti säilyy.
            const manualNote =
              getSetlistSongManualNote(song);

            return {
              ...song,
              setlistManualNote: manualNote,
              setlist_manual_note: manualNote,

              // Yhteensopivuuskenttiin jää vain biisin oma teksti.
              // Linkitetyn Keikkamuistin sisältöä ei kopioida tähän.
              setlistNote: manualNote,
              setlist_note: manualNote,

              setlistGigNoteIds: nextGigNoteIds,
              setlist_gig_note_ids: nextGigNoteIds,
            };
          }
        ),
      };
    })
  );

  setSetlistMessage(
    t("gigNoteLink.unlinked", {
      defaultValue:
        "Keikkamuisti irrotettu biisistä.",
    })
  );
}

function clearSetlistSongNote(setIndex, songIndex) {
  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      return {
        ...set,
        songs: set.songs.map(
          (song, currentSongIndex) => {
            if (currentSongIndex !== songIndex) {
              return song;
            }

            // Poistetaan vain tämän biisin oma käsin kirjoitettu
            // muistiinpano. Linkitettyjä Keikkamuisti-ID:itä
            // ei poisteta tästä toiminnosta.
            return {
              ...song,
              setlistManualNote: "",
              setlist_manual_note: "",
              setlistNote: "",
              setlist_note: "",
            };
          }
        ),
      };
    })
  );

  setSetlistMessage(
    t("gigNoteLink.manualNoteRemoved", {
      defaultValue:
        "Biisikohtainen muistiinpano poistettu.",
    })
  );
}

async function copyGeneratedSetlist() {
  if (!generatedSetlist.length) {
    setSetlistMessage(
      t("setlistExport.createFirst", {
        defaultValue: "Luo ensin settilista.",
      })
    );
    return;
  }

  const text = generatedSetlist
    .map((set, setIndex) => {
      const songs = set.songs
        .map((song, index) =>
        formatSetlistSongForText(
          song,
          index,
          gigNotes,
          {
            performancePrefix: t("setlistPerformance.prefix", {
              defaultValue: "Keikkamerkintä",
            }),
            getPerformanceLabel:
              getLocalizedSetlistSongPerformanceLabel,
            getGigNoteTypeLabelFn:
              getLocalizedGigNoteTypeLabel,
          }
        )
      )
        .join("\n");

      return `${getLocalizedSetTitle(set?.title, setIndex)}\n${songs}`;
    })
    .join("\n\n");

  try {
    await navigator.clipboard.writeText(text);

    setSetlistMessage(
      t("setlistExport.copied", {
        defaultValue: "Settilista kopioitu leikepöydälle.",
      })
    );
  } catch (error) {
    console.error("Settilistan kopiointi epäonnistui:", error);
    setSetlistMessage(
      t("setlistExport.copyFailed", {
        defaultValue:
          "Kopiointi epäonnistui. Voit maalata settilistan ja kopioida sen käsin.",
      })
    );
  }
}

function printGeneratedSetlist() {
  if (!generatedSetlist.length) {
    setSetlistMessage(
      t("setlistExport.createFirst", {
        defaultValue: "Luo ensin settilista.",
      })
    );
    return;
  }

  const localizedOccasion =
    translateAdminLabel(setlistOccasion);

  const documentLanguage =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en"
      : "fi";

  const exportTitle = t("setlistExport.title", {
    defaultValue: "Settilista",
  });

  const setCountLabel = t("setlistExport.setCount", {
    count: generatedSetlist.length,
    defaultValue:
      generatedSetlist.length === 1
        ? "{{count}} setti"
        : "{{count}} settiä",
  });

  const printWindow = window.open(
    "",
    "_blank",
    "width=900,height=700"
  );

  if (!printWindow) {
    setSetlistMessage(
      t("setlistExport.popupFailed", {
        defaultValue:
          "Tulostusikkunan avaaminen epäonnistui. Salli ponnahdusikkunat selaimessa.",
      })
    );
    return;
  }

  const setlistHtml = generatedSetlist
    .map((set, setIndex) => {
    const songsHtml = set.songs
      .map((song, index) => {
        const artist = song.artist
          ? ` <span>– ${escapeHtml(song.artist)}</span>`
          : "";

        // Tuloste käyttää aina biisin omaa tekstiä sekä linkitettyjen
        // Keikkamuistien tämänhetkistä sisältöä.
        const note = buildSetlistSongNote(
          song,
          gigNotes,
          getLocalizedGigNoteTypeLabel
        );
        const noteHtml = note
          ? `<div class="note">${escapeHtml(note).replace(/\n/g, "<br />")}</div>`
          : "";

        return `
          <li>
            <div class="song-line">
              <strong>${index + 1}. ${escapeHtml(song.title)}</strong>${artist}
            </div>
            ${noteHtml}
          </li>
        `;
      })
      .join("");

      return `
        <section class="set">
          <h2>${escapeHtml(getLocalizedSetTitle(set?.title, setIndex))}</h2>
          <ol>${songsHtml}</ol>
        </section>
      `;
    })
    .join("");

  printWindow.document.write(`
    <!doctype html>
    <html lang="${documentLanguage}">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(exportTitle)} - ${escapeHtml(localizedOccasion)}</title>
        <style>
          body {
            margin: 40px;
            font-family: Arial, sans-serif;
            color: #1f1f1f;
            background: #ffffff;
          }

          .header {
            border-bottom: 2px solid #1f1f1f;
            margin-bottom: 28px;
            padding-bottom: 16px;
          }

          h1 {
            margin: 0;
            font-size: 28px;
          }

          .meta {
            margin-top: 8px;
            font-size: 13px;
            color: #555;
          }

          .set {
            break-inside: avoid;
            margin-top: 26px;
          }

          h2 {
            margin: 0 0 12px;
            font-size: 20px;
          }

          ol {
            margin: 0;
            padding-left: 24px;
          }

          li {
            margin: 0 0 9px;
            font-size: 15px;
            line-height: 1.45;
          }

          li span {
            color: #555;
          }

          .song-line {
            margin-bottom: 4px;
          }

          .note {
            margin: 6px 0 0;
            padding: 8px 10px;
            border-left: 3px solid #999;
            background: #f4f4f4;
            color: #333;
            font-size: 13px;
            line-height: 1.45;
            white-space: pre-wrap;
          }

          @media print {
            body {
              margin: 22mm;
            }
          }
        </style>
      </head>

      <body>
        <div class="header">
          <h1>${escapeHtml(exportTitle)}</h1>
          <div class="meta">
            ${escapeHtml(
              t("setlistExport.occasion", {
                defaultValue: "Tilaisuus",
              })
            )}: ${escapeHtml(localizedOccasion)} · ${escapeHtml(setCountLabel)} · ${new Date().toLocaleString(adminLocale)}
          </div>
        </div>

        ${setlistHtml}
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();

  window.setTimeout(() => {
    printWindow.print();
  }, 250);

    setSetlistMessage(
    t("setlistExport.printOpened", {
      defaultValue:
        "Tulostus avattu. Valitse tarvittaessa Tallenna PDF:nä.",
    })
  );
}

function emailGeneratedSetlist() {
  if (!generatedSetlist.length) {
    setSetlistMessage(
      t("setlistExport.createFirst", {
        defaultValue: "Luo ensin settilista.",
      })
    );
    return;
  }

  const localizedOccasion =
    translateAdminLabel(setlistOccasion);

  const exportTitle = t("setlistExport.title", {
    defaultValue: "Settilista",
  });

  const text = generatedSetlist
    .map((set, setIndex) => {
      const songs = set.songs
        .map((song, index) => {
          const artist = song.artist ? ` – ${song.artist}` : "";
          return `${index + 1}. ${song.title}${artist}`;
        })
        .join("\n");

      return `${getLocalizedSetTitle(set?.title, setIndex)}\n${songs}`;
    })
    .join("\n\n");

  const subject = t("setlistExport.emailSubject", {
    occasion: localizedOccasion,
    defaultValue: "Settilista - {{occasion}}",
  });

  const body = [
    exportTitle,
    "",
    t("setlistExport.emailOccasion", {
      occasion: localizedOccasion,
      defaultValue: "Tilaisuus: {{occasion}}",
    }),
    t("setlistExport.emailSetCount", {
      count: generatedSetlist.length,
      defaultValue: "Settejä: {{count}}",
    }),
    t("setlistExport.emailCreated", {
      date: new Date().toLocaleString(adminLocale),
      defaultValue: "Luotu: {{date}}",
    }),
    "",
    text,
  ].join("\n");

  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setSetlistMessage(
    t("setlistExport.emailOpened", {
      defaultValue: "Sähköpostiluonnos avattu.",
    })
  );
}

async function saveGeneratedSetlist() {
  if (!generatedSetlist.length) {
    setSetlistMessage(
      t("setlistExport.createFirst", {
        defaultValue: "Luo ensin settilista.",
      })
    );
    return;
  }

  const slug = String(effectivePerformerSlug || "").trim();

  if (!slug) {
    setSetlistMessage(
      t("setlistSave.performerMissing", {
        defaultValue:
          "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään.",
      })
    );
    return;
  }

  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  const isUpdatingSavedSetlist = Boolean(activeSavedSetlistId);

  const url = isUpdatingSavedSetlist
    ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/setlists/${encodeURIComponent(activeSavedSetlistId)}`
    : `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/setlists`;

  try {
    const response = await fetchWithCredentials(url, {
      method: isUpdatingSavedSetlist ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        title: getEditableSetlistTitle(setlistTitle),
        occasion: setlistOccasion,
        setCount: getSetlistSetCount(generatedSetlist),
        songsPerSet: getSetlistMaxSongsPerSet(generatedSetlist),
        sets: generatedSetlist,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(
        data.error ||
          t("setlistSave.failed", {
            defaultValue: "Settilistan tallennus epäonnistui.",
          })
      );
    }

    const savedSetlistId = data.item?.id;

    if (!savedSetlistId) {
      throw new Error(
        t("setlistSave.idMissing", {
          defaultValue:
            "Tallennetun settilistan tunniste puuttuu palvelimen vastauksesta.",
        })
      );
    }

    // Tallennettu tai päivitetty settilista on samalla nykyisen
    // keikkasession aktiivinen settilista.
    await updateParanoidiSettings({
      currentGigSetlistId: savedSetlistId,
    });

      restoredCurrentGigSetlistIdRef.current =
        String(savedSetlistId);

      // Tämä nykyinen versio on nyt varmasti palvelimella.
      // Tallentamattomien muutosten varoitus poistuu.
      setSavedSetlistEditSnapshot(
        currentSetlistEditSnapshot
      );

      if (isUpdatingSavedSetlist) {
      setActiveSavedSetlistId(savedSetlistId);

      setSavedSetlists((current) =>
        current.map((savedSetlist) =>
          String(savedSetlist.id) === String(activeSavedSetlistId)
            ? data.item
            : savedSetlist
        )
      );

      setSetlistMessage(SETLIST_SAVE_SUCCESS_UPDATED);

      return;
    }

    setActiveSavedSetlistId(savedSetlistId);

    setSavedSetlists((current) =>
      [data.item, ...current].slice(0, 50)
    );

    setSetlistMessage(SETLIST_SAVE_SUCCESS_CREATED);

  } catch (error) {
    console.error("Settilistan tallennus epäonnistui:", error);
    setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistSave.failed",
        "Settilistan tallennus epäonnistui."
      )
    );
  }
}

function hydrateSavedSetlistSets(sets) {
  if (!Array.isArray(sets)) {
    return [];
  }

  return sets.map((set, setIndex) => ({
    ...set,
    title: getLocalizedSetTitle(set?.title, setIndex),
    songs: Array.isArray(set?.songs)
      ? set.songs.map((setSong) => {
          const freshLibrarySong = findMatchingLibrarySong(
            librarySongs,
            setSong
          );

          if (!freshLibrarySong) {
            return setSong;
          }

          const preservedManualNote =
            getSetlistSongManualNote(setSong);

          const preservedGigNoteIds =
            getSetlistSongGigNoteIds(setSong);

          return {
            ...setSong,
            ...freshLibrarySong,

            // Biisikirjastosta päivitetään tuore biisidata,
            // mutta setin oma käsin kirjoitettu muistiinpano
            // ja Keikkamuisti-ID-linkit säilyvät.
            setlistManualNote: preservedManualNote,
            setlist_manual_note: preservedManualNote,

            // Legacy-yhteensopivuuskentissä pidetään vain oma teksti.
            setlistNote: preservedManualNote,
            setlist_note: preservedManualNote,

            setlistGigNoteIds: preservedGigNoteIds,
            setlist_gig_note_ids: preservedGigNoteIds,
          };
        })
      : [],
  }));
}

async function loadSavedSetlist(savedSetlist) {
  try {
    // Tallennetaan palvelimelle, mikä settilista kuuluu
    // nykyiseen keikkasessioon.
    await updateParanoidiSettings({
      currentGigSetlistId: savedSetlist.id,
    });

    restoredCurrentGigSetlistIdRef.current =
      String(savedSetlist.id);

    setActiveSavedSetlistId(savedSetlist.id);
    setSetlistTitle(getEditableSetlistTitle(savedSetlist.title));
    setSetlistOccasion(savedSetlist.occasion || "Häät");
    setSetlistSetCount(
      savedSetlist.setCount ||
      savedSetlist.set_count ||
      1
    );
    setSetlistSongsPerSet(
      savedSetlist.songsPerSet ||
      savedSetlist.songs_per_set ||
      12
    );

    const hydratedSets =
      hydrateSavedSetlistSets(savedSetlist.sets || []);

    setGeneratedSetlist(hydratedSets);

    setSavedSetlistEditSnapshot(
      buildSetlistEditSnapshot({
        title: getEditableSetlistTitle(savedSetlist.title),
        occasion: savedSetlist.occasion || "Häät",
        sets: hydratedSets,
      })
    );

  setSetlistMessage(
    t("setlistLoad.loaded", {
      defaultValue:
        "Tallennettu settilista ladattu ja asetettu nykyisen keikan aktiiviseksi settilistaksi.",
    })
  );

  } catch (error) {
    console.error(
      "Aktiivisen settilistan tallennus epäonnistui:",
      error
    );

   setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistLoad.activateFailed",
        "Settilistaa ei voitu asettaa aktiiviseksi."
      )
    );
  }
}

useEffect(() => {
  // Aktiivista settilistaa ei palauteta selaimen vanhan
  // sessionStorage-cachen perusteella.
  // Odotetaan ensin tuoreet asetukset backendiltä.
  if (!areParanoidiSettingsLoaded) {
    return;
  }

  const currentGigSetlistId =
    paranoidiSettings?.currentGigSetlistId ??
    paranoidiSettings?.current_gig_setlist_id ??
    null;

  if (!currentGigSetlistId || !savedSetlists.length) {
    return;
  }

  // Sama backendistä löytynyt setti on jo palautettu
  // tämän sivulatauksen aikana.
  if (
    String(restoredCurrentGigSetlistIdRef.current || "") ===
    String(currentGigSetlistId)
  ) {
    return;
  }

  const savedSetlist = savedSetlists.find(
    (item) =>
      String(item.id) === String(currentGigSetlistId)
  );

  if (!savedSetlist) {
    return;
  }

  restoredCurrentGigSetlistIdRef.current =
    String(currentGigSetlistId);

  setActiveSavedSetlistId(savedSetlist.id);
  setSetlistTitle(getEditableSetlistTitle(savedSetlist.title));
  setSetlistOccasion(savedSetlist.occasion || "Häät");
  setSetlistSetCount(
    savedSetlist.setCount ||
    savedSetlist.set_count ||
    1
  );
  setSetlistSongsPerSet(
    savedSetlist.songsPerSet ||
    savedSetlist.songs_per_set ||
    12
  );

  // Sama hydratoitu rakenne kuin silloin, kun käyttäjä
  // painaa tallennetun settilistan Lataa-painiketta.
  const hydratedSets =
    hydrateSavedSetlistSets(savedSetlist.sets || []);

  setGeneratedSetlist(hydratedSets);

  setSavedSetlistEditSnapshot(
    buildSetlistEditSnapshot({
      title: getEditableSetlistTitle(savedSetlist.title),
      occasion: savedSetlist.occasion || "Häät",
      sets: hydratedSets,
    })
  );

    setSetlistMessage(
      t("setlistLoad.restoredAutomatically", {
        defaultValue:
          "Nykyisen keikan aktiivinen settilista palautettiin automaattisesti.",
      })
  );
}, [
  savedSetlists,
  librarySongs,
  paranoidiSettings,
  areParanoidiSettingsLoaded,
]);

function resetCurrentSetlistState() {
  setIsPrompterRunning(false);
  setActiveSavedSetlistId(null);
  setSavedSetlistEditSnapshot("");
  setSetlistTitle("");
  setGeneratedSetlist([]);
  setActiveSetlistSong(null);
  setIsSetlistNotePanelOpen(false);
  setIsSetlistSongEditOpen(false);
  setSetlistSongEditNotes("");
  setSetlistSongEditMessage("");
  setSetlistSongSearchBySet({});
  setActiveSetlistAddSongSetIndex(null);
}

async function closeCurrentSetlist() {
  try {
    // Tyhjennetään aktiivinen settilista myös palvelimelta.
    // Näin suljettu setti ei palaudu takaisin sivun päivityksessä.
    await updateParanoidiSettings({
      currentGigSetlistId: null,
    });

    restoredCurrentGigSetlistIdRef.current = null;

    resetCurrentSetlistState();

    setSetlistMessage(
      t("setlistClose.closed", {
        defaultValue:
          "Setti suljettu. Voit nyt luoda uuden settilistan.",
      })
    );
  } catch (error) {
    console.error(
      "Aktiivisen settilistan sulkeminen epäonnistui:",
      error
    );

    setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistClose.failed",
        "Settiä ei voitu sulkea."
      )
    );
  }
}

async function deleteSavedSetlist(savedSetlistId) {
  const slug = String(effectivePerformerSlug || "").trim();

  if (!slug) {
    setSetlistMessage(
      t("setlistDelete.performerMissing", {
        defaultValue:
          "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään.",
      })
    );
    return;
  }

  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  try {
    const response = await fetchWithCredentials(
      `${apiBaseUrl}/admin/performers/${encodeURIComponent(
        slug
      )}/setlists/${encodeURIComponent(savedSetlistId)}`,
      {
        method: "DELETE",
      }
    );

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(
        data.error ||
          t("setlistDelete.failed", {
            defaultValue: "Settilistan poisto epäonnistui.",
          })
      );
    }

const deletedSetlistWasActive =
  String(activeSavedSetlistId) === String(savedSetlistId);

setSavedSetlists((current) =>
  current.filter(
    (setlist) =>
      String(setlist.id) !== String(savedSetlistId)
  )
);

if (deletedSetlistWasActive) {
  try {
    // Jos poistettava settilista oli nykyisen keikan aktiivinen setti,
    // poistetaan myös sen tunniste keikkasession asetuksista.
    await updateParanoidiSettings({
      currentGigSetlistId: null,
    });

    restoredCurrentGigSetlistIdRef.current = null;

    // Poistettua aktiivista settiä ei voida enää pitää
    // nykyisen selaimen keikkatilassa.
    resetCurrentSetlistState();
  } catch (settingsError) {
    console.error(
      "Poistetun aktiivisen settilistan asetuksen tyhjennys epäonnistui:",
      settingsError
    );

    // Itse settilista on tässä vaiheessa jo poistettu palvelimelta,
    // joten tyhjennetään se joka tapauksessa myös käyttöliittymästä.
    restoredCurrentGigSetlistIdRef.current = null;
    resetCurrentSetlistState();

    setSetlistMessage(
      t("setlistDelete.settingsCleanupFailed", {
        defaultValue:
          "Settilista poistettiin, mutta aktiivisen settilistan tiedon tyhjennys asetuksista epäonnistui.",
      })
    );

    return;
  }
}

  setSetlistMessage(
    deletedSetlistWasActive
      ? t("setlistDelete.activeDeleted", {
          defaultValue:
            "Aktiivinen settilista poistettu ja nykyisen keikan settilista tyhjennetty.",
        })
      : t("setlistDelete.savedDeleted", {
          defaultValue:
            "Tallennettu settilista poistettu palvelimelta.",
        })
  );

  } catch (error) {
    console.error("Settilistan poisto epäonnistui:", error);
    setSetlistMessage(
      getLocalizedAdminError(
        error,
        "setlistDelete.failed",
        "Settilistan poisto epäonnistui."
      )
    );
  }
}

function getSetlistAddSongSearch(setIndex) {
  return setlistSongSearchBySet[String(setIndex)] || "";
}

function updateSetlistAddSongSearch(setIndex, nextValue) {
  setSetlistSongSearchBySet((current) => ({
    ...current,
    [String(setIndex)]: nextValue,
  }));
}

function getSetlistAddSongResults(setIndex) {
  const query = normalize(getSetlistAddSongSearch(setIndex));
  const selectedOccasion = normalize(setlistOccasion);

  if (!query) {
    return [];
  }

  const usedSongIds = new Set(
    generatedSetlist.flatMap((set) =>
      set.songs.map((song) => String(song.id))
    )
  );

  return librarySongs
    .filter((song) => {
      if (!song?.id || usedSongIds.has(String(song.id))) {
        return false;
      }

      // Käsin lisäämisessä haetaan vain biisin nimestä ja artistista.
      // Näin yhdellä kirjaimella ei tule osumia genrestä, vuodesta tai tägeistä.
      const titleAndArtist = normalize([song.title, song.artist].join(" "));

      return titleAndArtist.includes(query);
    })
    .sort((a, b) => {
      // Valitun tilaisuuden biisit nostetaan listan alkuun,
      // mutta muita biisejä ei piiloteta.
      const aTags = parseTagList(getSongOccasionTags(a)).map(normalize);
      const bTags = parseTagList(getSongOccasionTags(b)).map(normalize);

      const aMatchesOccasion =
        selectedOccasion && aTags.includes(selectedOccasion);
      const bMatchesOccasion =
        selectedOccasion && bTags.includes(selectedOccasion);

      if (aMatchesOccasion !== bMatchesOccasion) {
        return aMatchesOccasion ? -1 : 1;
      }

      const aTitle = String(a.title || "");
      const bTitle = String(b.title || "");

      return aTitle.localeCompare(bTitle, "fi");
    })
    .slice(0, 8);
}

function addLibrarySongToSet(setIndex, songId) {
  const songToAdd = librarySongs.find(
    (song) => String(song.id) === String(songId)
  );

  if (!songToAdd) {
    setSetlistMessage(
      t("setlistEdit.songNotFound", {
        defaultValue:
          "Biisiä ei löytynyt biisikirjastosta.",
      })
    );
    return;
  }

  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      return {
        ...set,
        songs: [
          ...set.songs,
          {
            ...songToAdd,
            setlistNote: "",
          },
        ],
      };
    })
  );

  updateSetlistAddSongSearch(setIndex, "");
  setActiveSetlistAddSongSetIndex(null);
  const saveAction = activeSavedSetlistId
    ? t("setlistEdit.saveChangesAction", {
        defaultValue: "Tallenna muutokset",
      })
    : t("setlistEdit.saveSetAction", {
        defaultValue: "Tallenna setti",
      });

  setSetlistMessage(
    t("setlistEdit.songAdded", {
      title: songToAdd.title,
      action: saveAction,
      defaultValue:
        'Lisättiin "{{title}}" settiin. Muista painaa {{action}}, jotta muutos säilyy.',
    })
  );
}

function removeSongFromSet(setIndex, songIndex) {
  let removedSongTitle = "";

  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== setIndex) {
        return set;
      }

      const removedSong = set.songs[songIndex];

      removedSongTitle =
        removedSong?.title ||
        t("setlistEdit.songFallback", {
          defaultValue: "Biisi",
        });

      return {
        ...set,
        songs: set.songs.filter(
          (_song, currentSongIndex) =>
            currentSongIndex !== songIndex
        ),
      };
    })
  );

  const saveAction = activeSavedSetlistId
    ? t("setlistEdit.saveChangesAction", {
        defaultValue: "Tallenna muutokset",
      })
    : t("setlistEdit.saveSetAction", {
        defaultValue: "Tallenna setti",
      });

  setSetlistMessage(
    t("setlistEdit.songRemoved", {
      title: removedSongTitle,
      action: saveAction,
      defaultValue:
        'Poistettiin "{{title}}" setistä. Muista painaa {{action}}, jotta muutos säilyy.',
    })
  );
}

function removeSetFromSetlist(setIndex) {
  if (generatedSetlist.length <= 1) {
    setSetlistMessage(
      t("setlistEdit.minimumOneSet", {
        defaultValue:
          "Settilistassa pitää olla vähintään yksi setti.",
      })
    );
    return;
  }

  const removedSetTitle = getLocalizedSetTitle(
    generatedSetlist[setIndex]?.title,
    setIndex
  );

  const nextSets = generatedSetlist
    .filter((_set, currentSetIndex) => currentSetIndex !== setIndex)
    .map((set, nextIndex) => ({
      ...set,
      title: t("setlistDynamic.setTitle", {
        number: nextIndex + 1,
        defaultValue: `Setti ${nextIndex + 1}`,
      }),
    }));

  setGeneratedSetlist(nextSets);
  setSetlistSetCount(nextSets.length);

  setSetlistSongSearchBySet((current) => {
    const nextSearches = {};

    Object.entries(current).forEach(([key, value]) => {
      const currentIndex = Number(key);

      if (!Number.isFinite(currentIndex) || currentIndex === setIndex) {
        return;
      }

      const nextIndex = currentIndex > setIndex ? currentIndex - 1 : currentIndex;
      nextSearches[String(nextIndex)] = value;
    });

    return nextSearches;
  });

  setActiveSetlistAddSongSetIndex((current) => {
    if (current === null || current === undefined) {
      return null;
    }

    if (current === setIndex) {
      return null;
    }

    return current > setIndex ? current - 1 : current;
  });

  setActiveSetlistSong((current) => {
    if (!current) {
      return null;
    }

    if (current.setIndex === setIndex) {
      return null;
    }

    if (current.setIndex > setIndex) {
      return {
        ...current,
        setIndex: current.setIndex - 1,
      };
    }

    return current;
  });

  const saveAction = activeSavedSetlistId
    ? t("setlistEdit.saveChangesAction", {
        defaultValue: "Tallenna muutokset",
      })
    : t("setlistEdit.saveSetAction", {
        defaultValue: "Tallenna setti",
      });

  setSetlistMessage(
    t("setlistEdit.setRemoved", {
      title: removedSetTitle,
      action: saveAction,
      defaultValue:
        "Poistettiin {{title}}. Muista painaa {{action}}, jotta muutos säilyy.",
    })
  );
}

function replaceSetlistSong(setIndex, songIndex) {
  const occasion = String(setlistOccasion || "").trim();

  if (!occasion) {
    setSetlistMessage(
      t("setlistEdit.selectOccasionFirst", {
        defaultValue: "Valitse ensin tilaisuus.",
      })
    );
    return;
  }

  setGeneratedSetlist((currentSets) => {
    const currentSet = currentSets[setIndex];

    if (!currentSet || !currentSet.songs[songIndex]) {
      return currentSets;
    }

    const currentSong = currentSet.songs[songIndex];

    const usedSongIds = new Set(
      currentSets.flatMap((set) => set.songs.map((song) => String(song.id)))
    );

    const candidates = librarySongs.filter((song) => {
      if (!isSongRequestable(song)) {
        return false;
      }

      if (String(song.id) === String(currentSong.id)) {
        return false;
      }

      if (usedSongIds.has(String(song.id))) {
        return false;
      }

      const tags = parseTagList(getSongOccasionTags(song)).map(normalize);

      return tags.includes(normalize(occasion));
    });

    if (!candidates.length) {
      setSetlistMessage(
        t("setlistEdit.noReplacementSongs", {
          occasion: translateAdminLabel(occasion),
          defaultValue:
            'Tilaisuudelle "{{occasion}}" ei löytynyt enää vaihtobiisejä.',
        })
      );
      return currentSets;
    }

    const replacement =
      candidates[Math.floor(Math.random() * candidates.length)];

    const nextSets = currentSets.map((set) => ({
      ...set,
      songs: [...set.songs],
    }));

    const preservedManualNote =
      getSetlistSongManualNote(currentSong);

    const preservedGigNoteIds =
      getSetlistSongGigNoteIds(currentSong);

    nextSets[setIndex].songs[songIndex] = {
      ...replacement,

      // Biisiä vaihdettaessa säilytetään settilistapaikan
      // oma käsin kirjoitettu muistiinpano sekä Keikkamuisti-ID-linkit.
      // Linkitetyn Keikkamuistin tekstiä ei kopioida biisiin.
      setlistManualNote: preservedManualNote,
      setlist_manual_note: preservedManualNote,

      // Legacy-yhteensopivuuskentissä pidetään vain biisin oma teksti.
      setlistNote: preservedManualNote,
      setlist_note: preservedManualNote,

      setlistGigNoteIds: preservedGigNoteIds,
      setlist_gig_note_ids: preservedGigNoteIds,
    };

       setSetlistMessage(
        t("setlistEdit.songReplaced", {
          oldTitle: currentSong.title,
          newTitle: replacement.title,
          defaultValue:
            'Vaihdettiin "{{oldTitle}}" → "{{newTitle}}".',
        })
      );

        return nextSets;
      });
    }

  async function markSeen(id) {
    try {
      await updateSongRequestStatus(id, "seen", effectivePerformerSlug);
      await refreshRequests();
    } catch (error) {
      console.error("Biisitoiveen merkintä nähdyksi epäonnistui:", error);
      alert(
        t("requestActions.updateFailed", {
          defaultValue: "Toiveen päivitys epäonnistui.",
        })
      );
    }
  }

  async function markPlayed(id) {
    try {
      await updateSongRequestStatus(id, "played", effectivePerformerSlug);
      await refreshRequests();
    } catch (error) {
      console.error("Biisitoiveen merkintä soitetuksi epäonnistui:", error);
      alert(
        t("requestActions.updateFailed", {
          defaultValue: "Toiveen päivitys epäonnistui.",
        })
      );
    }
  }

async function removeRequest(id) {
  const slug = String(effectivePerformerSlug || "").trim();

  if (!slug) {
    alert(
      t("requestActions.performerMissing", {
        defaultValue:
          "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään.",
      })
    );
    return;
  }

  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  const url = `${apiBaseUrl}/public/performers/${encodeURIComponent(
    slug
  )}/song-requests/${encodeURIComponent(id)}`;

  try {
    const response = await fetchWithCredentials(url, {
      method: "DELETE",
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.error ||
          t("requestActions.deleteFailed", {
            defaultValue: "Toiveen poisto epäonnistui.",
          })
      );
    }

    await refreshRequests();
  } catch (error) {
    console.error("Biisitoiveen poisto epäonnistui:", error);
    alert(
      getLocalizedAdminError(
        error,
        "requestActions.deleteFailed",
        "Toiveen poisto epäonnistui."
      )
    );
  }
}

  async function markGroupPlayed(groupRequests) {
  try {
    await Promise.all(
      groupRequests.map((request) =>
        updateSongRequestStatus(request.id, "played", effectivePerformerSlug)
      )
    );

    await refreshRequests();
  } catch (error) {
    console.error("Biisiryhmän merkintä soitetuksi epäonnistui:", error);
    alert(
      t("requestActions.groupUpdateFailed", {
        defaultValue:
          "Toiveiden päivitys epäonnistui.",
      })
    );
  }
}

async function markGroupSeen(groupRequests) {
  try {
    await Promise.all(
      groupRequests.map((request) =>
        updateSongRequestStatus(request.id, "new", effectivePerformerSlug)
      )
    );

    await refreshRequests();
  } catch (error) {
    console.error("Biisiryhmän palautus uudeksi epäonnistui:", error);
    alert(
      t("requestActions.groupUpdateFailed", {
        defaultValue:
          "Toiveiden päivitys epäonnistui.",
      })
    );
  }
}

async function removeGroupRequests(groupRequests) {
  try {
    await Promise.all(
      groupRequests.map((request) =>
        deleteSongRequest(request.id, effectivePerformerSlug)
      )
    );

    await refreshRequests();
  } catch (error) {
    console.error("Biisiryhmän poisto epäonnistui:", error);
    alert(
      t("requestActions.groupDeleteFailed", {
        defaultValue:
          "Toiveiden poisto epäonnistui.",
      })
    );
  }
}

  function clearAll() {
    setIsClearModalOpen(true);
  }

  function closeClearModal() {
    setIsClearModalOpen(false);
  }

async function confirmClearAll() {
  try {
    // Ei koskaan poisteta soittamattomia toiveita.
    // Siivotaan vain toiveet, jotka Paranoidi on merkinnyt soitetuksi.
    const requestsToRemove = requests.filter((request) => request.status === "played");

    await Promise.all(
        requestsToRemove.map((request) =>
        deleteSongRequest(request.id, effectivePerformerSlug)
      )
    );

    await refreshRequests();
    setRaffleWinner(null);
    setIsClearModalOpen(false);
  } catch (error) {
    console.error("Soitettujen biisitoiveiden siivous epäonnistui:", error);
    alert(
      t("requestActions.clearPlayedFailed", {
        defaultValue:
          "Soitettujen toiveiden siivous epäonnistui.",
      })
    );
  }
}

async function submitBulkSongs(event) {
  event.preventDefault();

  const slug = String(effectivePerformerSlug || "").trim();

  if (!slug) {
    setBulkImportMessage(
      t("bulkImport.performerMissing", {
        defaultValue:
          "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään.",
      })
    );
    return;
  }

  const lines = bulkSongsText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    setBulkImportMessage(
      t("bulkImport.addAtLeastOneSong", {
        defaultValue:
          "Lisää vähintään yksi biisirivi.",
      })
    );
    return;
  }

  const bulkItems = buildBulkSongItems(lines, bulkLanguage);

  try {
    const result = await bulkCreateSongs(
      {
        genre: bulkGenre.trim(),
        decade: bulkDecade.trim(),
        year: bulkYear ? Number(bulkYear) : null,
        language: bulkLanguage === "auto" ? "unknown" : bulkLanguage,
        mood: bulkMood
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        items: bulkItems,
      },
      slug
    );

  setBulkImportMessage(
    t("bulkImport.completed", {
      created: result.created_count || 0,
      skipped: result.skipped_count || 0,
      defaultValue:
        "Lisätty {{created}} biisiä. Ohitettu {{skipped}}.",
    })
  );

    setBulkSongsText("");
    await refreshLibrarySongs();
  } catch (error) {
    console.error("Biisien massa-ajo epäonnistui:", error);
    setBulkImportMessage(
      getLocalizedAdminError(
        error,
        "bulkImport.saveFailed",
        "Biisien tallennus epäonnistui."
      )
    );
  }
}

  const freeRequests = requests.filter(isFreeRequest);
  const libraryRequests = requests.filter((request) => !isFreeRequest(request));

  /*
  * Maksetut näkyvyysnostot näytetään aktiivisen Toivehallinnan
  * tavallisten toiveiden edellä.
  *
  * Nostetut toiveet muodostavat maksujärjestyksessä etenevän jonon:
  * ensimmäisenä maksanut säilyttää paikkansa ja uudemmat nostot
  * tulevat hänen jälkeensä.
  *
  * Jos samasta kappaleesta on useita aktiivisia toiveita ja yksikin niistä
  * on nostettu, koko kappaleryhmä näytetään Nostetut toiveet -osiossa.
  * Soitetut toiveet eivät palaa noston vuoksi aktiivisen listan kärkeen.
  */
  const activeBoostedRequestGroups = Array.from(
    requests
      .filter((request) => request.status !== "played")
      .reduce((groups, request) => {
        const key = [
          isFreeRequest(request) ? "free" : "library",
          normalize(request.songTitle),
          normalize(request.artist),
        ].join("::");

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            requests: [],
          });
        }

        groups.get(key).requests.push(request);

        return groups;
      }, new Map())
      .values()
  )
    .map((group) => {
      const boostedRequests = group.requests
        .filter((request) =>
          Boolean(request.isBoosted ?? request.is_boosted)
        )
        .sort((a, b) => {
          const rawTimeA = Date.parse(
            a.boostPaidAt || a.boost_paid_at || ""
          );

          const rawTimeB = Date.parse(
            b.boostPaidAt || b.boost_paid_at || ""
          );

          const timeA = Number.isFinite(rawTimeA) ? rawTimeA : 0;
          const timeB = Number.isFinite(rawTimeB) ? rawTimeB : 0;

          return timeA - timeB;
        });

      const boostedRequest = boostedRequests[0] || null;

      if (!boostedRequest) {
        return null;
      }

      const rawBoostTime = Date.parse(
        boostedRequest.boostPaidAt ||
          boostedRequest.boost_paid_at ||
          ""
      );

     return {
        ...group,

        // Nostettuun ryhmään kuuluvat vain ne yksittäiset toiveet,
        // joista näkyvyysnosto on oikeasti maksettu.
        requests: boostedRequests,

        request: boostedRequest,
        boostTime: Number.isFinite(rawBoostTime)
          ? rawBoostTime
          : 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.boostTime - b.boostTime);

  const boostedRequestIds = new Set(
    activeBoostedRequestGroups.flatMap((group) =>
      group.requests.map((request) => String(request.id))
    )
  );

  const regularFreeRequests = freeRequests.filter(
    (request) => !boostedRequestIds.has(String(request.id))
  );

  const regularLibraryRequests = libraryRequests.filter(
    (request) => !boostedRequestIds.has(String(request.id))
  );

  const freeRequestTotalCount = regularFreeRequests.reduce(
    (total, request) => total + getRequestCount(request),
    0
  );

  const newCount = libraryRequests.filter((request) => request.status === "new").length;

  const performerDisplayName = getLoggedInPerformerName(config?.performer?.name);

const rawPerformerPlanLabel = String(
  effectiveFeatures?.name ||
    effectivePerformer?.planName ||
    effectivePerformer?.plan ||
    config?.planName ||
    config?.plan ||
    ""
).trim();

  const normalizedPerformerPlanLabel = rawPerformerPlanLabel.toLowerCase();

  const performerPlanLabel =
    normalizedPerformerPlanLabel === "free" ||
    normalizedPerformerPlanLabel === "trial"
      ? t("planLabels.trial", {
          defaultValue: "Kokeilu",
        })
      : normalizedPerformerPlanLabel === "basic"
        ? t("planLabels.basic", {
            defaultValue: "Basic",
          })
        : normalizedPerformerPlanLabel === "plus"
          ? t("planLabels.plus", {
              defaultValue: "Plus",
            })
          : rawPerformerPlanLabel ||
            (canUseSetlists || canUseGigNotes
              ? t("planLabels.plus", {
                  defaultValue: "Plus",
                })
              : canUsePublicLanguageFilter
                ? t("planLabels.basic", {
                    defaultValue: "Basic",
                  })
                : t("planLabels.trial", {
                    defaultValue: "Kokeilu",
                  }));

const songLibraryLimit =
  Number(
    effectiveFeatures?.songLimit ??
      effectiveFeatures?.song_limit ??
      config?.songLimit ??
      config?.song_limit ??
      0
  ) || 0;

  const songLibraryCountLabel =
    songLibraryLimit > 0
      ? `${librarySongs.length} / ${songLibraryLimit}`
      : `${librarySongs.length}`;

  const songLibraryUsagePercent =
    songLibraryLimit > 0
      ? Math.min(
          100,
          Math.round((librarySongs.length / songLibraryLimit) * 100)
        )
      : 0;

  const songLibraryBarPercent =
  librarySongs.length > 0
    ? Math.max(3, songLibraryUsagePercent)
    : 0;

  const publicLanguageValue = String(
    paranoidiSettings?.publicSongLanguageMode || "all"
  ).trim();

  const publicLanguageSummary =
    publicLanguageValue === "all"
      ? t("languageLabels.all", {
          defaultValue: "Kaikki kielet",
        })
      : getLocalizedSongLanguageLabel(publicLanguageValue);

  const publicPerformerPath =
    String(
      getCurrentAdminPublicPath() ||
        (effectivePerformerSlug ? `/p/${effectivePerformerSlug}` : "/")
    ).trim() || "/";

  const publicPerformerUrl =
    typeof window !== "undefined"
      ? publicPerformerPath.startsWith("http")
        ? publicPerformerPath
        : `${window.location.origin}${publicPerformerPath}`
      : publicPerformerPath;

  const adminHeroImageUrl = String(
    paranoidiSettings?.heroImageUrl ||
      paranoidiSettings?.hero_image_url ||
      ""
  ).trim();

  // Nämä ovat jo soitetut/käsitellyt toiveet.
  // Vain nämä saa siivota pois hallinnasta massatoiminnolla.
  const playedRequests = requests.filter((request) => request.status === "played");

  const requestGroupCounts = regularLibraryRequests.reduce((counts, request) => {
      const key = request.status || "new";
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {});

  const groupedLibraryRequests = Array.from(
    regularLibraryRequests
      .reduce((groups, request) => {
        const status = request.status || "new";
        const songKey = [
          normalize(request.songTitle),
          normalize(request.artist),
        ].join("::");

        if (!groups.has(songKey)) {
          groups.set(songKey, {
            key: songKey,
            status,
            request,
            requests: [],
          });
        }

        groups.get(songKey).requests.push(request);

        return groups;
      }, new Map())
      .values()
  );


  const liveSummary = useMemo(() => {
    const allRequests = Array.isArray(requests) ? requests : [];
    const setlistSnapshot = Array.isArray(generatedSetlist) ? generatedSetlist : [];
    const requestChannelActive = Boolean(
      paranoidiSettings?.isLiveActive ?? paranoidiSettings?.is_live_active
    );
    const setlistSongCount = setlistSnapshot.reduce(
      (total, set) => total + (Array.isArray(set?.songs) ? set.songs.length : 0),
      0
    );

    // "Aloita uusi keikka" tallentaa tämän ajan backendin asetuksiin.
    // Jos arvo puuttuu, kyseessä on vanha keikkatilanne, jonka alkuaikaa
    // ei voida päätellä luotettavasti jälkikäteen.
    const currentGigStartedAt =
      paranoidiSettings?.currentGigStartedAt ||
      paranoidiSettings?.current_gig_started_at ||
      null;

    const currentGigStartedLabel =
      formatGigDateTime(currentGigStartedAt, adminLocale);

    // Settilista on keikkasession osa, mutta sessio voi olla käynnissä
    // myös ilman aktiivista settilistaa.
    const activeSetlistTitle =
      setlistSongCount > 0
        ? String(setlistTitle || "").trim()
        : "";

    const activeSetlistOccasion =
      setlistSongCount > 0
        ? String(setlistOccasion || "").trim()
        : "";
    const setlistNoteCount = setlistSnapshot.reduce((total, set) => {
      const songs = Array.isArray(set?.songs) ? set.songs : [];

      return (
        total +
        songs.filter(
          (song) =>
            // Lasketaan mukaan joko biisin oma muistiinpano
            // tai vähintään yksi ID:llä linkitetty Keikkamuisti.
            Boolean(getSetlistSongManualNote(song)) ||
            getSetlistSongGigNoteIds(song).length > 0
        ).length
      );
    }, 0);
    const gigNoteCount = Array.isArray(gigNotes) ? gigNotes.length : 0;

    const totalRequestCount = allRequests.reduce(
      (total, request) => total + getRequestCount(request),
      0
    );

    // Yksi maksettu nosto kuuluu yhdelle biisitoiveriville.
    // requestCountia ei käytetä tässä, koska samaan toiveeseen maksetaan
    // vain yksi näkyvyysnosto.
    const boostedRequests = allRequests.filter((request) =>
      Boolean(request.isBoosted ?? request.is_boosted)
    );

    const boostedRequestCount = boostedRequests.length;
    const boostRevenueCents =
      boostedRequestCount * REQUEST_BOOST_PRICE_CENTS;

    const songMap = new Map();

    const playedSongMap = new Map();
    const freeRequestItems = [];
    const activeRequesterMap = new Map();

    allRequests.forEach((request) => {
      const label = getRequestSongLabel(
        request,
        t("liveSummaryReport.unknownSong", {
          defaultValue: "Tuntematon biisi",
        })
      );
      const songKey = [
        normalize(request.songTitle || request.song_title),
        normalize(request.artist || request.songArtist || request.song_artist),
      ].join("::");
      const count = getRequestCount(request);

      if (!songMap.has(songKey)) {
        songMap.set(songKey, {
          key: songKey,
          label,
          count: 0,
        });
      }

      songMap.get(songKey).count += count;

      if (request.status === "played") {
        if (!playedSongMap.has(songKey)) {
          playedSongMap.set(songKey, {
            key: songKey,
            label,
            count: 0,
          });
        }

        playedSongMap.get(songKey).count += count;
      }

      if (isFreeRequest(request)) {
        freeRequestItems.push({
          id: request.id,
          label,
          requesterName: request.requesterName || "",
          message: request.message || "",
          count,
        });
      }

      const requesterName = String(request.requesterName || "").trim();

      if (requesterName) {
        const requesterKey = normalize(requesterName);

        if (!activeRequesterMap.has(requesterKey)) {
          activeRequesterMap.set(requesterKey, {
            key: requesterKey,
            label: requesterName,
            count: 0,
          });
        }

        activeRequesterMap.get(requesterKey).count += count;
      }
    });

    const uniqueSongs = sortSummaryItems(
      Array.from(songMap.values()),
      adminLocale
    );

    const playedSongs = sortSummaryItems(
      Array.from(playedSongMap.values()),
      adminLocale
    );

    const activeRequesters = sortSummaryItems(
      Array.from(activeRequesterMap.values()),
      adminLocale
    );

    const playedRequestCount = allRequests
      .filter((request) => request.status === "played")
      .reduce((total, request) => total + getRequestCount(request), 0);

    const seenRequestCount = allRequests
      .filter((request) => request.status === "seen")
      .reduce((total, request) => total + getRequestCount(request), 0);

    const unhandledRequestCount = allRequests
      .filter((request) => !["seen", "played"].includes(request.status || "new"))
      .reduce((total, request) => total + getRequestCount(request), 0);

    const freeRequestCount = freeRequestItems.reduce(
      (total, request) => total + request.count,
      0
    );

    const topSongs = uniqueSongs.slice(0, 5);
    const topPlayedSongs = playedSongs.slice(0, 8);

    const topFreeRequests = freeRequestItems
      .sort(
        (a, b) =>
          b.count - a.count ||
          a.label.localeCompare(b.label, adminLocale)
      )
      .slice(0, 8);

    const topRequesters = activeRequesters.slice(0, 6);

    const mostWantedText = topSongs.length
      ? topSongs
          .slice(0, 3)
          .map((item) => item.label)
          .join(", ")
      : t("liveSummaryReport.noMostWantedSongs", {
          defaultValue: "ei vielä yhtään biisiä",
        });

    const reportSources = [
      requestChannelActive
        ? t("liveSummaryReport.sourceRequestChannel", {
            defaultValue: "toivekanava",
          })
        : "",
      totalRequestCount > 0
        ? t("liveSummaryReport.sourceRequests", {
            defaultValue: "nykyiset biisitoiveet",
          })
        : "",
      setlistSongCount > 0
        ? t("liveSummaryReport.sourceSetlist", {
            defaultValue: "settilista",
          })
        : "",
      gigNoteCount > 0
        ? t("liveSummaryReport.sourceGigNotes", {
            defaultValue: "Keikkamuisti",
          })
        : "",
    ].filter(Boolean);

    const reportMode = reportSources.length
      ? reportSources.join(", ")
      : t("liveSummaryReport.sourceEmpty", {
          defaultValue: "tyhjä keikkatilanne",
        });

    const requestChannelLine = requestChannelActive
      ? t("liveSummaryReport.requestChannelActive", {
          defaultValue:
            "Toivekanava oli käytössä raportin tallennushetkellä. Mukaan otetaan yleisön live-toiveet, aktiiviseksi ladattu settilista ja siihen liitetty Keikkamuisti.",
        })
      : t("liveSummaryReport.requestChannelInactive", {
          defaultValue:
            "Toivekanava ei ollut käytössä raportin tallennushetkellä. Nykyiset biisitoiveet otetaan silti mukaan tämänhetkisestä listasta.",
        });

    const requestSummaryLines = totalRequestCount > 0
      ? [
          t("liveSummaryReport.totalRequests", {
            count: totalRequestCount,
            defaultValue:
              "Biisitoiveita tuli yhteensä {{count}}.",
          }),
          t("liveSummaryReport.uniqueSongs", {
            count: uniqueSongs.length,
            defaultValue:
              "Eri biisejä toivottiin {{count}}.",
          }),
          t("liveSummaryReport.playedRequests", {
            count: playedRequestCount,
            defaultValue:
              "Soitetuksi merkittiin {{count}} toivetta.",
          }),
          t("liveSummaryReport.unhandledRequests", {
            count: unhandledRequestCount,
            defaultValue:
              "Käsittelemättömiä toiveita jäi {{count}}.",
          }),
          t("liveSummaryReport.freeRequests", {
            count: freeRequestCount,
            defaultValue:
              "Vapaita / erikoistoiveita tuli {{count}}.",
          }),
          t("liveSummaryReport.boostedRequests", {
            count: boostedRequestCount,
            defaultValue:
              "Nostettuja toiveita: {{count}}.",
          }),
          t("liveSummaryReport.boostRevenue", {
            amount: formatBoostAmount(
              boostRevenueCents,
              adminLocale
            ),
            defaultValue:
              "Nostoista maksettu yhteensä: {{amount}}.",
          }),
          "",
          t("liveSummaryReport.mostWanted", {
            songs: mostWantedText,
            defaultValue:
              "Tällä keikalla yleisö toivoi eniten: {{songs}}.",
          }),
          "",
          t("liveSummaryReport.topRequestsHeading", {
            defaultValue:
              "Suosituimmat biisitoiveet:",
          }),
          formatSummaryList(
            topSongs,
            t("liveSummaryReport.noRequests", {
              defaultValue:
                "Ei biisitoiveita vielä.",
            })
          ),
          "",
          t("liveSummaryReport.playedSongsHeading", {
            defaultValue:
              "Soitetut biisit:",
          }),
          formatSummaryList(
            topPlayedSongs,
            t("liveSummaryReport.noPlayedSongs", {
              defaultValue:
                "Ei soitetuksi merkittyjä biisejä vielä.",
            })
          ),
          "",
          t("liveSummaryReport.activeRequestersHeading", {
            defaultValue:
              "Aktiivisimmat nimet / pöydät / porukat:",
          }),
          formatSummaryList(
            topRequesters,
            t("liveSummaryReport.noRequesters", {
              defaultValue:
                "Ei nimiä, pöytiä tai porukoita vielä.",
            })
          ),
        ]
      : [
          requestChannelActive
            ? t("liveSummaryReport.requestChannelActiveNoRequests", {
                defaultValue:
                  "Toivekanava oli käytössä, mutta biisitoiveita ei ollut vielä tallennushetkellä.",
              })
            : t("liveSummaryReport.requestChannelInactiveNoRequests", {
                defaultValue:
                  "Toivekanava ei ollut käytössä, joten raporttiin ei tullut yleisön live-toivedataa.",
              }),
        ];

    const setlistSongsForPerformance = setlistSnapshot.flatMap((set) =>
      Array.isArray(set?.songs) ? set.songs : []
    );

    const setlistPlayedCount = setlistSongsForPerformance.filter(
      (song) => getSetlistSongPerformanceStatus(song) === "played"
    ).length;

    const setlistSkippedCount = setlistSongsForPerformance.filter(
      (song) => getSetlistSongPerformanceStatus(song) === "skipped"
    ).length;

    const setlistUnmarkedCount = Math.max(
      0,
      setlistSongCount - setlistPlayedCount - setlistSkippedCount
    );

    const setlistSummaryLines = setlistSongCount > 0
      ? [
          t("liveSummaryReport.setlistSongCount", {
            count: setlistSongCount,
            defaultValue:
              "Settilistassa oli {{count}} biisiä.",
          }),
          t("liveSummaryReport.setlistPlayedCount", {
            count: setlistPlayedCount,
            defaultValue:
              "Soitetuksi merkitty: {{count}}.",
          }),
          t("liveSummaryReport.setlistSkippedCount", {
            count: setlistSkippedCount,
            defaultValue:
              "Ei soitettu / skipattu: {{count}}.",
          }),
          t("liveSummaryReport.setlistUnmarkedCount", {
            count: setlistUnmarkedCount,
            defaultValue:
              "Merkitsemättä: {{count}}.",
          }),
          t("liveSummaryReport.setlistNoteCount", {
            count: setlistNoteCount,
            defaultValue:
              "Biisikohtaisia muistiinpanoja / Keikkamuisti-liitoksia oli {{count}}.",
          }),
          "",
          t("liveSummaryReport.usedSetlistHeading", {
            defaultValue:
              "Käytetty settilista:",
          }),
          formatSetlistForSummaryText(
            setlistSnapshot,
            gigNotes,
            {
              emptySetlistText: t(
                "liveSummaryReport.noSetlist",
                {
                  defaultValue:
                    "Ei settilistaa mukana raportissa.",
                }
              ),
              defaultSetTitle: t(
                "setlistDynamic.setFallback",
                {
                  defaultValue: "Setti",
                }
              ),
              emptySetText: t(
                "liveSummaryReport.emptySet",
                {
                  defaultValue: "Ei biisejä.",
                }
              ),
              songTextOptions: {
                performancePrefix: t(
                  "setlistPerformance.prefix",
                  {
                    defaultValue:
                      "Keikkamerkintä",
                  }
                ),
                getPerformanceLabel:
                  getLocalizedSetlistSongPerformanceLabel,
                getGigNoteTypeLabelFn:
                  getLocalizedGigNoteTypeLabel,
              },
            }
          ),
        ]
      : [
          t("liveSummaryReport.noSetlist", {
            defaultValue:
              "Settilistaa ei ollut mukana raportissa.",
          }),
        ];

    const gigNotesSummaryLines = gigNoteCount > 0
      ? [
          t("liveSummaryReport.gigNotesCount", {
            count: gigNoteCount,
            defaultValue:
              "Keikkamuistissa oli {{count}} tallennettua merkintää.",
          }),
          t("liveSummaryReport.gigNotesLinkedInfo", {
            defaultValue:
              "Biiseihin liitetyt merkinnät tallentuvat raporttiin settilistan mukana.",
          }),
        ]
      : [
          t("liveSummaryReport.noGigNotes", {
            defaultValue:
              "Keikkamuistissa ei ollut erillisiä merkintöjä raportin tallennushetkellä.",
          }),
        ];

    const gigSessionSummaryLines = [
      currentGigStartedLabel
        ? t("liveSummaryReport.gigStarted", {
            date: currentGigStartedLabel,
            defaultValue:
              "Keikka aloitettu: {{date}}.",
          })
        : t("liveSummaryReport.gigStartMissing", {
            defaultValue:
              "Keikan aloitusaikaa ei ole tallennettu.",
          }),

      setlistSongCount > 0
        ? t("liveSummaryReport.activeSetlist", {
            title:
              activeSetlistTitle ||
              t("liveSummaryReport.unnamedSetlist", {
                defaultValue:
                  "Nimetön settilista",
              }),
            defaultValue:
              "Aktiivinen settilista: {{title}}.",
          })
        : t("liveSummaryReport.noActiveSetlist", {
            defaultValue:
              "Aktiivista settilistaa ei ole.",
          }),

      activeSetlistOccasion
        ? t("liveSummaryReport.occasion", {
            occasion:
              translateAdminLabel(
                activeSetlistOccasion
              ),
            defaultValue:
              "Tilaisuus: {{occasion}}.",
          })
        : "",
    ].filter(Boolean);

    const summaryText = [
      t("liveSummaryReport.title", {
        defaultValue: "Keikkaraportti",
      }),
      "",
      t("liveSummaryReport.sessionHeading", {
        defaultValue: "Keikkasessio:",
      }),
      ...gigSessionSummaryLines,
      "",
      t("liveSummaryReport.sourceLine", {
        source: reportMode,
        defaultValue:
          "Raportin lähde: {{source}}.",
      }),
      requestChannelLine,
      "",
      t("liveSummaryReport.requestsHeading", {
        defaultValue: "Biisitoiveet:",
      }),
      ...requestSummaryLines,
      "",
      t("liveSummaryReport.setlistHeading", {
        defaultValue: "Settilista:",
      }),
      ...setlistSummaryLines,
      "",
      t("liveSummaryReport.gigNotesHeading", {
        defaultValue: "Keikkamuisti:",
      }),
      ...gigNotesSummaryLines,
    ].join("\n");

    const metrics = {
      totalRequestCount,
      uniqueSongCount: uniqueSongs.length,
      playedRequestCount,
      seenRequestCount,

      // Maksetut näkyvyysnostot talteen keikkaraportin metriikoihin.
      boostedRequestCount,
      boostRevenueCents,

      setlistPlayedCount,
      setlistSkippedCount,
      setlistUnmarkedCount,
      unhandledRequestCount,
      freeRequestCount,
      setlistSongCount,
      setlistNoteCount,
      gigNoteCount,
      requestChannelActive,
      reportMode,

      // Keikkasession rajat tallennetaan myös raportin metriikoihin.
      currentGigStartedAt,
      activeSetlistTitle,
      activeSetlistOccasion,
    };

    const requestSnapshot = allRequests.map((request) => ({
      id: request.id,
      songTitle: request.songTitle || request.song_title || "",
      song_title: request.songTitle || request.song_title || "",
      artist: request.artist || request.songArtist || request.song_artist || "",
      requesterName: request.requesterName || request.requester_name || "",
      requester_name: request.requesterName || request.requester_name || "",
      requesterLocation:
        request.requesterLocation || request.requester_location || "",
      requester_location:
        request.requesterLocation || request.requester_location || "",
      message: request.message || "",
      status: request.status || "new",
      requestType: request.requestType || request.request_type || "library",
      request_type: request.requestType || request.request_type || "library",
      requestCount: getRequestCount(request),
      request_count: getRequestCount(request),

      // Säilytetään nostotieto myös yksittäisen toiveen snapshotissa.
      isBoosted: Boolean(request.isBoosted ?? request.is_boosted),
      is_boosted: Boolean(request.isBoosted ?? request.is_boosted),
      boostPaidAt: request.boostPaidAt || request.boost_paid_at || null,
      boost_paid_at: request.boost_paid_at || request.boostPaidAt || null,

      createdAt: request.createdAt || request.created_at || "",
      created_at: request.createdAt || request.created_at || "",
    }));

    const gigNotesSnapshot = Array.isArray(gigNotes)
      ? gigNotes.map((note) => ({
          id: note.id,
          type: note.type || note.note_type || "note",
          note_type: note.type || note.note_type || "note",
          occasion: note.occasion || "yleinen",
          title: note.title || "",
          content: note.content || "",
          isPinned: Boolean(note.isPinned ?? note.is_pinned),
          is_pinned: Boolean(note.isPinned ?? note.is_pinned),
        }))
      : [];

    return {
      ...metrics,
      topSongs,
      topPlayedSongs,
      topFreeRequests,
      topRequesters,
      metrics,
      requestSnapshot,
      setlistSnapshot,
      gigNotesSnapshot,
      summaryText,
    };
  }, [
    requests,
    generatedSetlist,
    gigNotes,
    paranoidiSettings,
    setlistTitle,
    setlistOccasion,
    adminLocale,
    t,
  ]);

  async function copyLiveSummary() {
    try {
      await navigator.clipboard.writeText(liveSummary.summaryText);
      setLiveSummaryMessage(
        t("liveSummaryReport.copied", {
          defaultValue:
            "Live-yhteenveto kopioitu leikepöydälle.",
        })
      );
    } catch (error) {
      console.error("Live-yhteenvedon kopiointi epäonnistui:", error);
      setLiveSummaryMessage(
        t("liveSummaryReport.copyFailed", {
          defaultValue:
            "Kopiointi epäonnistui. Voit maalata tekstin ja kopioida sen käsin.",
        })
      );
    }
  }

  async function saveLiveSummaryReport() {
    const slug = String(effectivePerformerSlug || "").trim();

    if (!slug) {
      setLiveSummaryMessage(
        t("liveSummaryReport.performerMissing", {
          defaultValue:
            "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään.",
        })
      );
      return;
    }

    if (
      liveSummary.totalRequestCount < 1 &&
      liveSummary.setlistSongCount < 1 &&
      liveSummary.gigNoteCount < 1
    ) {
      setLiveSummaryMessage(
        t("liveSummaryReport.nothingToSave", {
          defaultValue:
            "Raporttia ei tallennettu, koska biisitoiveita, settilistaa tai keikkamuistia ei ole vielä.",
        })
      );
      return;
    }

    const dateLabel = new Date().toLocaleDateString(adminLocale);
    const cleanSetlistTitle = String(setlistTitle || "").trim();
    const reportTitle =
      cleanSetlistTitle ||
      `${setlistOccasion
        ? translateAdminLabel(setlistOccasion)
        : t("liveSummaryReport.gigFallback", {
            defaultValue: "Keikka",
          })} ${dateLabel}`;

    // Raportin tallennushetki toimii tämän keikkaraportin päätepisteenä.
    const reportSavedAt = new Date().toISOString();

    const gigDurationMinutes = liveSummary.currentGigStartedAt
      ? getGigDurationMinutes(
          liveSummary.currentGigStartedAt,
          reportSavedAt
        )
      : null;

    const gigDurationLabel =
      formatGigDuration(gigDurationMinutes);

    const reportText = [
      liveSummary.summaryText,
      "",
      t("liveSummaryReport.savedAt", {
        date: formatGigDateTime(
          reportSavedAt,
          adminLocale
        ),
        defaultValue:
          "Raportti tallennettu: {{date}}.",
      }),
      ...(gigDurationLabel
        ? [
            t("liveSummaryReport.durationAtSave", {
              duration: gigDurationLabel,
              defaultValue:
                "Keikan kesto raportin tallennushetkellä: {{duration}}.",
            }),
          ]
        : []),
    ].join("\n");

    setIsSavingLiveSummary(true);
    setLiveSummaryMessage("");

    try {
      const response = await fetchWithCredentials(getGigReportsApiUrl(slug), {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          title: reportTitle,
          occasion: setlistOccasion || "",
          setlistTitle: cleanSetlistTitle,
          reportText,

          summary: {
            title: reportTitle,
            performerSlug: slug,

            // Keikkasession aikarajat.
            currentGigStartedAt:
              liveSummary.currentGigStartedAt || null,
            current_gig_started_at:
              liveSummary.currentGigStartedAt || null,

            reportSavedAt,
            report_saved_at: reportSavedAt,

            gigDurationMinutes,
            gig_duration_minutes: gigDurationMinutes,
            gigDurationLabel,

            activeSetlistTitle:
              liveSummary.activeSetlistTitle || "",
            activeSetlistOccasion:
              liveSummary.activeSetlistOccasion || "",

            createdLabel: formatGigDateTime(reportSavedAt, adminLocale),
            reportMode: liveSummary.reportMode,
            requestChannelActive:
              liveSummary.requestChannelActive,
            gigNotesSnapshot:
              liveSummary.gigNotesSnapshot,
          },

          metrics: {
            ...liveSummary.metrics,

            reportSavedAt,
            report_saved_at: reportSavedAt,

            gigDurationMinutes,
            gig_duration_minutes: gigDurationMinutes,
          },
          topRequests: liveSummary.topSongs,
          playedSongs: liveSummary.topPlayedSongs,
          freeRequests: liveSummary.topFreeRequests,
          activeRequesters: liveSummary.topRequesters,
          requestSnapshot: liveSummary.requestSnapshot,
          setlistSnapshot: generatedSetlist,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
            t("liveSummaryReport.saveFailed", {
              defaultValue:
                "Keikkaraportin tallennus epäonnistui.",
            })
        );
      }

      setSavedGigReports((currentReports) => [
        data.item,
        ...currentReports.filter(
          (report) => String(report.id) !== String(data.item.id)
        ),
      ].slice(0, 50));

      setLiveSummaryMessage(
        t("liveSummaryReport.saved", {
          defaultValue:
            "Keikkaraportti tallennettu.",
        })
      );
    } catch (error) {
      console.error("Keikkaraportin tallennus epäonnistui:", error);
      setLiveSummaryMessage(
        getLocalizedAdminError(
          error,
          "liveSummaryReport.saveFailed",
          "Keikkaraportin tallennus epäonnistui."
        )
      );
    } finally {
      setIsSavingLiveSummary(false);
    }
  }

  function deleteGigReport(reportId) {
    if (!reportId) {
      setLiveSummaryMessage(
        t("liveSummaryReport.deleteMissing", {
          defaultValue:
            "Raportin poisto ei onnistu. Raportti puuttuu.",
        })
      );
      return;
    }

    // Avataan Paranoidin oma vahvistusmodaali.
    setGigReportToDelete(reportId);
  }

  function closeDeleteGigReportModal() {
    if (isDeletingGigReport) {
      return;
    }

    setGigReportToDelete(null);
  }

  async function confirmDeleteGigReport() {
    const slug = String(effectivePerformerSlug || "").trim();
    const reportId = gigReportToDelete;

    if (!slug || !reportId) {
      setLiveSummaryMessage(
        t("liveSummaryReport.deleteContextMissing", {
          defaultValue:
            "Raportin poisto ei onnistu. Esiintyjä tai raportti puuttuu.",
        })
      );
      setGigReportToDelete(null);
      return;
    }

    setIsDeletingGigReport(true);
    setLiveSummaryMessage("");

    try {
      const response = await fetchWithCredentials(
        `${getGigReportsApiUrl(slug)}/${encodeURIComponent(reportId)}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.ok === false) {
        throw new Error(
          data?.error ||
            t("liveSummaryReport.deleteFailed", {
              defaultValue:
                "Keikkaraportin poisto epäonnistui.",
            })
        );
      }

      setSavedGigReports((currentReports) =>
        currentReports.filter(
          (report) => String(report.id) !== String(reportId)
        )
      );

      setGigReportToDelete(null);

      setLiveSummaryMessage(
        t("liveSummaryReport.deleted", {
          defaultValue:
            "Keikkaraportti poistettu. Nykyinen keikkatilanne säilyi ennallaan.",
        })
      );
    } catch (error) {
      console.error("Keikkaraportin poisto epäonnistui:", error);
      setLiveSummaryMessage(
        getLocalizedAdminError(
          error,
          "liveSummaryReport.deleteFailed",
          "Keikkaraportin poisto epäonnistui."
        )
      );
    } finally {
      setIsDeletingGigReport(false);
    }
  }

  function startNewGig() {
    setIsStartNewGigModalOpen(true);
  }

  function closeStartNewGigModal() {
    if (isStartingNewGig) {
      return;
    }

    setIsStartNewGigModalOpen(false);
  }

async function confirmStartNewGig() {
  setIsStartingNewGig(true);
  setLiveSummaryMessage("");

  try {
    // Tämä hetki määrittää uuden keikkasession tilastojen alkupisteen.
    const newGigStartedAt = new Date().toISOString();

    await Promise.all(
      requests.map((request) =>
        deleteSongRequest(
          request.id,
          effectivePerformerSlug
        )
      )
    );

    setRaffleWinner(null);
    setRaffleError("");
    setRandomSong(null);
    setRandomMessage("");

    // Uusi keikka ei peri edellisen keikan aktiivista settilistaa.
    // Tallennetaan aloitusaika ja tyhjennetään settilista samalla
    // settings-pyynnöllä, jotta backendin keikkasessio pysyy yhtenäisenä.
    await updateParanoidiSettings({
      currentGigStartedAt: newGigStartedAt,
      currentGigSetlistId: null,
    });

    restoredCurrentGigSetlistIdRef.current = null;

    // Backend on nyt päivitetty, joten voidaan tyhjentää myös
    // nykyisen selaimen settilistatilat.
    resetCurrentSetlistState();

    await refreshRequests();

    setIsStartNewGigModalOpen(false);

    setLiveSummaryMessage(
      t("liveSummaryReport.newGigStarted", {
        date: formatGigDateTime(
          newGigStartedAt,
          adminLocale
        ),
        defaultValue:
          "Uusi keikka aloitettu {{date}}. Nykyiset biisitoiveet poistettiin, aktiivinen settilista suljettiin ja arvonta nollattiin. Tallennetut raportit, biisikirjasto ja Keikkamuisti säilyivät.",
      })
    );
  } catch (error) {
    console.error(
      "Uuden keikan aloitus epäonnistui:",
      error
    );

    setLiveSummaryMessage(
      getLocalizedAdminError(
        error,
        "liveSummaryReport.newGigFailed",
        "Uuden keikan aloitus epäonnistui."
      )
    );
  } finally {
    setIsStartingNewGig(false);
  }
}

    const setlistSongEntries = useMemo(() => {
      return generatedSetlist.flatMap((set, setIndex) =>
        set.songs.map((song, songIndex) => {
          const freshLibrarySong = findMatchingLibrarySong(
            librarySongs,
            song
          );

    const preservedManualNote =
      getSetlistSongManualNote(song);

    const preservedGigNoteIds =
      getSetlistSongGigNoteIds(song);

    const mergedSong = freshLibrarySong
      ? {
          ...song,
          ...freshLibrarySong,

          // Lukutilaan päivitetään tuore biisikirjastodata,
          // mutta setin oma käsin kirjoitettu muistiinpano
          // ja Keikkamuisti-ID-linkit säilyvät.
          setlistManualNote: preservedManualNote,
          setlist_manual_note: preservedManualNote,

          // Legacy-yhteensopivuuskentissä pidetään vain oma teksti.
          setlistNote: preservedManualNote,
          setlist_note: preservedManualNote,

          setlistGigNoteIds: preservedGigNoteIds,
          setlist_gig_note_ids: preservedGigNoteIds,
        }
      : song;

      return {
        setTitle: t("setlistDynamic.setTitle", {
          number: setIndex + 1,
          defaultValue: `Setti ${setIndex + 1}`,
        }),
        setIndex,
        songIndex,
        song: mergedSong,
      };
    })
  );
}, [
  generatedSetlist,
  librarySongs,
  t,
  i18n.resolvedLanguage,
]);

const activeSetlistSongIndex = activeSetlistSong
  ? setlistSongEntries.findIndex(
      (entry) =>
        entry.setIndex === activeSetlistSong.setIndex &&
        entry.songIndex === activeSetlistSong.songIndex
    )
  : -1;

const activeSetlistSongEntry =
  activeSetlistSongIndex >= 0
    ? setlistSongEntries[activeSetlistSongIndex]
    : null;

// Prompterissa/lukutilassa näytettävä muistiinpano rakennetaan aina
// biisin oman tekstin ja linkitettyjen Keikkamuistien nykyisestä datasta.
// Näin Keikkamuistin muutos näkyy heti ilman biisikohtaista tekstikopiota.
const activeSetlistSongNote = activeSetlistSongEntry
  ? buildSetlistSongNote(
      activeSetlistSongEntry.song,
      gigNotes,
      getLocalizedGigNoteTypeLabel
    )
  : "";

useEffect(() => {
  const shouldLockScroll =
    Boolean(activeSetlistSongEntry) || isSetlistSongEditOpen;

  if (!shouldLockScroll) {
    return;
  }

  const originalBodyOverflow = document.body.style.overflow;
  const originalHtmlOverflow = document.documentElement.style.overflow;

  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";

  return () => {
    document.body.style.overflow = originalBodyOverflow;
    document.documentElement.style.overflow = originalHtmlOverflow;
  };
}, [activeSetlistSongEntry, isSetlistSongEditOpen]);

async function requestSetlistReaderWakeLock() {
  if (!activeSetlistSongEntry) {
    return;
  }

  if (!("wakeLock" in navigator)) {
    console.warn("Screen Wake Lock ei ole käytettävissä tässä selaimessa.");
    return;
  }

  try {
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      return;
    }

    wakeLockRef.current = await navigator.wakeLock.request("screen");

    wakeLockRef.current.addEventListener("release", () => {
      wakeLockRef.current = null;
    });
  } catch (error) {
    console.warn("Screen Wake Lock epäonnistui:", error);
    wakeLockRef.current = null;
  }
}

async function releaseSetlistReaderWakeLock() {
  try {
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      await wakeLockRef.current.release();
    }
  } catch (error) {
    console.warn("Screen Wake Lock vapautus epäonnistui:", error);
  } finally {
    wakeLockRef.current = null;
  }
}

useEffect(() => {
  if (!activeSetlistSongEntry) {
    releaseSetlistReaderWakeLock();
    return;
  }

  requestSetlistReaderWakeLock();

  function handleVisibilityChange() {
    if (document.visibilityState === "visible" && activeSetlistSongEntry) {
      requestSetlistReaderWakeLock();
    }
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    releaseSetlistReaderWakeLock();
  };
}, [activeSetlistSongEntry]);

function openSetlistSongView(setIndex, songIndex) {
  const adminRoot = document.getElementById("top");

  const activeAdminTheme = adminRoot?.closest(".theme-dark")
    ? "dark"
    : "light";

  let nextReaderTheme = activeAdminTheme;

  try {
    const savedTheme = window.localStorage.getItem(
      "paranoidi:setlist-reader-theme"
    );

    if (["dark", "light", "contrast"].includes(savedTheme)) {
      nextReaderTheme = savedTheme;
    }
  } catch {
    // Käytetään hallinnan nykyistä teemaa.
  }

  setSetlistReaderTheme(nextReaderTheme);
  setActiveSetlistSong({ setIndex, songIndex });
}

function closeSetlistSongView() {
  setIsSetlistNotePanelOpen(false);
  setIsSetlistSongEditOpen(false);
  setSetlistSongEditNotes("");
  setSetlistSongEditMessage("");
  setActiveSetlistSong(null);
}

function markActiveSetlistSongPerformanceStatus(nextStatus) {
  const entry = activeSetlistSongEntry;

  if (!entry) {
    return;
  }

  setGeneratedSetlist((currentSets) =>
    currentSets.map((set, currentSetIndex) => {
      if (currentSetIndex !== entry.setIndex) {
        return set;
      }

      return {
        ...set,
        songs: set.songs.map((song, currentSongIndex) => {
          if (currentSongIndex !== entry.songIndex) {
            return song;
          }

          return {
            ...song,
            performanceStatus: nextStatus,
            performance_status: nextStatus,
          };
        }),
      };
    })
  );

  setSetlistMessage(
    nextStatus === "played"
      ? t("setlistReader.markedPlayed", {
          defaultValue:
            "Biisi merkitty soitetuksi. Muista tallentaa setti, jos haluat merkinnän säilyvän setissä.",
        })
      : nextStatus === "skipped"
        ? t("setlistReader.markedSkipped", {
            defaultValue:
              "Biisi merkitty ei soitetuksi. Muista tallentaa setti, jos haluat merkinnän säilyvän setissä.",
          })
        : t("setlistReader.performanceCleared", {
            defaultValue:
              "Biisin keikkamerkintä tyhjennetty.",
          })
  );
}

function openActiveSetlistSongEditor() {
  if (!activeSetlistSongEntry?.song) {
    return;
  }

  setIsPrompterRunning(false);
  setSetlistSongEditNotes(
  getReadableChordProNotes(activeSetlistSongEntry.song, t) ||
     getSongNotes(activeSetlistSongEntry.song)
  );
  setSetlistSongEditMessage("");
  setIsSetlistSongEditOpen(true);
}

function closeActiveSetlistSongEditor() {
  setIsSetlistSongEditOpen(false);
  setSetlistSongEditNotes("");
  setSetlistSongEditMessage("");
}

async function saveActiveSetlistSongEdits() {
  const entry = activeSetlistSongEntry;
  const song = entry?.song;
  const songId = song?.id;
  const slug = String(effectivePerformerSlug || "").trim();

  if (!entry || !song || !songId || !slug) {
    setSetlistSongEditMessage(
      t("setlistReader.editMissingContext", {
        defaultValue:
          "Biisin tallennus ei onnistu. Esiintyjä tai biisi puuttuu.",
      })
    );
    return;
  }

  const apiBaseUrl = getAdminApiBaseUrl();
  const nextNotes = buildSetlistReaderNotes(song, setlistSongEditNotes);

  const payload = {
    title: song.title || "",
    artist: song.artist || "",
    decade: song.decade || song.year || "",
    year: song.year || null,
    genre: song.genre || "",
    language: song.language || "unknown",
    mood: song.mood || "",
    isRequestable: isSongRequestable(song),
    is_requestable: isSongRequestable(song),
    occasionTags: song.occasionTags || song.occasion_tags || [],
    occasion_tags: song.occasion_tags || song.occasionTags || [],
    notes: nextNotes,
  };

  setIsSetlistSongEditSaving(true);
  setSetlistSongEditMessage("");

  try {
    const response = await fetchWithCredentials(
      `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(songId)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(
        data.error ||
          t("setlistReader.saveSongFailed", {
            defaultValue:
              "Biisin tallennus epäonnistui.",
          })
      );
    }

const updatedSong = {
  ...song,
  ...(data.item || data.song || {}),
  notes: nextNotes,
};

setLibrarySongs((currentSongs) =>
  currentSongs.map((currentSong) =>
    String(currentSong.id) === String(songId)
      ? {
          ...currentSong,
          ...updatedSong,
          notes: nextNotes,
        }
      : currentSong
  )
);

setGeneratedSetlist((currentSets) =>
  currentSets.map((set) => ({
    ...set,
    songs: set.songs.map((currentSong) =>
      String(currentSong.id) === String(songId)
       ? {
        ...currentSong,
        ...updatedSong,
        notes: nextNotes,

        // Sanat ja soinnut päivittyvät biisikirjastoon,
        // mutta tämän settilistapaikan oma muistiinpano säilyy.
        setlistManualNote:
          getSetlistSongManualNote(currentSong),
        setlist_manual_note:
          getSetlistSongManualNote(currentSong),

        // Legacy-kentissä pidetään vain biisin oma teksti.
        setlistNote:
          getSetlistSongManualNote(currentSong),
        setlist_note:
          getSetlistSongManualNote(currentSong),

        // Keikkamuisti säilyy vain ID-linkkeinä.
        setlistGigNoteIds:
          getSetlistSongGigNoteIds(currentSong),
        setlist_gig_note_ids:
          getSetlistSongGigNoteIds(currentSong),
      }
        : currentSong
    ),
  }))
);

setSetlistSongEditMessage(
  t("setlistReader.songSaved", {
    defaultValue: "Biisi tallennettu.",
  })
);

setSetlistMessage(
  t("setlistReader.lyricsUpdated", {
    defaultValue:
      "Biisin sanat ja soinnut päivitetty.",
  })
);
closeActiveSetlistSongEditor();

window.requestAnimationFrame(() => {
  lyricsScrollRef.current?.scrollTo({
    top: 0,
    behavior: "auto",
  });
});

  } catch (error) {
    console.error("Lukutilan biisimuokkaus epäonnistui:", error);
    setSetlistSongEditMessage(
      getLocalizedAdminError(
        error,
        "setlistReader.saveSongFailed",
        "Biisin tallennus epäonnistui."
      )
    );
  } finally {
    setIsSetlistSongEditSaving(false);
  }
}

function moveSetlistSongView(direction) {
  if (activeSetlistSongIndex < 0) {
    return;
  }

  const nextIndex = activeSetlistSongIndex + direction;
  const nextEntry = setlistSongEntries[nextIndex];

  if (!nextEntry) {
    return;
  }

  setActiveSetlistSong({
    setIndex: nextEntry.setIndex,
    songIndex: nextEntry.songIndex,
  });
}

function markActiveSetlistSongPlayedAndMoveNext() {
  markActiveSetlistSongPerformanceStatus("played");

  if (
    activeSetlistSongIndex >= 0 &&
    activeSetlistSongIndex < setlistSongEntries.length - 1
  ) {
    moveSetlistSongView(1);
  }
}

function markActiveSetlistSongSkippedAndMoveNext() {
  markActiveSetlistSongPerformanceStatus("skipped");

  if (
    activeSetlistSongIndex >= 0 &&
    activeSetlistSongIndex < setlistSongEntries.length - 1
  ) {
    moveSetlistSongView(1);
  }
}
function handleSetlistReaderTouchStart(event) {
  const target = event.target;

  if (
    target instanceof Element &&
    target.closest(
      "button, input, textarea, select, a, [contenteditable='true']"
    )
  ) {
    setlistSwipeStartRef.current = null;
    return;
  }

  if (event.touches.length !== 1) {
    setlistSwipeStartRef.current = null;
    return;
  }

  const touch = event.touches[0];

  setlistSwipeStartRef.current = {
    x: touch.clientX,
    y: touch.clientY,
  };
}

function handleSetlistReaderTouchEnd(event) {
  const start = setlistSwipeStartRef.current;
  const touch = event.changedTouches?.[0];

  setlistSwipeStartRef.current = null;

  if (!start || !touch) {
    return;
  }

  const deltaX = touch.clientX - start.x;
  const deltaY = touch.clientY - start.y;

  const horizontalDistance = Math.abs(deltaX);
  const verticalDistance = Math.abs(deltaY);

  // Normaali pystyscrollaus ei vaihda biisiä.
  // Vaaditaan vähintään 70 px selkeä vaakapyyhkäisy.
  if (
    horizontalDistance < 70 ||
    horizontalDistance <= verticalDistance * 1.25
  ) {
    return;
  }

  if (deltaX < 0) {
    moveSetlistSongView(1);
    return;
  }

  moveSetlistSongView(-1);
}
function updatePrompterSpeed(nextSpeed) {
  const safeSpeed = Math.min(12, Math.max(1, Number(nextSpeed) || 3));

  setPrompterSpeed(safeSpeed);

  try {
    window.localStorage.setItem(
      "paranoidi:setlist-reader-prompter-speed",
      String(safeSpeed)
    );
  } catch {
    // Nopeus toimii silti tämän istunnon ajan.
  }
}

function updateSetlistReaderFontSize(nextFontSize) {
  const clampedFontSize = Math.min(22, Math.max(10, Number(nextFontSize) || 14));

  setSetlistReaderFontSize(clampedFontSize);

  try {
    window.localStorage.setItem(
      "paranoidi:setlist-reader-font-size",
      String(clampedFontSize)
    );
  } catch {
    // Fonttikoko toimii silti tämän istunnon ajan, vaikka tallennus epäonnistuisi.
  }
}

function updateSetlistReaderTheme(nextTheme) {
  const safeTheme = ["dark", "light", "contrast"].includes(nextTheme)
    ? nextTheme
    : "light";

  setSetlistReaderTheme(safeTheme);

  try {
    window.localStorage.setItem(
      "paranoidi:setlist-reader-theme",
      safeTheme
    );
  } catch {
    // Teema toimii silti tämän istunnon ajan.
  }
}

function updateSetlistReaderChordsVisible(nextValue) {
  const safeValue = Boolean(nextValue);

  setIsSetlistReaderChordsVisible(safeValue);

  try {
    window.localStorage.setItem(
      "paranoidi:setlist-reader-chords-visible",
      String(safeValue)
    );
  } catch {
    // Asetus toimii silti tämän istunnon ajan, vaikka tallennus epäonnistuisi.
  }
}

function updateSetlistReaderTextAlign(nextValue) {
  const safeValue = nextValue === "center" ? "center" : "left";

  setSetlistReaderTextAlign(safeValue);

  try {
    window.localStorage.setItem("paranoidi:setlist-reader-text-align", safeValue);
  } catch {
    // Asetus toimii silti tämän istunnon ajan, vaikka tallennus epäonnistuisi.
  }
}

function resetPrompterScroll() {
  setIsPrompterRunning(false);
  prompterRemainderRef.current = 0;

  lyricsScrollRef.current?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

useEffect(() => {
  setIsPrompterRunning(false);
  setIsSetlistNotePanelOpen(false);

  const timer = window.setTimeout(() => {
    lyricsScrollRef.current?.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, 50);

  return () => window.clearTimeout(timer);
}, [activeSetlistSong?.setIndex, activeSetlistSong?.songIndex]);

useEffect(() => {
  if (!isPrompterRunning || !activeSetlistSongEntry) {
    return undefined;
  }

  let frameId = 0;
  let previousTimestamp = 0;

  function tick(timestamp) {
    const element = lyricsScrollRef.current;

    if (!element) {
      frameId = window.requestAnimationFrame(tick);
      return;
    }

    if (!previousTimestamp) {
      previousTimestamp = timestamp;
    }

    const deltaSeconds = (timestamp - previousTimestamp) / 1000;
    previousTimestamp = timestamp;

    const maxScrollTop = element.scrollHeight - element.clientHeight;

    if (element.scrollTop >= maxScrollTop - 2) {
      setIsPrompterRunning(false);
      return;
    }

    const pixelsToMove =
        Number(prompterSpeed) * deltaSeconds + prompterRemainderRef.current;

      const wholePixels = Math.floor(pixelsToMove);

      prompterRemainderRef.current = pixelsToMove - wholePixels;

      if (wholePixels > 0) {
        element.scrollTop += wholePixels;
      }

    frameId = window.requestAnimationFrame(tick);
  }

  frameId = window.requestAnimationFrame(tick);

  return () => window.cancelAnimationFrame(frameId);
}, [isPrompterRunning, prompterSpeed, activeSetlistSongEntry]);

  useEffect(() => {
    if (!activeSetlistSongEntry) {
      return undefined;
    }

    function handleSetlistReaderKeyDown(event) {
      const target = event.target;

      const isInteractiveTarget =
        target instanceof Element &&
        Boolean(
          target.closest(
            "button, input, textarea, select, a, [contenteditable='true']"
          )
        );

      if (event.key === "Escape") {
        event.preventDefault();

        if (isSetlistSongEditOpen) {
          setIsSetlistSongEditOpen(false);
          setSetlistSongEditNotes("");
          setSetlistSongEditMessage("");
          return;
        }

        if (isSetlistNotePanelOpen) {
          setIsSetlistNotePanelOpen(false);
          return;
        }

        setIsPrompterRunning(false);
        setIsSetlistNotePanelOpen(false);
        setIsSetlistSongEditOpen(false);
        setSetlistSongEditNotes("");
        setSetlistSongEditMessage("");
        setActiveSetlistSong(null);
        return;
      }

      if (
        isInteractiveTarget ||
        isSetlistSongEditOpen ||
        isSetlistNotePanelOpen
      ) {
        return;
      }

      if (event.key === "ArrowRight") {
        const nextEntry =
          setlistSongEntries[activeSetlistSongIndex + 1];

        if (nextEntry) {
          event.preventDefault();

          setActiveSetlistSong({
            setIndex: nextEntry.setIndex,
            songIndex: nextEntry.songIndex,
          });
        }

        return;
      }

      if (event.key === "ArrowLeft") {
        const previousEntry =
          setlistSongEntries[activeSetlistSongIndex - 1];

        if (previousEntry) {
          event.preventDefault();

          setActiveSetlistSong({
            setIndex: previousEntry.setIndex,
            songIndex: previousEntry.songIndex,
          });
        }

        return;
      }

      if (event.code === "Space") {
        if (hasChordProNotes(activeSetlistSongEntry.song)) {
          event.preventDefault();
          setIsPrompterRunning((current) => !current);
        }

        return;
      }

      if (event.key === "Home") {
        event.preventDefault();

        setIsPrompterRunning(false);
        prompterRemainderRef.current = 0;

        lyricsScrollRef.current?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }

    window.addEventListener(
      "keydown",
      handleSetlistReaderKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleSetlistReaderKeyDown
      );
    };
  }, [
    activeSetlistSongEntry,
    activeSetlistSongIndex,
    setlistSongEntries,
    isSetlistSongEditOpen,
    isSetlistNotePanelOpen,
  ]);
  const activeSetlistReaderTheme =
    SETLIST_READER_THEMES[setlistReaderTheme] || SETLIST_READER_THEMES.dark;

  return (
    <>

    {activeInfoModal &&
  createPortal(
    <div className="fixed inset-0 z-[170000] flex items-center justify-center bg-black/75 px-4 text-white backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-[1.5rem] border border-white/10 bg-zinc-950 p-5 text-left shadow-2xl shadow-black/80">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-red-400">
              {activeInfoModal.title}
            </p>

            <p className="mt-3 text-sm font-semibold leading-7 text-zinc-300">
              {activeInfoModal.text}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setActiveInfoModal(null)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-sm font-black text-white transition hover:bg-white/10"
            aria-label={t("text001")}
          >
            <FiX className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

  {gigReportToDelete &&
  createPortal(
    <div
      className="fixed inset-0 z-[175000] flex items-center justify-center bg-black/75 px-4 text-white backdrop-blur-sm"
      onClick={closeDeleteGigReportModal}
    >
      <div
        className="w-full max-w-lg rounded-[1.75rem] border border-[#ef4444]/25 bg-[#050605] p-5 text-left shadow-2xl shadow-black/80 sm:p-6"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-gig-report-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#ef4444]">
              {t("text002")}
            </p>

            <h3
              id="delete-gig-report-title"
              className="mt-3 text-2xl font-black tracking-[-0.045em] text-white"
            >
              {t("text003")}
            </h3>
          </div>

          <button
            type="button"
            onClick={closeDeleteGigReportModal}
            disabled={isDeletingGigReport}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("text004")}
          >
            <FiX className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 text-sm font-semibold leading-7 text-zinc-300">
          <p>
            {t("text005")}
          </p>

          <p className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-emerald-100">
            {t("text006")}
          </p>
        </div>

        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={closeDeleteGigReportModal}
            disabled={isDeletingGigReport}
            className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-black text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("text007")}
          </button>

          <button
            type="button"
            onClick={confirmDeleteGigReport}
            disabled={isDeletingGigReport}
            className="rounded-full border border-[#ef4444]/40 bg-[#ef4444] px-5 py-3 text-sm font-black text-white transition hover:bg-[#dc2626] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isDeletingGigReport ? t("text201") : t("text202")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

  {isStartNewGigModalOpen &&
  createPortal(
    <div
      className="fixed inset-0 z-[175000] flex items-center justify-center bg-black/75 px-4 text-white backdrop-blur-sm"
      onClick={closeStartNewGigModal}
    >
      <div
        className="w-full max-w-lg rounded-[1.75rem] border border-[#ef4444]/25 bg-[#050605] p-5 text-left shadow-2xl shadow-black/80 sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#ef4444]">
              {t("text008")}
            </p>

            <h3 className="mt-3 text-2xl font-black tracking-[-0.045em] text-white">
              {t("text009")}
            </h3>
          </div>

          <button
            type="button"
            onClick={closeStartNewGigModal}
            disabled={isStartingNewGig}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("text004")}
          >
            <FiX className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 text-sm font-semibold leading-7 text-zinc-300">
          <p>
            {t("text010")}
          </p>

          <p className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-amber-100">
            {t("text011")}
          </p>

          <p className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-emerald-100">
            {t("text012")}
          </p>
        </div>

        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={closeStartNewGigModal}
            disabled={isStartingNewGig}
            className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-black text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("text007")}
          </button>

          <button
            type="button"
            onClick={confirmStartNewGig}
            disabled={isStartingNewGig}
            className="rounded-full border border-[#ef4444]/40 bg-[#ef4444] px-5 py-3 text-sm font-black text-white transition hover:bg-[#dc2626] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isStartingNewGig ? t("text203") : t("text008")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

  {isSetlistSongEditOpen &&
      activeSetlistSongEntry &&
      createPortal(
        <div className="fixed inset-0 z-[170000] overscroll-contain bg-black/85 px-3 py-4 text-white backdrop-blur-sm sm:px-6">
          <div className="mx-auto flex h-full max-w-5xl flex-col overflow-hidden rounded-[1.5rem] border border-[#ef4444]/25 bg-[#050605] shadow-2xl shadow-black/70">
            <div className="shrink-0 border-b border-white/10 px-4 py-4 sm:px-6">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#ef4444] sm:text-xs">
                {t("text013")}
              </p>

              <h3 className="mt-2 break-words text-2xl font-black tracking-[-0.04em] text-white sm:text-3xl">
                {activeSetlistSongEntry.song.title}
              </h3>

              {activeSetlistSongEntry.song.artist && (
                <p className="mt-1 text-sm font-semibold text-zinc-400">
                  {activeSetlistSongEntry.song.artist}
                </p>
              )}

              <p className="mt-3 text-xs font-semibold leading-6 text-zinc-400">
                {t("text014")}
              </p>
            </div>

            <div className="min-h-0 flex-1 p-4 sm:p-6">
              <label className="flex h-full min-h-[65vh] flex-col gap-3 text-xs font-black uppercase tracking-[0.16em] text-zinc-400">
                {t("text015")}
                <textarea
                  value={setlistSongEditNotes}
                  onChange={(event) => setSetlistSongEditNotes(event.target.value)}
                  className="min-h-[60vh] flex-1 resize-none rounded-[1.25rem] border border-white/10 bg-black/60 p-4 font-mono text-sm normal-case leading-7 tracking-normal text-white outline-none transition placeholder:text-zinc-600 focus:border-[#ef4444]/60 sm:text-base"
                  placeholder={t("text016")}
                />
              </label>
            </div>

            <div className="shrink-0 border-t border-white/10 px-4 py-4 sm:px-6">
              {setlistSongEditMessage && (
                <p className="mb-3 text-sm font-semibold text-zinc-300">
                  {translateAdminLabel(setlistSongEditMessage)}
                </p>
              )}

              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={closeActiveSetlistSongEditor}
                  disabled={isSetlistSongEditSaving}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("text007")}
                </button>

                <button
                  type="button"
                  onClick={saveActiveSetlistSongEdits}
                  disabled={isSetlistSongEditSaving}
                  className="rounded-full border border-[#ef4444]/35 bg-[#ef4444]/15 px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-[#fecaca] transition hover:bg-[#ef4444]/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSetlistSongEditSaving ? t("text204") : t("text205")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isSetlistNotePanelOpen &&
  activeSetlistSongEntry &&
  activeSetlistSongNote &&
  createPortal(
    <div
      className="fixed inset-0 z-[180000] flex items-end bg-black/70 px-3 pb-3 pt-16 text-white backdrop-blur-sm lg:hidden"
      onClick={() => setIsSetlistNotePanelOpen(false)}
    >
      <div
        className="w-full overflow-hidden rounded-[1.5rem] border border-[#ef4444]/25 bg-[#050605] shadow-2xl shadow-black/80"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ef4444]">
              {t("text017")}
            </p>

            <p className="mt-1 truncate text-sm font-black text-white">
              {activeSetlistSongEntry.song.title}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsSetlistNotePanelOpen(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-base font-black text-white transition hover:bg-white/10"
            aria-label={t("text018")}
          >
            <FiX className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[48svh] overflow-auto px-4 py-4">
          <p className="whitespace-pre-wrap break-words text-base font-semibold leading-8 text-zinc-100">
            {activeSetlistSongNote}
          </p>
        </div>
      </div>
    </div>,
    document.body
  )}

   {activeSetlistSongEntry &&
      createPortal(
        <div
          className={[
            "setlist-reader fixed inset-0 z-[160000] overscroll-contain",
            isSetlistReaderControlsHidden
              ? "p-0"
              : "lg:px-6 lg:py-4 lg:backdrop-blur-sm",
          ].join(" ")}
          style={{
            "--reader-background":
              activeSetlistReaderTheme.fullscreenBackground,
            "--reader-shell": activeSetlistReaderTheme.shell,
            "--reader-surface": activeSetlistReaderTheme.surface,
            "--reader-toolbar": activeSetlistReaderTheme.toolbar,
            "--reader-navigation":
              activeSetlistReaderTheme.navigation,

            "--reader-panel-border":
              activeSetlistReaderTheme.panelBorder,
            "--reader-divider": activeSetlistReaderTheme.divider,

            "--reader-text": activeSetlistReaderTheme.body,
            "--reader-muted": activeSetlistReaderTheme.muted,
            "--reader-subtle": activeSetlistReaderTheme.subtle,

            "--reader-accent": activeSetlistReaderTheme.accent,
            "--reader-accent-strong":
              activeSetlistReaderTheme.accentStrong,
            "--reader-highlight-panel":
              activeSetlistReaderTheme.highlightPanel,

            "--reader-button-bg":
              activeSetlistReaderTheme.buttonBackground,
            "--reader-button-border":
              activeSetlistReaderTheme.buttonBorder,
            "--reader-button-text":
              activeSetlistReaderTheme.buttonText,

            "--reader-active-bg":
              activeSetlistReaderTheme.activeBackground,
            "--reader-active-text":
              activeSetlistReaderTheme.activeText,

            "--reader-shadow": activeSetlistReaderTheme.shadow,

            background: activeSetlistReaderTheme.fullscreenBackground,
            color: activeSetlistReaderTheme.body,
          }}
        >
          <div
            className={[
              "setlist-reader-shell relative flex h-[100svh] w-full flex-col overflow-hidden",
              isSetlistReaderControlsHidden
                ? "max-w-none bg-transparent shadow-none"
                : "mx-auto max-w-6xl shadow-2xl lg:h-full lg:rounded-[2rem] lg:border",
            ].join(" ")}
          >
              {isSetlistReaderControlsHidden && (
                <>
                  <div className="fixed left-1/2 top-3 z-[160010] flex w-max max-w-none origin-top -translate-x-1/2 scale-[0.82] flex-nowrap items-center justify-center gap-2 sm:scale-100">
                    <button
                      type="button"
                      onClick={() => setIsPrompterRunning((current) => !current)}
                      disabled={!hasChordProNotes(activeSetlistSongEntry.song)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur transition disabled:cursor-not-allowed disabled:opacity-35"
                      style={{
                        borderColor: "rgba(239,68,68,0.65)",
                        color: "#ffffff",
                        background: isPrompterRunning
                          ? "rgba(239,68,68,0.95)"
                          : "rgba(127,29,29,0.92)",
                      }}
                      aria-label={isPrompterRunning ? t("text323") : t("text324")}
                      title={isPrompterRunning ? t("text323") : t("text324")}
                    >
                      {isPrompterRunning ? (
                        <FiPause className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <FiPlay className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>

                    <div
                      className="inline-flex items-center gap-1 rounded-full border px-1 py-1 shadow-lg backdrop-blur"
                      style={{
                        borderColor: "rgba(239,68,68,0.45)",
                        color: "#ffffff",
                        background: "rgba(127,29,29,0.88)",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => updatePrompterSpeed(prompterSpeed - 1)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                        disabled={Number(prompterSpeed) <= 1}
                        aria-label={t("text019")}
                        title={t("text019")}
                      >
                        <FiMinus className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>

                      <span className="min-w-[2.25rem] text-center text-[10px] font-black uppercase tracking-[0.08em]">
                        {prompterSpeed}
                      </span>

                      <button
                        type="button"
                        onClick={() => updatePrompterSpeed(prompterSpeed + 1)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                        disabled={Number(prompterSpeed) >= 12}
                        aria-label={t("text020")}
                        title={t("text020")}
                      >
                        <FiPlus className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        updateSetlistReaderChordsVisible(!isSetlistReaderChordsVisible)
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur"
                      style={{
                        borderColor: activeSetlistReaderTheme.panelBorder,
                        color: activeSetlistReaderTheme.accent,
                        background: activeSetlistReaderTheme.emptyPanel,
                      }}
                      aria-label={
                        isSetlistReaderChordsVisible ? t("text325") : t("text326")
                      }
                      title={
                        isSetlistReaderChordsVisible ? t("text325") : t("text326")
                      }
                    >
                      {isSetlistReaderChordsVisible ? (
                        <FiEyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <FiEye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        updateSetlistReaderTextAlign(
                          setlistReaderTextAlign === "center" ? "left" : "center"
                        )
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur"
                      style={{
                        borderColor: activeSetlistReaderTheme.panelBorder,
                        color: activeSetlistReaderTheme.accent,
                        background: activeSetlistReaderTheme.emptyPanel,
                      }}
                      aria-label={
                        setlistReaderTextAlign === "center"
                          ? t("text327")
                          : t("text328")
                      }
                      title={
                        setlistReaderTextAlign === "center"
                          ? t("text327")
                          : t("text328")
                      }
                    >
                      {setlistReaderTextAlign === "center" ? (
                        <FiAlignLeft className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <FiAlignCenter className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={resetPrompterScroll}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur"
                      style={{
                        borderColor: activeSetlistReaderTheme.panelBorder,
                        color: activeSetlistReaderTheme.accent,
                        background: activeSetlistReaderTheme.emptyPanel,
                      }}
                      aria-label={t("text021")}
                      title={t("text021")}
                    >
                      <FiArrowUp className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsSetlistReaderControlsHidden(false)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur"
                      style={{
                        borderColor: activeSetlistReaderTheme.panelBorder,
                        color: activeSetlistReaderTheme.accent,
                        background: activeSetlistReaderTheme.emptyPanel,
                      }}
                      aria-label={t("text022")}
                      title={t("text022")}
                    >
                      <FiMinimize2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="fixed bottom-4 left-1/2 z-[160010] flex -translate-x-1/2 items-center justify-center gap-2 sm:grid sm:w-[min(94vw,36rem)] sm:grid-cols-[1fr_auto_auto_auto_1fr]">
                    <button
                      type="button"
                      onClick={() => moveSetlistSongView(-1)}
                      disabled={activeSetlistSongIndex <= 0}
                      className="hidden h-11 items-center justify-center rounded-full border px-4 text-xs font-black shadow-lg backdrop-blur transition disabled:cursor-not-allowed disabled:opacity-55 sm:inline-flex"
                      style={{
                        borderColor: "rgba(254,202,202,0.95)",
                        color: "#ffffff",
                        background: "rgba(69,10,10,0.98)",
                        boxShadow: "0 16px 34px rgba(69,10,10,0.46)",
                      }}
                      aria-label={t("text023")}
                      title={t("text023")}
                    >
                      <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <button
                      type="button"
                      onClick={markActiveSetlistSongPlayedAndMoveNext}
                      className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-full border px-3 text-[10px] font-black uppercase tracking-[0.06em] shadow-lg backdrop-blur transition sm:px-4 sm:text-xs"
                      style={{
                        borderColor:
                          getSetlistSongPerformanceStatus(
                            activeSetlistSongEntry?.song
                          ) === "played"
                            ? "rgba(134,239,172,1)"
                            : "rgba(255,255,255,0.30)",
                        color: "#ffffff",
                        background:
                          getSetlistSongPerformanceStatus(
                            activeSetlistSongEntry?.song
                          ) === "played"
                            ? "rgba(22,101,52,0.98)"
                            : "rgba(39,39,42,0.94)",
                      }}
                      aria-label={t("text024")}
                      title={t("text025")}
                    >
                      {t("text026")}
                    </button>

                    <span
                      className="min-w-[3.6rem] rounded-full border px-3 py-2 text-center text-[10px] font-black uppercase tracking-[0.12em] shadow-lg backdrop-blur"
                      style={{
                        borderColor: "rgba(254,202,202,0.95)",
                        color: "#ffffff",
                        background: "rgba(69,10,10,0.98)",
                        boxShadow: "0 16px 34px rgba(69,10,10,0.46)",
                      }}
                    >
                      {activeSetlistSongIndex + 1} / {setlistSongEntries.length}
                    </span>

                    <button
                      type="button"
                      onClick={markActiveSetlistSongSkippedAndMoveNext}
                      className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-full border px-3 text-[10px] font-black uppercase tracking-[0.06em] shadow-lg backdrop-blur transition sm:px-4 sm:text-xs"
                      style={{
                        borderColor:
                          getSetlistSongPerformanceStatus(
                            activeSetlistSongEntry?.song
                          ) === "skipped"
                            ? "rgba(252,165,165,1)"
                            : "rgba(255,255,255,0.30)",
                        color: "#ffffff",
                        background:
                          getSetlistSongPerformanceStatus(
                            activeSetlistSongEntry?.song
                          ) === "skipped"
                            ? "rgba(153,27,27,0.98)"
                            : "rgba(39,39,42,0.94)",
                      }}
                      aria-label={t("text027")}
                      title={t("text028")}
                    >
                      {t("text029")}
                    </button>

                    <button
                      type="button"
                      onClick={() => moveSetlistSongView(1)}
                      disabled={
                        activeSetlistSongIndex < 0 ||
                        activeSetlistSongIndex >= setlistSongEntries.length - 1
                      }
                      className="hidden h-11 items-center justify-center rounded-full border px-4 text-xs font-black shadow-lg backdrop-blur transition disabled:cursor-not-allowed disabled:opacity-55 sm:inline-flex"
                      style={{
                        borderColor: "rgba(254,202,202,0.95)",
                        color: "#ffffff",
                        background: "rgba(69,10,10,0.98)",
                        boxShadow: "0 16px 34px rgba(69,10,10,0.46)",
                      }}
                      aria-label={t("text030")}
                      title={t("text030")}
                    >
                      <FiChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </>
              )}
            <div
              className={[
                "setlist-reader-header flex shrink-0 items-start justify-between gap-3 border-b px-3 py-3 sm:px-4 lg:p-5",
                isSetlistReaderControlsHidden ? "hidden" : "",
              ].join(" ")}
            >
              <div className="min-w-0">
                <p className="setlist-reader-kicker text-[10px] font-black uppercase tracking-[0.2em] sm:text-xs">
                  {activeSetlistSongEntry.setTitle} ·{" "}
                  {t("songPosition", { number: activeSetlistSongEntry.songIndex + 1 })}
                </p>

                <h3 className="setlist-reader-title mt-1 break-words text-xl font-black tracking-[-0.04em] sm:text-3xl lg:mt-2 lg:text-4xl">
                  {activeSetlistSongEntry.song.title}
                </h3>

                {activeSetlistSongEntry.song.artist && (
                  <p className="setlist-reader-muted mt-0.5 text-sm font-semibold sm:text-base">
                    {activeSetlistSongEntry.song.artist}
                  </p>
                )}

                {activeSetlistSongNote && (
                  <button
                    type="button"
                    onClick={() => setIsSetlistNotePanelOpen(true)}
                    className="setlist-reader-mobile-note mt-3 flex w-full items-center gap-3 rounded-2xl border px-3 py-2 text-left transition lg:hidden"
                  >
                    <span className="setlist-reader-mobile-note-badge shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em]">
                      {t("text032")}
                    </span>

                    <span className="setlist-reader-mobile-note-text min-w-0 flex-1 truncate text-xs font-semibold">
                      {activeSetlistSongNote.split(/\r?\n/)[0]}
                    </span>

                    <span className="setlist-reader-mobile-note-action shrink-0 text-[10px] font-black uppercase tracking-[0.14em]">
                      {t("text033")}
                    </span>
                  </button>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setIsSetlistReaderControlsHidden((current) => !current)
                  }
                  className="setlist-reader-icon-button flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition"
                  aria-label={
                    isSetlistReaderControlsHidden
                      ? t("text329")
                      : t("text330")
                  }
                  title={
                    isSetlistReaderControlsHidden
                      ? t("text329")
                      : t("text330")
                  }
                >
                  {isSetlistReaderControlsHidden ? (
                    <FiMinimize2 className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <FiMaximize2 className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>

              <button
                type="button"
                onClick={closeSetlistSongView}
                className="setlist-reader-icon-button flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition"
                aria-label={t("text034")}
              >
                <FiX className="h-4 w-4" aria-hidden="true" />
              </button>
              </div>
            </div>

              <div
              className={[
                "min-h-0 flex-1 overflow-hidden",
                isSetlistReaderControlsHidden
                  ? "flex flex-col p-0"
                  : activeSetlistSongNote
                    ? "flex flex-col gap-3 p-3 sm:p-4 lg:grid lg:grid-cols-[0.95fr_1.35fr] lg:gap-4 lg:p-5"
                    : "flex flex-col gap-3 p-3 sm:p-4 lg:block lg:p-5",
              ].join(" ")}
            >
                {activeSetlistSongNote && (
                  <aside className="setlist-reader-note-panel hidden min-h-0 overflow-auto rounded-[1.5rem] border p-4 lg:block">
                    <p className="setlist-reader-kicker text-[10px] font-black uppercase tracking-[0.2em] sm:text-xs">
                      {t("text017")}
                    </p>

                    <p className="setlist-reader-body mt-3 whitespace-pre-wrap break-words text-sm font-semibold leading-7 sm:text-base sm:leading-8">
                      {activeSetlistSongNote}
                    </p>
                  </aside>
                )}

                <section
                  ref={lyricsScrollRef}
                  onTouchStart={handleSetlistReaderTouchStart}
                  onTouchEnd={handleSetlistReaderTouchEnd}
                  onTouchCancel={() => {
                    setlistSwipeStartRef.current = null;
                  }}
                  className={[
                    "min-h-0 flex-1 overflow-auto transition-colors duration-200",
                    isSetlistReaderControlsHidden
                      ? "rounded-none border-0 px-4 pb-36 pt-16 sm:px-8 sm:pb-40 sm:pt-20"
                      : "rounded-[1.25rem] border p-3 sm:rounded-[1.5rem] sm:p-4",
                  ].join(" ")}
                  style={{
                    background: activeSetlistReaderTheme.panel,
                    borderColor: activeSetlistReaderTheme.panelBorder,
                    color: activeSetlistReaderTheme.body,
                  }}
                >
                  <div
                    className={[
                      "flex items-center justify-between gap-3",
                      isSetlistReaderControlsHidden ? "hidden" : "",
                    ].join(" ")}
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.2em] sm:text-xs"
                      style={{ color: activeSetlistReaderTheme.accent }}
                    >
                      {t("text035")}
                    </p>

                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateSetlistReaderChordsVisible(!isSetlistReaderChordsVisible)
                        }
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border text-[13px] transition hover:scale-[1.03]"
                        style={{
                          borderColor: activeSetlistReaderTheme.panelBorder,
                          color: activeSetlistReaderTheme.accent,
                          background: activeSetlistReaderTheme.emptyPanel,
                        }}
                        aria-label={
                          isSetlistReaderChordsVisible
                            ? t("text325")
                            : t("text326")
                        }
                        title={
                          isSetlistReaderChordsVisible
                            ? t("text325")
                            : t("text326")
                        }
                      >
                        {isSetlistReaderChordsVisible ? (
                          <FiEyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <FiEye className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          updateSetlistReaderTextAlign(
                            setlistReaderTextAlign === "center" ? "left" : "center"
                          )
                        }
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border text-[13px] transition hover:scale-[1.03]"
                        style={{
                          borderColor: activeSetlistReaderTheme.panelBorder,
                          color: activeSetlistReaderTheme.accent,
                          background: activeSetlistReaderTheme.emptyPanel,
                        }}
                        aria-label={
                          setlistReaderTextAlign === "center"
                            ? t("text327")
                            : t("text328")
                        }
                        title={
                          setlistReaderTextAlign === "center"
                            ? t("text327")
                            : t("text328")
                        }
                      >
                        {setlistReaderTextAlign === "center" ? (
                          <FiAlignLeft className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <FiAlignCenter className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>

                  {hasChordProNotes(activeSetlistSongEntry.song) ? (
                    <div
                      className={[
                        "font-mono",
                        isSetlistReaderControlsHidden ? "mt-0" : "mt-3 lg:mt-4",
                      ].join(" ")}
                    >
                      {renderChordProReader(activeSetlistSongEntry.song, {
                        showChords: isSetlistReaderChordsVisible,
                        theme: activeSetlistReaderTheme,
                        fontSize: setlistReaderFontSize,
                        textAlign: setlistReaderTextAlign,
                        translate: t,
                      })}
                    </div>
                  ) : (
                    <div
                      className="mt-3 flex min-h-[45svh] flex-col justify-center rounded-[1rem] border border-dashed px-4 py-6 text-center sm:min-h-[40svh]"
                      style={{
                        background: activeSetlistReaderTheme.emptyPanel,
                        borderColor: activeSetlistReaderTheme.panelBorder,
                      }}
                    >
                      <p
                        className="text-sm font-black uppercase tracking-[0.14em]"
                        style={{ color: activeSetlistReaderTheme.accent }}
                      >
                        {t("text036")}
                      </p>

                      <p
                        className="mx-auto mt-3 max-w-md text-sm font-semibold leading-7"
                        style={{ color: activeSetlistReaderTheme.body, opacity: 0.74 }}
                      >
                        {t("text037")}
                      </p>

                      <button
                        type="button"
                        onClick={openActiveSetlistSongEditor}
                        disabled={!activeSetlistSongEntry.song?.id}
                        className="mx-auto mt-5 rounded-full border px-5 py-2.5 text-xs font-black uppercase tracking-[0.12em] transition disabled:cursor-not-allowed disabled:opacity-45"
                        style={{
                          borderColor: activeSetlistReaderTheme.accent,
                          color: activeSetlistReaderTheme.accent,
                          background: activeSetlistReaderTheme.emptyPanel,
                        }}
                      >
                        {t("text038")}
                      </button>
                    </div>
                  )}
                </section>
              </div>

            <div
              className={[
                "setlist-reader-toolbar shrink-0 border-t px-2 py-2 sm:px-3 lg:px-5 lg:py-3",
                isSetlistReaderControlsHidden ? "hidden" : "",
              ].join(" ")}
            >
              <div className="grid gap-1.5 lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-3">
                <div className="grid grid-cols-3 gap-1.5 lg:col-span-3">
                  <span className="setlist-reader-subtle col-span-3 text-[10px] font-black uppercase tracking-[0.12em]">
                    {t("text039")}
                  </span>

                  {SETLIST_READER_THEME_OPTIONS.map((option) => {
                    const isActiveTheme = setlistReaderTheme === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateSetlistReaderTheme(option.value)}
                        className={[
                         "setlist-reader-theme-option rounded-full border px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] transition sm:px-3 sm:py-2 sm:text-[11px]",
                          isActiveTheme ? "is-active" : "",
                        ].join(" ")}
                      >
                        {t(option.labelKey)}
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsPrompterRunning((current) => !current)}
                    disabled={!hasChordProNotes(activeSetlistSongEntry.song)}
                    className={[
                      "setlist-reader-tool-button rounded-full border px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] transition disabled:cursor-not-allowed disabled:opacity-35 sm:px-3 sm:py-2 sm:text-[11px]",
                      isPrompterRunning ? "is-active" : "",
                    ].join(" ")}
                  >
                    {isPrompterRunning ? t("text206") : t("text207")}
                  </button>

                  <button
                    type="button"
                    onClick={resetPrompterScroll}
                    disabled={!hasChordProNotes(activeSetlistSongEntry.song)}
                    className="setlist-reader-tool-button rounded-full border px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] transition disabled:cursor-not-allowed disabled:opacity-35 sm:px-3 sm:py-2 sm:text-[11px]"
                  >
                    {t("text040")}
                  </button>

                  <button
                    type="button"
                    onClick={openActiveSetlistSongEditor}
                    disabled={!activeSetlistSongEntry.song?.id}
                    className="setlist-reader-tool-button rounded-full border px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] transition disabled:cursor-not-allowed disabled:opacity-35 sm:px-3 sm:py-2 sm:text-[11px]"
                  >
                    {t("text041")}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={markActiveSetlistSongPlayedAndMoveNext}
                  className={[
                    "setlist-reader-status-button rounded-full border px-5 py-2.5 text-xs font-black uppercase tracking-[0.12em] transition",
                    getSetlistSongPerformanceStatus(activeSetlistSongEntry?.song) === "played"
                      ? "is-played"
                      : "",
                  ].join(" ")}
                >
                  {activeSetlistSongIndex < setlistSongEntries.length - 1
                    ? t("text025")
                    : t("text024")}
                </button>

                <button
                  type="button"
                  onClick={() => markActiveSetlistSongPerformanceStatus("skipped")}
                  className={[
                    "setlist-reader-status-button rounded-full border px-5 py-2.5 text-xs font-black uppercase tracking-[0.12em] transition",
                    getSetlistSongPerformanceStatus(activeSetlistSongEntry?.song) === "skipped"
                      ? "is-skipped"
                      : "",
                  ].join(" ")}
                >
                  {t("text027")}
                </button>

                {getSetlistSongPerformanceStatus(activeSetlistSongEntry?.song) && (
                  <button
                    type="button"
                    onClick={() => markActiveSetlistSongPerformanceStatus("")}
                    className="setlist-reader-tool-button rounded-full border px-5 py-2.5 text-xs font-black uppercase tracking-[0.12em] transition"
                  >
                    {t("text042")}
                  </button>
                )}

                <div className="grid grid-cols-[auto_1fr] items-center gap-1.5">
                  <div className="setlist-reader-font-control grid grid-cols-[auto_1fr_auto] items-center gap-1 rounded-full border px-1.5 py-1">
                    <button
                      type="button"
                      onClick={() => updateSetlistReaderFontSize(setlistReaderFontSize - 1)}
                      disabled={setlistReaderFontSize <= 10}
                      className="setlist-reader-small-button flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-black transition disabled:cursor-not-allowed disabled:opacity-35"
                      aria-label={t("text043")}
                    >
                      {t("text044")}
                    </button>

                    <span className="setlist-reader-muted min-w-[3.25rem] text-center text-[10px] font-black uppercase tracking-[0.08em]">
                      {setlistReaderFontSize}{t("text045")}
                    </span>

                    <button
                      type="button"
                      onClick={() => updateSetlistReaderFontSize(setlistReaderFontSize + 1)}
                      disabled={setlistReaderFontSize >= 22}
                      className="setlist-reader-small-button is-primary flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-black transition disabled:cursor-not-allowed disabled:opacity-35"
                      aria-label={t("text046")}
                    >
                      {t("text047")}
                    </button>
                  </div>

                  <label className="setlist-reader-muted grid min-w-0 grid-cols-[auto_1fr] items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.08em]">
                    <span className="whitespace-nowrap">{t("text048")} {prompterSpeed}</span>
                      <input
                        type="range"
                        min="1"
                        max="12"
                        step="1"
                        value={prompterSpeed}
                        onChange={(event) => updatePrompterSpeed(event.target.value)}
                        disabled={!hasChordProNotes(activeSetlistSongEntry.song)}
                      className="setlist-reader-speed min-w-0 w-full disabled:opacity-35"
                    />
                  </label>
                </div>

                <p className="setlist-reader-subtle hidden text-xs font-semibold lg:block">
                  {t("text048")} {prompterSpeed}/12
                </p>
              </div>
            </div>


            <div
              className={[
                "setlist-reader-navigation grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-t p-3 sm:p-4",
                isSetlistReaderControlsHidden ? "hidden" : "",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => moveSetlistSongView(-1)}
                disabled={activeSetlistSongIndex <= 0}
                className="setlist-reader-navigation-button rounded-full border px-3 py-3 text-xs font-black uppercase tracking-[0.1em] transition disabled:cursor-not-allowed disabled:opacity-35 sm:px-5 sm:text-sm"
              >
                {t("text049")}
              </button>

              <p className="setlist-reader-subtle text-center text-[11px] font-black uppercase tracking-[0.14em] sm:text-xs">
                {activeSetlistSongIndex + 1} / {setlistSongEntries.length}
              </p>

              <button
                type="button"
                onClick={() => moveSetlistSongView(1)}
                disabled={
                  activeSetlistSongIndex < 0 ||
                  activeSetlistSongIndex >= setlistSongEntries.length - 1
                }
                className="setlist-reader-navigation-button is-primary rounded-full border px-3 py-3 text-xs font-black uppercase tracking-[0.1em] transition disabled:cursor-not-allowed disabled:opacity-35 sm:px-5 sm:text-sm"
              >
                {t("text050")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <main
        id="top"
        className="min-h-screen bg-[#ead6c2] text-[#241a16] transition-colors duration-300 theme-dark:bg-[#050706] theme-dark:text-white"
      >
        <Navbar config={config} onLogout={handleLogout} />
          {!activeSetlistSongEntry && <HelpCenter />}

          <section className="px-3 pt-28 pb-12 sm:px-6 sm:pt-28 sm:pb-16">
  <div className="mx-auto max-w-6xl">

    <section className="mb-4 overflow-hidden rounded-[1.75rem] border border-[#5a3824]/10 bg-[#fff8ef]/55 p-4 shadow-2xl shadow-[#3a2b18]/5 theme-dark:border-white/10 theme-dark:bg-white/[0.025] theme-dark:shadow-black/20 sm:p-5">
      <div className="grid gap-4 sm:grid-cols-[88px_minmax(0,1fr)_auto] sm:items-center">
        <div className="h-20 w-20 overflow-hidden rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef] theme-dark:border-white/10 theme-dark:bg-black/40">
          {adminHeroImageUrl ? (
            <img
              src={adminHeroImageUrl}
              alt={performerDisplayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-2xl font-black text-[#5a3824] theme-dark:text-white">
              {performerDisplayName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
            {t("text051")}
          </p>

          <h1 className="mt-1 truncate text-3xl font-black tracking-[-0.05em] text-[#241a16] theme-dark:text-white sm:text-4xl">
            {performerDisplayName}
          </h1>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
                {t("text052")}
              </span>

              <span className="text-sm font-black text-[#241a16] theme-dark:text-white">
                {songLibraryCountLabel}
              </span>
            </div>

            <div className="h-3 overflow-hidden rounded-full border border-[#5a3824]/10 bg-[#fff8ef] theme-dark:border-white/10 theme-dark:bg-black/40">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#ef4444] via-[#fb7185] to-[#fecaca] transition-all duration-700 ease-out"
                style={{ width: `${songLibraryBarPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex sm:justify-end">
          <span className="rounded-full border border-[#5a3824]/15 bg-[#fff8ef]/80 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#5a3824] theme-dark:border-[#ef4444]/25 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]">
            {performerPlanLabel}
          </span>
        </div>
      </div>
    </section>

    <div className="rounded-[2rem] border border-[#5a3824]/10 bg-[#fff8ef]/55 p-4 shadow-2xl shadow-[#3a2b18]/5 theme-dark:border-white/10 theme-dark:bg-white/[0.025] theme-dark:shadow-black/20 sm:p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.32em] text-[#8a5a35] theme-dark:text-[#ef4444]">
            {t("text053")}
          </p>

          <h1 className="mt-2 text-2xl font-black leading-none tracking-[-0.05em] text-[#241a16] theme-dark:text-white sm:text-3xl">
            {t("text054")}
          </h1>

          <p className="mt-4 text-xl font-black leading-tight text-[#ef4444] sm:text-2xl">
            {adminHeroTitle}
          </p>

          <p className="mt-2 max-w-3xl text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
            {adminHeroIntro}
          </p>
        </div>

       {adminTab === "requests" && (
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center xl:justify-end">
          <span className="inline-flex min-w-[8.5rem] items-center justify-center rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/70 px-4 py-3 text-sm font-black text-[#241a16] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white">
            {t("text055")} {newCount}
          </span>

          <span className="inline-flex min-w-[8.5rem] items-center justify-center rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/70 px-4 py-3 text-sm font-black text-[#241a16] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white">
            {t("text056")} {requests.length}
          </span>
        </div>
        )}
      </div>

        {adminTab === "requests" && (
        <div className="mt-5 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <button
            type="button"
            onClick={() =>
              updateParanoidiSettings({
                isLiveActive: !paranoidiSettings.isLiveActive,
              })
            }
            className={[
              "inline-flex w-full items-center justify-center gap-3 rounded-2xl border px-4 py-3 text-sm font-black uppercase tracking-[0.08em] transition sm:w-auto sm:rounded-full sm:px-5 sm:py-2.5",
              paranoidiSettings.isLiveActive
                ? "border-red-500/50 bg-red-600 text-white shadow-[0_0_28px_rgba(239,68,68,0.32)] hover:bg-red-500"
                : "border-[#5a3824]/20 bg-white/65 text-[#541717] hover:border-[#ef4444]/35 hover:bg-white hover:text-[#b42318] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:border-red-500/30 theme-dark:hover:bg-white/[0.07] theme-dark:hover:text-white",
            ].join(" ")}
          >
            <span className="relative flex h-3 w-3 items-center justify-center">
              {paranoidiSettings.isLiveActive && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              )}

              <span
                className={[
                  "relative inline-flex h-3 w-3 rounded-full",
                  paranoidiSettings.isLiveActive ? "bg-white" : "bg-zinc-500",
                ].join(" ")}
              />
            </span>

            <span>
              {paranoidiSettings.isLiveActive
                ? t("text208")
                : t("text209")}
            </span>
          </button>

          {canUsePublicLanguageFilter && (
            <div className="grid w-full gap-2 rounded-2xl border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-xs font-semibold text-[#241a16] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white sm:w-auto sm:min-w-[19rem] sm:rounded-full sm:py-2.5 sm:text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="whitespace-nowrap">
                  {t("text057")}
                </span>

                <select
                  value={paranoidiSettings.publicSongLanguageMode || "all"}
                  onChange={(event) =>
                    updateParanoidiSettings({
                      publicSongLanguageMode: event.target.value,
                    })
                  }
                  className="max-w-[10rem] rounded-full border border-[#5a3824]/10 bg-[#fff8ef] px-2.5 py-1.5 text-xs font-black text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark] sm:text-sm"
                >
                  <option value="all">{t("text058")}</option>
                  <option value="fi">{t("text059")}</option>
                  <option value="en">{t("text060")}</option>
                  <option value="sv">{t("text061")}</option>
                  <option value="de">{t("text062")}</option>
                  <option value="es">{t("text063")}</option>
                  <option value="fr">{t("text267")}</option>
                  <option value="it">{t("text064")}</option>
                  <option value="et">{t("text065")}</option>
                  <option value="no">{t("text066")}</option>
                  <option value="da">{t("text067")}</option>
                  <option value="other">{t("text068")}</option>
                </select>
              </div>
            </div>
          )}

          {isRequestRaffleEnabled && requests.length > 0 && !raffleWinner && (
            <ParanoidiButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={drawRaffleWinner}
              className="w-full justify-center !rounded-full !border-[#5a3824]/20 !bg-[#fff8ef]/80 !px-4 !py-2.5 !text-sm !text-[#5a3824] !shadow-none hover:!bg-white theme-dark:!border-[#ef4444]/25 theme-dark:!bg-[#ef4444]/10 theme-dark:!text-[#fecaca] theme-dark:hover:!bg-[#ef4444]/15 sm:w-auto"
            >
              {t("text069")}
            </ParanoidiButton>
          )}

          {playedRequests.length > 0 && !raffleWinner && (
            <ParanoidiButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={clearAll}
              className="w-full justify-center !rounded-full !border-red-200 !bg-red-50 !px-4 !py-2.5 !text-sm !text-red-700 !shadow-none hover:!bg-red-100 theme-dark:!border-red-500/25 theme-dark:!bg-red-500/10 theme-dark:!text-red-200 theme-dark:hover:!bg-red-500/15 sm:w-auto"
            >
              {t("text070")}
            </ParanoidiButton>
          )}
        </div>
        )}
      </div>

            {adminTab === "requests" && isRequestRaffleEnabled && raffleWinner && (
              <div className="mt-8 rounded-[1.75rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-6 shadow-2xl shadow-[#3a2b18]/10 theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] theme-dark:shadow-black/30">
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                  {t("text071")}
                </p>

                <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div>
                    <h2 className="text-4xl font-semibold tracking-[-0.05em] text-[#241a16] theme-dark:text-white">
                      {raffleWinner.name}
                    </h2>

                    <p className="mt-3 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                      {t("text072")} {raffleWinner.requestCount} {t("text073")}
                    </p>

                    {raffleWinner.songs.length > 0 && (
                      <p className="mt-2 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("text074")}{" "}
                        <span className="font-semibold text-[#241a16] theme-dark:text-white">
                          {raffleWinner.songs[0].title}
                        </span>
                        {raffleWinner.songs[0].artist
                          ? ` – ${raffleWinner.songs[0].artist}`
                          : ""}
                      </p>
                    )}
                  </div>

                  <ParanoidiButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={clearAll}
                    className="w-full justify-center !border-red-200 !bg-red-50 !text-red-700 hover:!bg-red-100 theme-dark:!border-red-500/25 theme-dark:!bg-red-500/10 theme-dark:!text-red-200 theme-dark:hover:!bg-red-500/15 lg:w-auto"
                  >
                    {t("text070")}
                  </ParanoidiButton>
                </div>
              </div>
            )}
            <div
              className={[
                "mt-8 grid gap-2 rounded-[1.75rem] border border-[#5a3824]/15 bg-[#5a3824]/[0.08] p-2 shadow-inner shadow-[#3a2b18]/10 backdrop-blur-sm theme-dark:border-white/10 theme-dark:bg-black/25 theme-dark:shadow-black/25 sm:rounded-[2rem]",
                adminTabGridClass,
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => setAdminTab("requests")}
                className={getAdminTabButtonClass(adminTab === "requests")}
                aria-current={adminTab === "requests" ? "page" : undefined}
              >
                <span className="flex items-center gap-2 sm:justify-center">
                  <span className={getAdminTabDotClass(adminTab === "requests")} />
                  <span className="text-sm font-black">{t("text075")}</span>
                </span>
              </button>

              {canUseEvents && (
                          <button
              type="button"
              onClick={() => setAdminTab("events")}
              className={getAdminTabButtonClass(adminTab === "events")}
              aria-current={adminTab === "events" ? "page" : undefined}
            >
              <span className="flex items-center gap-2 sm:justify-center">
                <span className={getAdminTabDotClass(adminTab === "events")} />
                <span className="text-sm font-black">{t("text076")}</span>
              </span>
            </button>
              )}
              <button
                type="button"
                onClick={() => setAdminTab("songs")}
                className={getAdminTabButtonClass(adminTab === "songs")}
                aria-current={adminTab === "songs" ? "page" : undefined}
              >
                <span className="flex items-center gap-2 sm:justify-center">
                  <span className={getAdminTabDotClass(adminTab === "songs")} />
                  <span className="text-sm font-black">{t("text052")}</span>
                </span>
              </button>

              {canUseSetlists && (
                <button
                  type="button"
                  onClick={() => setAdminTab("setlists")}
                  className={getAdminTabButtonClass(adminTab === "setlists")}
                  aria-current={adminTab === "setlists" ? "page" : undefined}
                >
                  <span className="flex items-center gap-2 sm:justify-center">
                    <span className={getAdminTabDotClass(adminTab === "setlists")} />
                    <span className="text-sm font-black">{t("text077")}</span>
                  </span>
                </button>
              )}

              {canUseGigNotes && (
                <button
                  type="button"
                  onClick={() => setAdminTab("gigNotes")}
                  className={getAdminTabButtonClass(adminTab === "gigNotes")}
                  aria-current={adminTab === "gigNotes" ? "page" : undefined}
                >
                  <span className="flex items-center gap-2 sm:justify-center">
                    <span className={getAdminTabDotClass(adminTab === "gigNotes")} />
                    <span className="text-sm font-black">{t("text078")}</span>
                  </span>
                </button>
              )}

              {canUseLiveSummary && (
                <button
                  type="button"
                  onClick={() => setAdminTab("summary")}
                  className={getAdminTabButtonClass(adminTab === "summary")}
                  aria-current={adminTab === "summary" ? "page" : undefined}
                >
                  <span className="flex items-center gap-2 sm:justify-center">
                    <span className={getAdminTabDotClass(adminTab === "summary")} />
                    <span className="text-sm font-black">{t("text079")}</span>
                  </span>
                </button>
              )}
            </div>

              {adminTab === "setlistsLocked" && (
                <section className="mt-8 rounded-[2rem] border border-[#ef4444]/20 bg-[#fff8ef]/80 p-6 text-center shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-8">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                    {t("text077")}
                  </p>

                  <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                    {t("text080")}
                  </h2>

                  <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                    {t("text081")}
                  </p>

                  <a
                    href="/admin/subscription"
                    className="mt-6 inline-flex rounded-full border border-[#ef4444]/30 bg-[#ef4444]/10 px-6 py-3 text-sm font-black text-[#fecaca] no-underline transition hover:bg-[#ef4444]/15"
                  >
                    {t("text082")}
                  </a>
                </section>
              )}

              {adminTab === "gigNotesLocked" && (
              <section className="mt-8 rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                  {t("text083")}
                </p>

                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                  {t("text084")}
                </h2>

                <p className="mt-4 max-w-3xl text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                  {t("text085")}
                </p>

                <a
                  href="/admin/subscription"
                  className="mt-5 inline-flex rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-6 py-3 text-sm font-semibold text-white no-underline transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                >
                  {t("text082")}
                </a>
              </section>
            )}

            {canUseGigNotes && adminTab === "gigNotes" && (
             <GigNotesAdmin performerSlug={effectivePerformerSlug} />
            )}


            {canUseLiveSummary && adminTab === "summary" && (
              <div className="relative mx-auto w-full max-w-6xl overflow-x-hidden px-0">
                <section className="mt-8 rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                        {t("text079")}
                      </p>

                      <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                        {t("text086")}
                      </h2>

                      <p className="mt-4 max-w-3xl text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                        {t("text087")}
                      </p>

                      <div className="mt-5 max-w-3xl rounded-2xl border border-[#5a3824]/12 bg-white/55 px-4 py-4 theme-dark:border-white/10 theme-dark:bg-black/20">
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                          {t("text088")}
                        </p>

                        {liveSummary.currentGigStartedAt ? (
                          <p className="mt-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                            {t("text089")}{" "}
                            <span className="font-black">
                              {formatGigDateTime(
                                liveSummary.currentGigStartedAt,
                                adminLocale
                              )}
                            </span>
                          </p>
                        ) : (
                          <p className="mt-2 text-sm font-semibold text-amber-800 theme-dark:text-amber-200">
                            {t("text090")}
                            {" "}
                            <span className="font-black">
                              {t("text008")}
                            </span>
                            .
                          </p>
                        )}

                        <p className="mt-2 text-sm font-semibold text-[#5a3824] theme-dark:text-zinc-300">
                          {t("text091")}{" "}
                          <span className="font-black text-[#241a16] theme-dark:text-white">
                            {liveSummary.setlistSongCount > 0
                              ? liveSummary.activeSetlistTitle ||
                                t("text210")
                              : t("text211")}
                          </span>
                        </p>

                        {liveSummary.activeSetlistOccasion && (
                          <p className="mt-1 text-xs font-semibold text-[#5a3824]/80 theme-dark:text-zinc-500">
                            {t("text092")} {translateAdminLabel(liveSummary.activeSetlistOccasion)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-2 sm:flex sm:flex-wrap sm:justify-end">
                      <button
                        type="button"
                        onClick={saveLiveSummaryReport}
                        disabled={isSavingLiveSummary}
                        className="inline-flex justify-center rounded-full border border-[#ef4444]/35 bg-[#ef4444] px-5 py-3 text-sm font-black text-white transition hover:bg-[#dc2626] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isSavingLiveSummary ? t("text204") : t("text212")}
                      </button>

                      <button
                        type="button"
                        onClick={copyLiveSummary}
                        className="inline-flex justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-5 py-3 text-sm font-black text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:border-[#ef4444]/60 theme-dark:hover:bg-[#ef4444]/15"
                      >
                        {t("text093")}
                      </button>

                      <button
                        type="button"
                        onClick={startNewGig}
                        disabled={isStartingNewGig}
                        className="inline-flex justify-center rounded-full border border-[#5a3824]/20 bg-white/80 px-5 py-3 text-sm font-black text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/[0.07]"
                      >
                        {isStartingNewGig ? t("text203") : t("text008")}
                      </button>
                    </div>
                  </div>

                  {liveSummaryMessage && (
                    <p className="mt-5 rounded-2xl border border-emerald-300/30 bg-emerald-100/70 px-4 py-3 text-sm font-semibold text-emerald-900 theme-dark:border-emerald-400/20 theme-dark:bg-emerald-500/10 theme-dark:text-emerald-200">
                      {translateAdminLabel(liveSummaryMessage)}
                    </p>
                  )}

                  {savedGigReports.length > 0 && (
                    <div className="mt-6 rounded-[1.5rem] border border-[#5a3824]/12 bg-white/55 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                      <p className="text-xs font-black uppercase tracking-[0.22em] text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("text094")}
                      </p>

                      <div className="mt-3 grid gap-2">
                        {savedGigReports.slice(0, 3).map((report) => (
                          <div
                            key={report.id}
                            className="flex flex-col gap-1 rounded-2xl border border-[#5a3824]/10 bg-white/60 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-black/20 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div>
                              <p className="text-sm font-black text-[#241a16] theme-dark:text-white">
                                {report.title || t("text110")}
                              </p>

                              <p className="text-xs font-semibold text-[#5a3824]/80 theme-dark:text-zinc-400">
                                {formatTime(report.createdAt || report.created_at, adminLocale)}
                                {report.occasion ? ` · ${translateAdminLabel(report.occasion)}` : ""}
                              </p>
                            </div>

                            <div className="flex items-center gap-3">
                              <span className="text-xs font-black uppercase tracking-[0.16em] text-[#8a5a35] theme-dark:text-[#fecaca]">
                                {t("text095")}
                              </span>

                              <button
                                type="button"
                                onClick={() => deleteGigReport(report.id)}
                                className="inline-flex rounded-full border border-red-300/30 bg-red-50/80 px-3 py-1.5 text-xs font-black text-red-800 transition hover:bg-red-100 theme-dark:border-red-400/20 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15"
                              >
                                {t("text096")}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-5">
                    {[
                      {
                        label: t("text213"),
                        value: liveSummary.totalRequestCount,
                        help: t("text214"),
                      },
                      {
                        label: t("text215"),
                        value: liveSummary.uniqueSongCount,
                        help: t("text216"),
                      },
                      {
                        label: t("text217"),
                        value: liveSummary.playedRequestCount,
                        help: t("text218"),
                      },
                      {
                        label: t("text219"),
                        value: liveSummary.unhandledRequestCount,
                        help: t("text220"),
                      },
                      {
                        label: t("text221"),
                        value: liveSummary.freeRequestCount,
                        help: t("text222"),
                      },
                      {
                        label: t("text223"),
                        value: liveSummary.boostedRequestCount,
                        help: t("text224"),
                      },
                      {
                        label: t("text225"),
                        value: formatBoostAmount(
                          liveSummary.boostRevenueCents,
                          adminLocale
                        ),
                        help: t("text226"),
                      },
                      ...(liveSummary.setlistSongCount > 0
                      ? [
                          {
                            label: t("text227"),
                            value: liveSummary.setlistSongCount,
                            help: t("text228"),
                          },
                          {
                            label: t("text229"),
                            value: liveSummary.setlistPlayedCount || 0,
                            help: t("text230"),
                          },
                          {
                            label: t("text027"),
                            value: liveSummary.setlistSkippedCount || 0,
                            help: t("text231"),
                          },
                          {
                            label: t("text232"),
                            value: liveSummary.setlistUnmarkedCount || 0,
                            help: t("text233"),
                          },
                          {
                            label: t("text234"),
                            value: liveSummary.setlistNoteCount,
                            help: t("text235"),
                          },
                        ]
                      : []),
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-[1.5rem] border border-[#5a3824]/12 bg-white/65 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                      >
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
                          {item.label}
                        </p>

                        <p className="mt-3 text-4xl font-black tracking-[-0.06em] text-[#241a16] theme-dark:text-white">
                          {item.value}
                        </p>

                        <p className="mt-2 text-xs font-semibold leading-5 text-[#5a3824]/75 theme-dark:text-zinc-500">
                          {item.help}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="mt-8 grid gap-5 lg:grid-cols-2">
                  <section className="rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                      {t("text097")}
                    </p>

                    <h3 className="mt-3 text-2xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                      {t("text098")}
                    </h3>

                    <div className="mt-5 grid gap-3">
                      {liveSummary.topSongs.length > 0 ? (
                        liveSummary.topSongs.map((item, index) => (
                          <div
                            key={item.key}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                          >
                            <div className="min-w-0">
                              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
                                #{index + 1}
                              </p>

                              <p className="mt-1 break-words text-sm font-black text-[#241a16] theme-dark:text-white">
                                {item.label}
                              </p>
                            </div>

                            <span className="shrink-0 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-black text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-[#fecaca]">
                              {item.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
                          {t("text099")}
                        </p>
                      )}
                    </div>
                  </section>

                  <section className="rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                      {t("text100")}
                    </p>

                    <h3 className="mt-3 text-2xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                      {t("text101")}
                    </h3>

                    <div className="mt-5 grid gap-3">
                      {liveSummary.topPlayedSongs.length > 0 ? (
                        liveSummary.topPlayedSongs.map((item) => (
                          <div
                            key={item.key}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-300/25 bg-emerald-50/70 px-4 py-3 theme-dark:border-emerald-400/15 theme-dark:bg-emerald-500/10"
                          >
                            <p className="break-words text-sm font-black text-emerald-950 theme-dark:text-emerald-100">
                              {item.label}
                            </p>

                            <span className="shrink-0 rounded-full border border-emerald-300/35 bg-white/70 px-3 py-1 text-xs font-black text-emerald-900 theme-dark:border-emerald-400/20 theme-dark:bg-white/[0.04] theme-dark:text-emerald-200">
                              {item.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
                          {t("text102")}
                        </p>
                      )}
                    </div>
                  </section>

                  <section className="rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                      {t("text103")}
                    </p>

                    <h3 className="mt-3 text-2xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                      {t("text104")}
                    </h3>

                    <div className="mt-5 grid gap-3">
                      {liveSummary.topFreeRequests.length > 0 ? (
                        liveSummary.topFreeRequests.map((item) => (
                          <div
                            key={item.id}
                            className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <p className="break-words text-sm font-black text-[#241a16] theme-dark:text-white">
                                {item.label}
                              </p>

                              <span className="shrink-0 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-black text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-[#fecaca]">
                                {item.count}
                              </span>
                            </div>

                            {(item.requesterName || item.message) && (
                              <p className="mt-2 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                                {[item.requesterName, item.message]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
                          {t("text105")}
                        </p>
                      )}
                    </div>
                  </section>

                  <section className="rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                    <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                      {t("text106")}
                    </p>

                    <h3 className="mt-3 text-2xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                      {t("text107")}
                    </h3>

                    <div className="mt-5 grid gap-3">
                      {liveSummary.topRequesters.length > 0 ? (
                        liveSummary.topRequesters.map((item, index) => (
                          <div
                            key={item.key}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                          >
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
                                #{index + 1}
                              </p>

                              <p className="mt-1 break-words text-sm font-black text-[#241a16] theme-dark:text-white">
                                {item.label}
                              </p>
                            </div>

                            <span className="shrink-0 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-black text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-[#fecaca]">
                              {item.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
                          {t("text108")}
                        </p>
                      )}
                    </div>
                  </section>
                </div>

                <section className="mt-8 rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                    {t("text109")}
                  </p>

                  <h3 className="mt-3 text-2xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                    {t("text110")}
                  </h3>

                  <pre className="mt-5 max-h-[30rem] overflow-auto whitespace-pre-wrap rounded-[1.5rem] border border-[#5a3824]/12 bg-white/70 p-4 text-sm leading-7 text-[#241a16] theme-dark:border-white/10 theme-dark:bg-black/25 theme-dark:text-zinc-200">
                    {liveSummary.summaryText}
                  </pre>
                </section>
              </div>
            )}

            {adminTab === "songs" && (
              <div className="relative mx-auto w-full max-w-6xl overflow-x-hidden px-0">
                <section className="mx-auto mt-8 max-w-6xl rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                    {t("text052")}
                  </p>

                  <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                    {config?.features?.songImportEnabled === true
                      ? t("text236")
                      : t("text237")}
                  </h2>

                  <p className="mt-4 max-w-3xl text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                    {config?.features?.songImportEnabled === true
                      ? t("text238")
                      : t("text239")}
                  </p>
                </section>
                  <SongLibraryAdmin
                    performerSlug={config?.performer?.slug}
                    features={config?.features}
                  />
              </div>
            )}

            {canUseSetlists && adminTab === "setlists" && (
              <div className="relative w-full max-w-full overflow-x-hidden px-0">
                <section className="mx-auto mt-8 w-full max-w-6xl overflow-x-hidden border-y border-[#5a3824]/15 bg-[#fff8ef]/85 px-3 py-6 shadow-none theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:px-5 lg:rounded-[2rem] lg:border lg:p-7 lg:shadow-[0_18px_50px_rgba(58,43,24,0.10)]">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                    {t("text077")}
                  </p>

                  <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                    {t("text111")}
                  </h2>

                  <div className="mt-4 max-w-3xl text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                    <p>
                    {/* Kokonainen lause säilyttää kielen sanajärjestyksen ja välilyönnit. */}
                      <Trans
                        t={t}
                        i18nKey="setlistGuide"
                        components={{
                          help: <strong className="font-black text-[#241a16] theme-dark:text-white" />,
                        }}
                      />
                    </p>
                  </div>

                  <label className="mt-6 grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {t("text115")}
                    <input
                      type="text"
                      value={setlistTitle}
                      onChange={(event) => setSetlistTitle(event.target.value)}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none placeholder:text-[#8a5a35]/45 [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600 theme-dark:[color-scheme:dark]"
                      placeholder={t("text116")}
                    />
                    <span className="text-xs font-medium leading-5 text-[#6b4229] theme-dark:text-zinc-500">
                      {t("text117")}
                    </span>
                  </label>

                  <div className="mt-6 rounded-[1.5rem] border border-[#5a3824]/12 bg-white/45 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                          {t("text118")}
                        </p>
                        <h3 className="mt-1 text-xl font-black text-[#241a16] theme-dark:text-white">
                          {t("text119")}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                          {t("text120")}
                        </p>
                      </div>

                      {setlistUnknownLanguageCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setAdminTab("songs")}
                          className="inline-flex justify-center rounded-full border border-amber-300/35 bg-amber-300/10 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-amber-900 transition hover:bg-amber-300/20 theme-dark:text-amber-100"
                        >
                          {setlistUnknownLanguageCount} {t("text121")}
                        </button>
                      )}
                    </div>

                    {setlistUnknownLanguageCount > 0 && (
                      <div className="mt-4 rounded-2xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm leading-6 text-amber-950 theme-dark:text-amber-100">
                        {t("text122")} <strong>{t("text058")}</strong> {t("text123")}
                      </div>
                    )}

                    <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="relative grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                        {t("text124")}
                        <select
                          value={setlistOccasion}
                          onChange={(event) => setSetlistOccasion(event.target.value)}
                          className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                        >
                          {SETLIST_OCCASIONS.map((occasion) => (
                            <option key={occasion} value={occasion}>
                              {translateAdminLabel(occasion)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                        {t("text125")}
                        <select
                          value={setlistLanguageFilter}
                          onChange={(event) => setSetlistLanguageFilter(event.target.value)}
                          className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                        >
                          {availableSetlistLanguageOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                        {t("text126")}
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={setlistSetCount}
                          onChange={(event) => setSetlistSetCount(event.target.value)}
                          className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                        />
                      </label>

                      <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                        {t("text127")}
                        <input
                          type="number"
                          min="1"
                          max="25"
                          value={setlistSongsPerSet}
                          onChange={(event) => setSetlistSongsPerSet(event.target.value)}
                          className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                        />
                      </label>
                    </div>

                    <details className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-white/55 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.04]">
                      <summary className="cursor-pointer text-sm font-black uppercase tracking-[0.1em] text-[#5a3824] theme-dark:text-zinc-200">
                        {t("text128")}
                      </summary>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                          <span className="flex items-center gap-2">
                            {t("text129")}

                            <button
                              type="button"
                              onClick={() =>
                                setActiveInfoModal({
                                  title: t("setlistFilterHelp.artistsTitle", {
                                    defaultValue: "Vältä artisteja",
                                  }),
                                  text: t("setlistFilterHelp.artistsText", {
                                    defaultValue:
                                      "Kirjoita tähän artistit, joita et halua mukaan settilistaan. Erottele useampi artisti pilkulla, esimerkiksi: Queen, Eppu Normaali.",
                                  }),
                                })
                              }
                              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-[#5a3824]/15 bg-[#fff8ef]/80 text-[11px] font-black text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.06] theme-dark:text-zinc-200"
                              aria-label={t("text130")}
                            >
                              {t("text131")}
                            </button>
                          </span>

                          <input
                            type="text"
                            value={setlistExcludedArtists}
                            onChange={(event) => setSetlistExcludedArtists(event.target.value)}
                            className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none placeholder:text-[#8a5a35]/45 [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600 theme-dark:[color-scheme:dark]"
                            placeholder={t("text132")}
                          />
                        </label>

                        <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                          <span className="flex items-center gap-2">
                            {t("text133")}

                            <button
                              type="button"
                              onClick={() =>
                               setActiveInfoModal({
                                  title: t("setlistFilterHelp.genresTitle", {
                                    defaultValue: "Vältä genrejä / tyylejä",
                                  }),
                                  text: t("setlistFilterHelp.genresText", {
                                    defaultValue:
                                      "Kirjoita tähän genrejä tai tyylejä, joita et halua mukaan settilistaan. Erottele useampi rajaus pilkulla, esimerkiksi: hevi, rap, iskelmä. Rajaus hakee osumia genrestä, tunnelmasta ja tilaisuustägeistä.",
                                  }),
                                })
                              }
                              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-[#5a3824]/15 bg-[#fff8ef]/80 text-[11px] font-black text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.06] theme-dark:text-zinc-200"
                              aria-label={t("text134")}
                            >
                              {t("text131")}
                            </button>
                          </span>

                          <input
                            type="text"
                            value={setlistExcludedGenres}
                            onChange={(event) => setSetlistExcludedGenres(event.target.value)}
                            className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none placeholder:text-[#8a5a35]/45 [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600 theme-dark:[color-scheme:dark]"
                            placeholder={t("text135")}
                          />
                        </label>
                      </div>
                    </details>
                  </div>
                  <div className="mt-5 grid gap-3">
                    <div className="grid gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap">
                      <button
                        type="button"
                        onClick={generateSetlist}
                        className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] sm:px-3 sm:py-2 sm:text-xs lg:w-auto lg:px-6 lg:py-3 lg:text-sm"
                      >
                        {activeSavedSetlistId
                          ? t("text240")
                          : generatedSetlist.length > 0
                            ? t("text241")
                            : t("text118")}
                      </button>

                      <button
                        type="button"
                        onClick={createEmptySetlist}
                        className="inline-flex w-full justify-center rounded-full border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:bg-[#ef4444]/15 sm:px-3 sm:py-2 sm:text-xs lg:w-auto lg:px-6 lg:py-3 lg:text-sm"
                      >
                        {t("text136")}
                      </button>

                      <button
                        type="button"
                        onClick={saveGeneratedSetlist}
                        disabled={!generatedSetlist.length}
                        className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-sm font-semibold text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:px-3 sm:py-2 sm:text-xs lg:w-auto lg:px-6 lg:py-3 lg:text-sm"
                      >
                        {activeSavedSetlistId ? t("text242") : t("text243")}
                      </button>

                      {generatedSetlist.length > 0 && (
                        <button
                          type="button"
                          onClick={closeCurrentSetlist}
                          className="inline-flex w-full justify-center rounded-full border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15 sm:px-3 sm:py-2 sm:text-xs lg:w-auto lg:px-6 lg:py-3 lg:text-sm"
                        >
                          {t("text137")}
                        </button>
                      )}
                    </div>

                    {isSetlistSaveSuccess && (
                      <p
                        role="status"
                        className="rounded-xl border border-emerald-300/60 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800 theme-dark:border-emerald-400/25 theme-dark:bg-emerald-400/10 theme-dark:text-emerald-200"
                      >
                        {t("text138")}
                      </p>
                    )}

                    {hasUnsavedSetlistChanges && (
                      <p
                        role="status"
                        className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800 theme-dark:border-amber-400/25 theme-dark:bg-amber-400/10 theme-dark:text-amber-200"
                      >
                        {t("text139")}{" "}
                        <strong>
                          {activeSavedSetlistId
                            ? t("text242")
                            : t("text243")}
                        </strong>{" "}
                        {t("text140")}
                      </p>
                    )}

                    {generatedSetlist.length > 0 && (
                      <details className="group rounded-2xl border border-[#5a3824]/12 bg-white/45 p-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                       <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-black uppercase tracking-[0.12em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                        <span>{t("text141")}</span>

                        <span
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/60 text-base leading-none text-[#5a3824] transition group-open:rotate-180 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300"
                          aria-hidden="true"
                        >
                          ↓
                        </span>
                      </summary>

                        <div className="mt-3 grid gap-2 sm:grid-cols-3">
                          <button
                            type="button"
                            onClick={copyGeneratedSetlist}
                            disabled={!generatedSetlist.length}
                            className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-sm font-semibold text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                          >
                            {t("text142")}
                          </button>

                          <button
                            type="button"
                            onClick={printGeneratedSetlist}
                            disabled={!generatedSetlist.length}
                            className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-sm font-semibold text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                          >
                            {t("text143")}
                          </button>

                          <button
                            type="button"
                            onClick={emailGeneratedSetlist}
                            disabled={!generatedSetlist.length}
                            className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-sm font-semibold text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                          >
                            {t("text144")}
                          </button>
                        </div>
                      </details>
                    )}
                  </div>

                {setlistMessage && !isSetlistSaveSuccess && (
                  <p className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-white/55 px-4 py-3 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                    {translateAdminLabel(setlistMessage)}
                  </p>
                )}

                {savedSetlists.length > 0 && (
                  <div className="-mx-3 mt-6 border-y border-[#5a3824]/10 bg-white/30 px-3 py-4 theme-dark:border-white/10 theme-dark:bg-white/[0.025] sm:mx-0 sm:rounded-[1.5rem] sm:border sm:p-4">
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                      {t("text145")}
                    </p>

                    <div className="mt-3 grid gap-2">
                      {savedSetlists.map((savedSetlist) => (
                        <div
                          key={savedSetlist.id}
                          className="flex flex-col gap-3 rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/55 p-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div>
                            <p className="text-sm font-black text-[#241a16] theme-dark:text-white">
                              {getSavedSetlistDisplayTitle(savedSetlist, t)}
                            </p>

                            {getEditableSetlistTitle(savedSetlist.title) && (
                              <p className="mt-1 text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                                {formatSavedSetlistMeta(savedSetlist, t)}
                              </p>
                            )}

                            <p className="mt-1 text-xs text-[#6b4229] theme-dark:text-zinc-400">
                              {new Date(savedSetlist.createdAt).toLocaleString(adminLocale)}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => loadSavedSetlist(savedSetlist)}
                              className="rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300"
                            >
                              {t("text146")}
                            </button>

                            <button
                              type="button"
                              onClick={() => deleteSavedSetlist(savedSetlist.id)}
                              className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-red-700 transition hover:bg-red-100 theme-dark:border-red-400/20 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                            >
                              {t("text096")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {generatedSetlist.length > 0 && (
                  <div className="mt-6 grid w-full min-w-0 max-w-full gap-3 overflow-x-hidden">
                    {generatedSetlist.map((set, setIndex) => (
                      <div
                        key={set.title}
                        className="w-full min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#5a3824]/12 bg-white/65 p-2 theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-4"
                      >
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="min-w-0 text-xl font-semibold tracking-[-0.035em] text-[#241a16] theme-dark:text-white">
                          {getLocalizedSetTitle(set?.title, setIndex)}
                        </h3>

                        <button
                          type="button"
                          onClick={() => removeSetFromSetlist(setIndex)}
                          disabled={generatedSetlist.length <= 1}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-400/30 bg-red-500/10 text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-35"
                          aria-label={t("text147")}
                          title={t("text147")}
                        >
                          <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>

                        <ol className="mt-4 grid gap-2">
                          {set.songs.length === 0 && (
                            <li className="rounded-2xl border border-dashed border-white/10 bg-white/[0.03] px-4 py-4 text-sm font-semibold text-zinc-500">
                              {t("text148")}
                            </li>
                          )}
                            {set.songs.map((song, index) => {
                              // Textarea muokkaa vain tämän biisin omaa muistiinpanoa.
                              // Linkitetyt Keikkamuistit näytetään ja muokataan erikseen niiden ID:llä.
                                const hasExplicitManualNote =
                                  Object.prototype.hasOwnProperty.call(
                                    song || {},
                                    "setlistManualNote"
                                  ) ||
                                  Object.prototype.hasOwnProperty.call(
                                    song || {},
                                    "setlist_manual_note"
                                  );

                                const songNote = hasExplicitManualNote
                                  ? String(
                                      song?.setlistManualNote ??
                                        song?.setlist_manual_note ??
                                        ""
                                    )
                                  : getSetlistSongManualNote(song);
                                const linkedGigNoteIds = getSetlistSongGigNoteIds(song);

                                // Haetaan ID-linkkien perusteella aina Keikkamuistin nykyiset tietueet.
                                // Näitä käytetään seuraavaksi Keikkatyökalun omissa editorikorteissa.
                                const linkedGigNotes = linkedGigNoteIds
                                  .map((gigNoteId) =>
                                    gigNotes.find(
                                      (item) => String(item.id) === String(gigNoteId)
                                    )
                                  )
                                  .filter(Boolean);

                              return (
                              <li
                                key={`${set.title}-${song.id}-${index}`}
                                className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/70 px-2 py-3 text-sm theme-dark:border-white/10 theme-dark:bg-white/[0.03] sm:px-4"
                              >
                                <div className="flex w-full min-w-0 max-w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                  <div className="min-w-0 max-w-full break-words">
                                    <span className="font-black text-[#241a16] theme-dark:text-white">
                                      {index + 1}. {song.title}
                                    </span>

                                    {song.artist && (
                                      <span className="ml-2 text-[#6b4229] theme-dark:text-zinc-400">
                                        – {song.artist}
                                      </span>
                                    )}
                                  </div>

                                  <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:w-auto sm:grid-cols-none sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                                    <button
                                      type="button"
                                      onClick={() => openSetlistSongView(setIndex, index)}
                                      className="col-span-2 inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#ef4444]/35 bg-[#ef4444] px-4 py-2 text-sm font-black text-white shadow-[0_10px_24px_rgba(239,68,68,0.20)] transition hover:bg-[#dc2626] sm:col-span-1 sm:min-h-8 sm:px-3 sm:py-1.5 sm:text-xs"
                                      aria-label={t("text149")}
                                      title={t("text149")}
                                    >
                                      <FiPlay className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{t("text039")}</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => replaceSetlistSong(setIndex, index)}
                                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-2 text-xs font-black text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:min-h-8 sm:px-3 sm:py-1.5"
                                      aria-label={t("text150")}
                                      title={t("text151")}
                                    >
                                      <FiRefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{t("text151")}</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => moveSetlistSong(setIndex, index, -1)}
                                      disabled={index === 0}
                                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-2 text-xs font-black text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-35 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:min-h-8 sm:px-3 sm:py-1.5"
                                      aria-label={t("text152")}
                                      title={t("text153")}
                                    >
                                      <FiArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{t("text153")}</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => moveSetlistSong(setIndex, index, 1)}
                                      disabled={index === set.songs.length - 1}
                                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-2 text-xs font-black text-[#5a3824] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-35 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:min-h-8 sm:px-3 sm:py-1.5"
                                      aria-label={t("text154")}
                                      title={t("text155")}
                                    >
                                      <FiArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{t("text155")}</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => removeSongFromSet(setIndex, index)}
                                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs font-black text-red-700 transition hover:bg-red-500/15 theme-dark:text-red-200 sm:min-h-8 sm:px-3 sm:py-1.5"
                                      aria-label={t("text156")}
                                      title={t("text157")}
                                    >
                                      <FiTrash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{t("text096")}</span>
                                    </button>
                                  </div>
                                </div>

                                <div className="mt-3 w-full min-w-0 max-w-full">
                                  <div className="grid w-full min-w-0 max-w-full gap-3 lg:grid-cols-[1fr_1.15fr]">
                                    <label className="grid gap-2 text-xs font-black uppercase tracking-[0.12em] text-[#8a5a35] theme-dark:text-zinc-400">
                                      {t("text158")}
                                      <select
                                        value=""
                                        onChange={(event) =>
                                          applyGigNoteToSetlistSong(setIndex, index, event.target.value)
                                        }
                                        className="w-full min-w-0 max-w-full rounded-2xl border border-[#5a3824]/15 bg-white/90 px-3 py-2 text-sm font-semibold normal-case tracking-normal text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                                      >
                                        <option value="">
                                          {gigNotes.length
                                            ? t("text244")
                                            : t("text245")}
                                        </option>

                                          {gigNotes.map((item) => {
                                            const isLinked = linkedGigNoteIds.includes(
                                              String(item.id)
                                            );

                                            return (
                                              <option
                                                key={item.id}
                                                value={item.id}
                                                disabled={isLinked}
                                              >
                                                {getLocalizedGigNoteTypeLabel(item.type)}: {item.title}
                                                {isLinked ? t("text246") : ""}
                                              </option>
                                            );
                                          })}
                                      </select>
                                    </label>

                                    <label className="grid gap-2 text-xs font-black uppercase tracking-[0.12em] text-[#8a5a35] theme-dark:text-zinc-400">
                                      {t("text159")}
                                      <textarea
                                        value={songNote}
                                        onChange={(event) =>
                                          updateSetlistSongNote(setIndex, index, event.target.value)
                                        }
                                        rows={3}
                                        className="w-full min-w-0 max-w-full resize-none rounded-2xl border border-[#5a3824]/15 bg-white/90 px-3 py-2 text-sm font-semibold normal-case leading-6 tracking-normal text-[#241a16] outline-none placeholder:text-[#8a5a35]/45 theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600"
                                        placeholder={t("text160")}
                                      />
                                                                        </label>
                                  </div>

                                  {/* Linkitetty Keikkamuisti on aina sama alkuperäinen
                                      tietue riippumatta siitä, moneen biisiin se on liitetty. */}
                                  {linkedGigNotes.length > 0 && (
                                    <div className="mt-3 grid gap-3">
                                      {linkedGigNotes.map((linkedGigNote) => {
                                        const gigNoteId = String(
                                          linkedGigNote.id
                                        );

                                        const saveNoticeKey = `${setIndex}:${index}:${gigNoteId}`;

                                        const draftContent =
                                          Object.prototype.hasOwnProperty.call(
                                            gigNoteDrafts,
                                            gigNoteId
                                          )
                                            ? gigNoteDrafts[gigNoteId]
                                            : linkedGigNote.content || "";

                                        return (
                                          <div
                                            key={gigNoteId}
                                            className="rounded-2xl border border-[#5a3824]/15 bg-white/65 p-3 theme-dark:border-white/10 theme-dark:bg-black/20"
                                          >
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                              <div>
                                                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#8a5a35] theme-dark:text-zinc-500">
                                                  {t("text161")}
                                                </p>

                                                <p className="mt-1 text-sm font-black text-[#241a16] theme-dark:text-white">
                                                  {getLocalizedGigNoteTypeLabel(
                                                    linkedGigNote.type
                                                  )}
                                                  {linkedGigNote.title
                                                    ? `: ${linkedGigNote.title}`
                                                    : ""}
                                                </p>
                                              </div>

                                              <span className="rounded-full border border-[#5a3824]/15 bg-white/75 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-[#6b4229] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-400">
                                                {t("text162")}
                                              </span>
                                            </div>

                                            <textarea
                                              value={draftContent}
                                              onChange={(event) =>
                                                setGigNoteDrafts(
                                                  (currentDrafts) => ({
                                                    ...currentDrafts,
                                                    [gigNoteId]:
                                                      event.target.value,
                                                  })
                                                )
                                              }
                                              rows={3}
                                              className="mt-3 w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/90 px-3 py-2 text-sm font-semibold leading-6 text-[#241a16] outline-none theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white"
                                            />

                                           <div className="mt-3 flex flex-wrap justify-end gap-2">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                unlinkGigNoteFromSetlistSong(
                                                  setIndex,
                                                  index,
                                                  gigNoteId
                                                )
                                              }
                                              className="rounded-full border border-red-300/60 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                                            >
                                              {t("text163")}
                                            </button>

                                            <button
                                              type="button"
                                              onClick={() =>
                                              saveLinkedGigNote(
                                                gigNoteId,
                                                saveNoticeKey
                                              )
                                              }
                                              className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                                            >
                                              {t("text164")}
                                            </button>
                                          </div>
                                          {gigNoteSaveNotice.key === saveNoticeKey &&
                                            gigNoteSaveNotice.text && (
                                              <p
                                                role="status"
                                                className={[
                                                  "mt-2 rounded-xl border px-3 py-2 text-xs font-black",
                                                  gigNoteSaveNotice.type === "error"
                                                    ? "border-red-200 bg-red-50 text-red-700 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                                                    : "border-emerald-200 bg-emerald-50 text-emerald-700 theme-dark:border-emerald-500/25 theme-dark:bg-emerald-500/10 theme-dark:text-emerald-300",
                                                ].join(" ")}
                                              >
                                                {gigNoteSaveNotice.text}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {songNote && (
                                    <div className="mt-3 flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => clearSetlistSongNote(setIndex, index)}
                                        className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15"
                                      >
                                        {t("text165")}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                        <div className="mt-4 w-full min-w-0 max-w-full rounded-2xl border border-[#5a3824]/15 bg-white/55 p-3 theme-dark:border-white/10 theme-dark:bg-black/20">
                          <label className="grid gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#8a5a35] theme-dark:text-zinc-400">
                            {t("text166")}

                            <input
                              type="search"
                              value={getSetlistAddSongSearch(setIndex)}
                              onChange={(event) =>
                                updateSetlistAddSongSearch(setIndex, event.target.value)
                              }
                              className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/90 px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-[#241a16] outline-none placeholder:text-[#8a5a35]/45 focus:border-[#ef4444]/45 theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600"
                              placeholder={t("text167")}
                            />
                          </label>

                          {getSetlistAddSongSearch(setIndex).trim() && (
                            <div className="mt-3 grid gap-2">
                              {getSetlistAddSongResults(setIndex).length > 0 ? (
                                getSetlistAddSongResults(setIndex).map((song) => (
                                  <div
                                    key={song.id}
                                    className="flex min-w-0 items-center justify-between gap-2 rounded-2xl border border-[#5a3824]/15 bg-white/75 px-3 py-2 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                                  >
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-black text-[#241a16] theme-dark:text-white">
                                        {song.title}
                                      </p>

                                      {song.artist && (
                                        <p className="truncate text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                                          {song.artist}
                                        </p>
                                      )}
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        addLibrarySongToSet(setIndex, song.id)
                                      }
                                      className="shrink-0 rounded-full border border-[#ef4444]/30 bg-[#ef4444]/10 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-[#b42318] transition hover:bg-[#ef4444]/15 theme-dark:text-[#fecaca]"
                                    >
                                      {t("text168")}
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <p className="rounded-2xl border border-[#5a3824]/15 bg-white/75 px-3 py-3 text-sm font-semibold text-[#8a5a35] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-500">
                                  {t("text169")}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
              </div>
            )}

            {canUseEvents && adminTab === "events" && (
            <PerformerEventsAdmin performerSlug={effectivePerformerSlug} />
          )}
          <div className={adminTab === "requests" ? "block" : "hidden"}>

              {activeBoostedRequestGroups.length > 0 && (
                <section className="mt-10 rounded-[2rem] border border-amber-400/30 bg-amber-50/90 p-5 shadow-[0_18px_50px_rgba(120,80,20,0.12)] theme-dark:border-amber-300/20 theme-dark:bg-amber-400/[0.06] sm:p-7">
                  <div className="flex flex-col gap-3 border-b border-amber-500/20 pb-5 theme-dark:border-amber-300/15 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.28em] text-amber-800 theme-dark:text-amber-300">
                        {t("text170")}
                      </p>

                      <h2 className="mt-2 text-3xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                        {t("text171")}
                      </h2>

                      <p className="mt-2 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("text172")}
                      </p>
                    </div>

                    <span className="inline-flex w-fit rounded-full border border-amber-400/30 bg-white/70 px-4 py-2 text-sm font-black text-amber-900 theme-dark:border-amber-300/20 theme-dark:bg-amber-400/10 theme-dark:text-amber-200">
                      {activeBoostedRequestGroups.length}{" "}
                      {activeBoostedRequestGroups.length === 1
                        ? t("text247")
                        : t("text248")}
                    </span>
                  </div>

                  <div className="mt-5 grid gap-4">
                    {activeBoostedRequestGroups.map((group) => {
                      const request = group.request;
                      const groupRequests = group.requests;

                      const totalRequestCount = groupRequests.reduce(
                        (total, item) =>
                          total + getRequestCount(item),
                        0
                      );

                      return (
                        <article
                          key={`boosted-${group.key}`}
                          className="rounded-[1.75rem] border border-amber-400/25 bg-white/80 p-5 shadow-[0_14px_38px_rgba(120,80,20,0.10)] theme-dark:border-amber-300/15 theme-dark:bg-black/25"
                        >
                          <div className="grid gap-5 lg:grid-cols-[1fr_220px] lg:items-start">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-amber-400/40 bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-amber-900 theme-dark:border-amber-300/30 theme-dark:bg-amber-400/15 theme-dark:text-amber-200">
                                  {t("text173")}
                                </span>

                                <span
                                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${
                                    request.status === "seen"
                                      ? "bg-amber-100 text-amber-800 theme-dark:bg-amber-500/15 theme-dark:text-amber-200"
                                      : "bg-[#f7eadc] text-[#5a3824] theme-dark:bg-white/10 theme-dark:text-white"
                                  }`}
                                >
                                  {getLocalizedRequestStatusLabel(request.status)}
                                </span>

                                {isFreeRequest(request) && (
                                  <span className="rounded-full bg-[#5a3824] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-white theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]">
                                    {t("text174")}
                                  </span>
                                )}

                                {request.eventName && (
                                  <span className="rounded-full border border-sky-400/25 bg-sky-500/10 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-sky-700 theme-dark:text-sky-200">
                                    {t("text175")} {request.eventName}
                                  </span>
                                )}

                                <span className="text-xs font-semibold text-[#8a5a35]/70 theme-dark:text-zinc-400">
                                  {formatTime(request.createdAt, adminLocale)}
                                </span>
                              </div>

                              <h3 className="mt-4 text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white sm:text-3xl">
                                {request.songTitle}
                              </h3>

                              {request.artist && (
                                <p className="mt-1 text-sm font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                                  {request.artist}
                                </p>
                              )}

                              <div className="mt-5 border-l-2 border-amber-400/30 pl-4 theme-dark:border-amber-300/20">
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-amber-800 theme-dark:text-amber-300">
                                  {totalRequestCount}{" "}
                                  {totalRequestCount === 1
                                    ? t("text249")
                                    : t("text250")}
                                </p>

                                <div className="mt-3 grid gap-2">
                                  {groupRequests.map((groupRequest) => (
                                    <div
                                      key={groupRequest.id}
                                      className="rounded-2xl border border-[#5a3824]/10 bg-white/55 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                                    >
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          {groupRequest.requesterName && (
                                            <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                                              {groupRequest.requesterName}
                                            </p>
                                          )}

                                          {Boolean(
                                            groupRequest.isBoosted ??
                                              groupRequest.is_boosted
                                          ) && (
                                            <span className="rounded-full border border-amber-400/30 bg-amber-100 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-amber-900 theme-dark:border-amber-300/25 theme-dark:bg-amber-400/10 theme-dark:text-amber-200">
                                              {t("text176")}
                                            </span>
                                          )}
                                        </div>

                                        <span className="text-xs font-semibold text-[#8a5a35]/70 theme-dark:text-zinc-500">
                                          {formatTime(groupRequest.createdAt, adminLocale)}
                                        </span>
                                      </div>

                                      {groupRequest.message && (
                                        <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                                          {groupRequest.message}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                              <button
                                type="button"
                                onClick={() =>
                                  markGroupPlayed(groupRequests)
                                }
                                className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:border-[#ef4444]/60 theme-dark:hover:bg-[#ef4444]/15 sm:w-auto"
                              >
                                {t("text177")}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  removeGroupRequests(groupRequests)
                                }
                                className="rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 theme-dark:border-red-500/25 theme-dark:bg-red-500/5 theme-dark:text-red-200 theme-dark:hover:border-red-400/45 theme-dark:hover:bg-red-500/10"
                              >
                                {t("text096")}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}

              {regularFreeRequests.length > 0 && (
                <section className="mt-10 rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef]/85 p-5 shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:p-7">
                  <div className="flex flex-col gap-3 border-b border-[#5a3824]/12 pb-5 theme-dark:border-white/10 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                        {t("text178")}
                      </p>

                      <h2 className="mt-2 text-3xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                        {t("text104")}
                      </h2>

                      <p className="mt-2 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("text179")}
                      </p>
                    </div>

                    <span className="inline-flex w-fit rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-sm font-black text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-[#fecaca]">
                      {freeRequestTotalCount} {freeRequestTotalCount === 1 ? t("text249") : t("text250")}
                    </span>
                  </div>

                  <div className="mt-5 grid gap-3">
                    {regularFreeRequests.map((request) => (
                      <article
                        key={request.id}
                        className="rounded-[1.5rem] border border-[#5a3824]/12 bg-white/75 p-4 shadow-sm theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-5"
                      >
                        <div className="grid gap-4 lg:grid-cols-[1fr_220px] lg:items-start">
                          <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#5a3824] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-white theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]">
                            {t("text174")}
                          </span>

                          {request.isBoosted && (
                            <span className="rounded-full border border-amber-400/40 bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-amber-900 theme-dark:border-amber-300/30 theme-dark:bg-amber-400/15 theme-dark:text-amber-200">
                              {t("text173")}
                            </span>
                          )}

                          <span className="rounded-full border border-[#5a3824]/15 bg-[#fff8ef] px-3 py-1 text-xs font-black text-[#5a3824] theme-dark:border-[#ef4444]/20 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]">
                            {t("text180")} {getRequestCount(request)} {t("text181")}
                          </span>

                          {isRequestRaffleEnabled && isRaffleOptedIn(request) && (
                            <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-emerald-700 theme-dark:text-emerald-200">
                              {t("text182")}
                            </span>
                          )}

                              {request.eventName && (
                                <span className="rounded-full border border-sky-400/25 bg-sky-500/10 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-sky-700 theme-dark:text-sky-200">
                                  {t("text175")} {request.eventName}
                                </span>
                              )}

                          <span className="text-xs font-semibold text-[#8a5a35]/70 theme-dark:text-zinc-400">
                            {formatTime(request.createdAt, adminLocale)}
                          </span>
                        </div>

                            <h3 className="mt-4 text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white sm:text-3xl">
                              {request.songTitle}
                            </h3>

                            {request.artist && (
                              <p className="mt-1 text-sm font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                                {request.artist}
                              </p>
                            )}

                            {(request.requesterName || request.message) && (
                              <div className="mt-5 border-l-2 border-[#5a3824]/18 pl-4 theme-dark:border-white/10">
                                {request.requesterName && (
                                  <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                                    {request.requesterName}
                                  </p>
                                )}

                                {request.message && (
                                  <p className="mt-2 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                                    {request.message}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                            <button
                              type="button"
                              onClick={() =>
                                request.status === "played"
                                  ? markSeen(request.id)
                                  : markPlayed(request.id)
                              }
                              className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:border-[#ef4444]/60 theme-dark:hover:bg-[#ef4444]/15 sm:w-auto"
                            >
                              {request.status === "played"
                                ? t("text251")
                                : t("text177")}
                            </button>

                            <button
                              type="button"
                              onClick={() => removeRequest(request.id)}
                              className="rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 theme-dark:border-red-500/25 theme-dark:bg-red-500/5 theme-dark:text-red-200 theme-dark:hover:border-red-400/45 theme-dark:hover:bg-red-500/10"
                            >
                              {t("text096")}
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}

            {activeBoostedRequestGroups.length === 0 && regularLibraryRequests.length === 0 && regularFreeRequests.length === 0 ? (
              <div className="mt-10 overflow-hidden rounded-[2rem] border border-[#5a3824]/12 bg-[#fff8ef]/80 text-center shadow-[0_18px_50px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04]">
                <div className="mx-auto max-w-2xl px-6 py-10">

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-[#241a16] theme-dark:text-white sm:text-4xl">
                    {t("text183")}
                </h2>

                  <p className="mt-3 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                    {t("text184")}
                  </p>

                  <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
                    <a
                      href={publicPerformerPath}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-6 py-3 text-sm font-black text-white no-underline transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:border-[#ef4444]/60 theme-dark:hover:bg-[#ef4444]/15"
                    >
                      {t("text185")}
                    </a>

                    <a
                      href="/admin/settings"
                      className="inline-flex justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-6 py-3 text-sm font-black text-[#5a3824] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10"
                    >
                      {t("text186")}
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-10 grid gap-4">
                {groupedLibraryRequests.map((group, index) => {
                  const request = group.request;
                  const groupRequests = group.requests;
                  const previousGroup = groupedLibraryRequests[index - 1];
                  const previousRequest = previousGroup?.request;
                  const isFirstInGroup =
                    !previousRequest || previousRequest.status !== request.status;

                  const isPlayedRequest = request.status === "played";

                  if (isPlayedRequest && !isPlayedRequestsOpen && !isFirstInGroup) {
                    return null;
                  }

                  if (isPlayedRequest && !isPlayedRequestsOpen && isFirstInGroup) {
                    return (
                      <div key="played-requests-collapsed">
                        <button
                          type="button"
                          onClick={() => setIsPlayedRequestsOpen(true)}
                          className="mt-8 flex w-full items-center gap-4 rounded-full border border-[#5a3824]/15 bg-white/55 px-4 py-3 text-left transition hover:bg-white/80 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:hover:bg-white/10"
                        >
                          <div className="h-px flex-1 bg-[#5a3824]/15 theme-dark:bg-white/10" />

                          <div className="flex shrink-0 items-center gap-3">
                            <span className={`text-xs font-black uppercase tracking-[0.28em] ${getStatusGroupClass(request.status)}`}>
                              {t("text187")}
                            </span>

                            <span className="rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                              {requestGroupCounts.played || 0}
                            </span>

                            <span className="text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                              {t("text033")}
                            </span>
                          </div>

                          <div className="h-px flex-1 bg-[#5a3824]/15 theme-dark:bg-white/10" />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div key={request.id || `${request.songTitle}-${index}`}>
                      {isFirstInGroup && (
                        <div className="mt-8 flex items-center gap-4 first:mt-0">
                          <div className="h-px flex-1 bg-[#5a3824]/15 theme-dark:bg-white/10" />

                          <div className="flex shrink-0 items-center gap-3">
                            <span
                              className={`text-xs font-black uppercase tracking-[0.28em] ${getStatusGroupClass(request.status)}`}
                            >
                              {getLocalizedRequestStatusGroupLabel(request.status)}
                            </span>

                            <span className="rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                              {requestGroupCounts[request.status || "new"] || 0}
                            </span>

                            {isPlayedRequest && (
                              <button
                                type="button"
                                onClick={() => setIsPlayedRequestsOpen(false)}
                                className="rounded-full border border-[#5a3824]/15 bg-white/70 px-3 py-1 text-xs font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                              >
                                {t("text188")}
                              </button>
                            )}
                          </div>

                          <div className="h-px flex-1 bg-[#5a3824]/15 theme-dark:bg-white/10" />
                        </div>
                      )}

                      <article className="rounded-[1.75rem] border border-[#5a3824]/12 bg-white/75 p-5 shadow-[0_14px_38px_rgba(58,43,24,0.08)] theme-dark:border-white/10 theme-dark:bg-white/[0.04]">
                        <div className="grid gap-5 lg:grid-cols-[1fr_220px] lg:items-start">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${
                                  request.status === "played"
                                    ? "bg-red-100 text-red-800 theme-dark:bg-red-500/15 theme-dark:text-red-200"
                                    : request.status === "seen"
                                      ? "bg-amber-100 text-amber-800 theme-dark:bg-amber-500/15 theme-dark:text-amber-200"
                                      : "bg-[#f7eadc] text-[#5a3824] theme-dark:bg-white/10 theme-dark:text-white"
                                }`}
                              >
                                {getLocalizedRequestStatusLabel(request.status)}
                              </span>

                              {request.isBoosted && (
                                <span className="rounded-full border border-amber-400/40 bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-amber-900 theme-dark:border-amber-300/30 theme-dark:bg-amber-400/15 theme-dark:text-amber-200">
                                  {t("text173")}
                                </span>
                              )}

                              {request.eventName && (
                                <span className="rounded-full border border-sky-400/25 bg-sky-500/10 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-sky-700 theme-dark:text-sky-200">
                                  {t("text175")} {request.eventName}
                                </span>
                              )}

                              <span className="text-xs font-semibold text-[#8a5a35]/70 theme-dark:text-zinc-400">
                                {formatTime(request.createdAt, adminLocale)}
                              </span>
                            </div>

                            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                              {request.songTitle}
                            </h2>

                            <p className="mt-1 text-sm font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                              {request.artist}
                            </p>

                            <div className="mt-5 border-l-2 border-[#5a3824]/18 pl-4 theme-dark:border-white/10">
                              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                                {groupRequests.length} {groupRequests.length === 1 ? t("text249") : t("text250")}
                              </p>

                              <div className="mt-3 grid gap-2">
                                {groupRequests.map((groupRequest) => (
                                  <div
                                    key={groupRequest.id}
                                    className="rounded-2xl border border-[#5a3824]/10 bg-white/55 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.03]"
                                  >
                                                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                      {groupRequest.requesterName && (
                                        <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                                          {groupRequest.requesterName}
                                        </p>
                                      )}

                                                                                                            {isRequestRaffleEnabled &&        isRaffleOptedIn(groupRequest) && (
                                        <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.12em] text-emerald-700 theme-dark:text-emerald-200">
                                          {t("text182")}
                                        </span>
                                      )}
                                    </div>

                                    <span className="text-xs font-semibold text-[#8a5a35]/70 theme-dark:text-zinc-500">
                                      {formatTime(groupRequest.createdAt, adminLocale)}
                                    </span>
                                  </div>

                                    {groupRequest.message && (
                                      <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                                        {groupRequest.message}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                            <button
                              type="button"
                                onClick={() =>
                                  request.status === "played"
                                    ? markGroupSeen(groupRequests)
                                    : markGroupPlayed(groupRequests)
                                }
                              className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:border-[#ef4444]/60 theme-dark:hover:bg-[#ef4444]/15 sm:w-auto"
                            >
                              {request.status === "played"
                                ? t("text252")
                                : t("text177")}
                            </button>

                            <button
                              type="button"
                              onClick={() => removeGroupRequests(groupRequests)}
                              className="rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 theme-dark:border-red-500/25 theme-dark:bg-red-500/5 theme-dark:text-red-200 theme-dark:hover:border-red-400/45 theme-dark:hover:bg-red-500/10"
                            >
                              {t("text096")}
                            </button>
                          </div>
                        </div>
                      </article>
                    </div>
                  );
                })}
              </div>
            )}

            </div>


          </div>
        </section>
      </main>

      <ParanoidiAdminFooter />

      {raffleError && (
        <div
          className="fixed inset-0 z-[90] overflow-y-auto bg-black/80 px-4 py-4 backdrop-blur-sm sm:py-8"
          role="dialog"
          aria-modal="true"
          aria-label={t("text189")}
        >
          <div className="w-full max-w-xl overflow-hidden rounded-[2rem] border border-[#5a3824]/15 bg-[#f7f0e8] text-[#241a16] shadow-2xl shadow-black/60 theme-dark:border-[#ef4444]/20 theme-dark:bg-zinc-950 theme-dark:text-white">
            <div className="flex items-start justify-between gap-4 border-b border-[#5a3824]/15 px-6 py-5 theme-dark:border-white/10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                  {t("text190")}
                </p>

                <h3 className="mt-2 text-3xl font-semibold tracking-[-0.04em]">
                  {t("text191")}
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setRaffleError("")}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#5a3824]/15 text-xl transition hover:bg-white/70 theme-dark:border-[#ef4444]/20 theme-dark:hover:bg-[#ef4444]/10"
                aria-label={t("text192")}
              >
                <FiX className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6">
              <div className="rounded-3xl border border-[#5a3824]/15 bg-[#fff8ef]/80 p-5 theme-dark:border-[#ef4444]/15 theme-dark:bg-white/[0.04]">
                <p className="text-base leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                  {translateAdminLabel(raffleError)}
                </p>

                <p className="mt-3 text-sm leading-7 text-[#6b4229] theme-dark:text-zinc-400">
                  {t("text193")}
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <ParanoidiButton
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setRaffleError("")}
                >
                  {t("text194")}
                </ParanoidiButton>
              </div>
            </div>
          </div>
        </div>
      )}
      {isClearModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 px-4 py-8 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t("text195")}
        >
          <div className="w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/10 bg-[#f7f5f1] text-zinc-950 shadow-2xl shadow-black/60 theme-dark:border-[#ef4444]/20 theme-dark:bg-zinc-950 theme-dark:text-white">
            <div className="flex items-start justify-between gap-4 border-b border-zinc-200 px-6 py-5 theme-dark:border-white/10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-500 theme-dark:text-red-300">
                  {t("text196")}
                </p>

                <h3 className="mt-2 text-3xl font-semibold tracking-[-0.04em]">
                  {t("text197")}
                </h3>
              </div>

              <button
                type="button"
                onClick={closeClearModal}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-200 text-xl transition hover:bg-zinc-100 theme-dark:border-[#ef4444]/20 theme-dark:hover:bg-[#ef4444]/10"
                aria-label={t("text198")}
              >
                <FiX className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6">
              <div className="rounded-3xl border border-red-200 bg-red-50 p-5 text-red-950 theme-dark:border-red-500/20 theme-dark:bg-red-500/10 theme-dark:text-red-100">
                <p className="text-base leading-7">
                  {t("text199")}
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeClearModal}
                  className="inline-flex items-center justify-center rounded-full border border-zinc-300 px-6 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-white theme-dark:border-white/10 theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                >
                  {t("text007")}
                </button>

                <button
                  type="button"
                  onClick={confirmClearAll}
                  className="inline-flex items-center justify-center rounded-full border border-red-400/40 bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-red-950/20 transition hover:-translate-y-0.5 hover:bg-red-500 theme-dark:border-red-400/30 theme-dark:bg-red-500/20 theme-dark:text-red-100 theme-dark:hover:bg-red-500/30"
                >
                  {t("text200")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
/*
 * Admin-pyyntöjen autentikointi perustuu palvelimen HttpOnly-session-cookieen.
 * Lähetetään cookie mukana myös localhostin frontend -> backend -pyynnöissä.
 */
function fetchWithCredentials(input, init = {}) {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}
